import { useProjectStudyDialogStore } from "../projectStudyDialogStore";
import { subscribeSnapShotComposerFocus } from "../lib/desktopSnapShot";
import { useProviderGoal } from "../hooks/useProviderGoal";
import { useManualCompaction } from "../hooks/useManualCompaction";
import { parseConversationCommand } from "./chat/conversationCommands.logic";
import { buildTemporaryWorktreeBranchName } from "@afar-research/shared/git";
import type { DesignAgentOperationSubmission } from "@afar-research/afar";
import { useWorkspacePanelLayout } from "../hooks/useWorkspacePanelLayout";
import {
  type ApprovalRequestId,
  type DesignOperationSubmission,
  isDesignOperationSubmission,
  DEFAULT_MODEL,
  defaultInstanceIdForDriver,
  type EnvironmentId,
  type MessageId,
  type ModelSelection,
  type ProjectScript,
  type ProjectId,
  type ProviderApprovalDecision,
  ProviderInstanceId,
  type ServerProvider,
  type ScopedThreadRef,
  type SourceProposedPlanReference,
  ThreadId,
  type ThreadTurnStartBootstrap,
  type TurnId,
  type KeybindingCommand,
  OrchestrationThreadActivity,
  ProviderInteractionMode,
  ProviderDriverKind,
  RuntimeMode,
  TerminalOpenInput,
  DesignDeleteTarget,
} from "@afar-research/contracts";
import { presentEnvironmentRecovery } from "@afar-research/client-runtime/state/presentation";
import { deriveCodexSubagentProjection } from "@afar-research/client-runtime/state/subagent-runtime";
import {
  parseScopedThreadKey,
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@afar-research/client-runtime/environment";
import { createModelSelection } from "@afar-research/shared/model";
import { getProjectEntriesQueryAtom } from "./files/projectFilesQueryState";
import { appAtomRegistry } from "~/rpc/atomRegistry";
import {
  orderTimelineMessages,
  brainActionOf,
  brainAwareComposerCanSend,
  EMPTY_DESIGN_COMPOSER_COLLECTION,
  buildDesignComposerSubmission,
  composerAttachmentLimit,
  designOperationOf,
  offersProjectStudy,
  PROJECT_REFERENCES_INGEST_THREAD_TITLE,
  projectAwaitsStudy,
  formatOutgoingPrompt,
  latestSettledDesignOperationId,
  planProviderSkillDispatch,
  resolveThreadCurrentPullRequestLink,
  visibleThreadPullRequests,
} from "@afar-research/afar";
import { projectScriptCwd, projectScriptRuntimeEnv } from "@afar-research/shared/projectScripts";
import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
} from "@afar-research/contracts";
import { truncate } from "@afar-research/shared/String";
import { nextTerminalId, resolveTerminalSessionLabel } from "@afar-research/shared/terminalLabels";
import { useAtomValue } from "@effect/atom-react";
import { useStableCallback } from "../hooks/useStableCallback";
import { useStableValue } from "../hooks/useStableValue";
import {
  deriveContextWindowSnapshotFromActivity,
  findLatestContextWindowActivity,
} from "../lib/contextWindow";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";
import {
  isAtomCommandInterrupted,
  mapAtomCommandResult,
  settlePromise,
  squashAtomCommandFailure,
  type AtomCommandResult,
} from "@afar-research/client-runtime/state/runtime";
import * as Cause from "effect/Cause";
import * as Option from "effect/Option";
import { AsyncResult } from "effect/unstable/reactivity";
import { isElectron } from "../env";
import { useDiffPanelStore } from "../diffPanelStore";
import {
  derivePendingApprovals,
  derivePendingUserInputs,
  derivePhase,
  deriveDesignDeleteReportEntries,
  deriveTimelineEntries,
  deriveActiveWorkStartedAt,
  deriveAgentTaskListStates,
  findLatestProposedPlan,
  deriveWorkLogEntries,
  hasActionableProposedPlan,
  isLatestTurnSettled,
} from "../session-logic";
import { type LegendListRef } from "@legendapp/list/react";
import {
  buildPendingUserInputAnswers,
  derivePendingUserInputProgress,
  setPendingUserInputCustomAnswer,
  togglePendingUserInputOptionSelection,
  type PendingUserInputDraftAnswer,
} from "../pendingUserInput";
import { useUiStateStore } from "../uiStateStore";
import { derivePromptProviderReadiness } from "../providerReadinessGate";
import {
  isProposedPlanHandoffPending,
  useProposedPlanHandoffStore,
} from "../proposedPlanHandoffStore";
import {
  buildPlanImplementationThreadTitle,
  buildPlanFollowUpTurnStart,
  buildNewThreadPlanImplementationHandoff,
  resolvePlanFollowUpFailureRecovery,
  resolvePlanJourneyPresentation,
  shouldDeleteFailedImplementationThread,
} from "../proposedPlan";
import {
  DEFAULT_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  DEFAULT_THREAD_TERMINAL_ID,
  MAX_TERMINALS_PER_GROUP,
  type ChatMessage,
  type SessionPhase,
  type Thread,
  type TurnDiffSummary,
} from "../types";
import { useTheme } from "../hooks/useTheme";
import { resolveFollowUpAction } from "../followUp.logic";
import {
  isQueuedMessageDue,
  planQueuedAttachmentRestore,
  queuedMessageSendContext,
  useQueuedMessages,
  useQueuedMessageStore,
  type QueuedComposerMessage,
} from "../queuedMessageStore";
import { collapseExpandedComposerCursor, type ComposerSubmissionIntent } from "../composer-logic";
import { useProactivePanel } from "../hooks/useProactivePanel";
import { useTurnDiffSummaries } from "../hooks/useTurnDiffSummaries";
import { isCommandPaletteOpen } from "../commandPaletteContext";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY } from "../rightPanelLayout";
import {
  selectActiveRightPanel,
  selectActiveRightPanelSurface,
  selectThreadRightPanelState,
  type RightPanelSurface,
  useRightPanelStore,
} from "../rightPanelStore";
import {
  reportWorkspaceSurfaceFailure,
  renderWorkspaceSurfaceFailure,
  transitionWorkspaceSurfaceState,
  type WorkspaceSurfaceCommand,
  type WorkspaceSurfacePresentation,
} from "../workspaceSurfaceHost";
import {
  isPreviewSupportedInRuntime,
  setActivePreviewTab,
  useThreadPreviewSurfaceState,
} from "../previewStateStore";
import { addBrowserSurface } from "./preview/addBrowserSurface";
import { ThreadPreviewMiniPlayer } from "./preview/ThreadPreviewMiniPlayer";
import { PreviewSessionSync } from "./preview/usePreviewSession";
import {
  selectThreadPreviewMiniPlayer,
  usePreviewMiniPlayerStore,
} from "../previewMiniPlayerStore";
import { closePreviewSession } from "./preview/closePreviewSession";
import { subscribePreviewAction } from "./preview/previewActionBus";
import { getConfiguredPreviewUrls } from "./preview/previewEmptyStateLogic";
import { RightPanelTabs } from "./RightPanelTabs";
import type { RightPanelSurfaceContentMap } from "./RightPanelSurfaceContent";
import { RightPanelWorkspaceSurfaceHost } from "./RightPanelWorkspaceSurfaceHost";
import { AgentTaskListPanel } from "./AgentTaskListPanel";
import { type DiffWorkspaceSurfaceContextValue } from "./diffs/DiffWorkspaceSurfaceContext";
import { type FilesWorkspaceSurfaceContextValue } from "./files/FilesWorkspaceSurface";
import { type BrowserWorkspaceSurfaceContextValue } from "./browser/BrowserWorkspaceSurface";
import { type TerminalWorkspaceSurfaceContextValue } from "./terminals/TerminalWorkspaceSurface";
import { requestScopedTerminalProcessClose } from "./terminals/terminalProcessClose";
import { BranchToolbar } from "./BranchToolbar";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { ChevronDownIcon, ListChecksIcon, WifiOffIcon } from "lucide-react";
import { cn, newCommandId, randomHex } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { decodeProjectScriptKeybindingRule } from "~/lib/projectScriptKeybindings";
import { type NewProjectScriptInput } from "./ProjectScriptsControl";
import {
  commandForProjectScript,
  nextProjectScriptId,
  projectScriptIdFromCommand,
} from "~/projectScripts";
import { newDraftId, newMessageId, newThreadId } from "~/lib/utils";
import { resolveSelectableProvider } from "../providerModels";
import { providerConversationOperationSupported } from "../providerInstances";
import { useClientSettings, useEnvironmentSettings } from "../hooks/useSettings";
import { resolveAppModelSelectionForInstance } from "../modelSelection";
import { getTerminalFocusOwner } from "../lib/terminalFocus";
import {
  deriveLogicalProjectKeyFromSettings,
  selectProjectGroupingSettings,
} from "../logicalProject";
import { buildDraftThreadRouteParams, buildThreadRouteParams } from "../threadRoutes";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type DraftThreadEnvMode,
  useComposerDraftStore,
  type DraftId,
} from "../composerDraftStore";
import {
  appendTerminalContextsToPrompt,
  formatTerminalContextLabel,
  type TerminalContextDraft,
  type TerminalContextSelection,
} from "../lib/terminalContext";
import { appendPreviewAnnotationPrompt } from "../lib/previewAnnotation";
import { appendReviewCommentsToPrompt, type ReviewCommentContext } from "../reviewCommentContext";
import {
  selectOpenTerminalThreadKeys,
  selectThreadTerminalUiState,
  useTerminalUiStateStore,
} from "../terminalUiStateStore";
import { useKnownTerminalSessions, useThreadRunningTerminalIds } from "../state/terminalSessions";
import { projectEnvironment } from "../state/projects";
import { uploadComposerAttachmentFile, uploadProjectReferenceFile } from "../state/attachments";
import { useEnvironmentQuery } from "../state/query";
import { usePaginatedThreadHistory, useProviderSkillDiscovery } from "../state/queries";
import {
  primaryServerAvailableEditorsAtom,
  primaryServerKeybindingsAtom,
  serverEnvironment,
} from "../state/server";
import { terminalEnvironment } from "../state/terminal";
import {
  refreshEnvironmentThread,
  threadEnvironment,
  useEnvironmentThread,
} from "../state/threads";
import { vcsEnvironment } from "../state/vcs";
import {
  useEnvironmentHasCloudDisplay,
  useEnvironments,
  usePrimaryEnvironment,
} from "../state/environments";
import {
  readThreadDetail,
  useProject,
  useProjects,
  useThreadRefs,
  useThreadShell,
  useThreadShellsForProjectRefs,
} from "../state/entities";
import { environmentShell } from "../state/shell";
import {
  PersistentThreadTerminalDrawer,
  PersistentThreadTerminalPanel,
} from "./chat/PersistentThreadTerminals";
import { compensateFailedSend } from "./chat/compensateFailedSend";
import { createOutgoingSendLifecycle } from "./chat/outgoingSendLifecycle";
import { restoreComposerDraftAfterFailedSend } from "./chat/restoreComposerDraftAfterFailedSend";
import { isThreadTerminalCommand, placeThreadTerminalCommand } from "./chat/threadTerminalCommands";
import { useTimelineScrollAnchoring } from "./chat/useTimelineScrollAnchoring";
import { ChatComposer, type ChatComposerHandle } from "./chat/ChatComposer";
import { ExpandedImageDialog } from "./chat/ExpandedImageDialog";
import { ExpandedFileDialog } from "./chat/ExpandedFileDialog";
import { PullRequestThreadDialog } from "./PullRequestThreadDialog";
import { ThreadPullRequestsPanel } from "./pullRequest/ThreadPullRequestsPanel";
import { ContributionsPanel } from "./contributions/ContributionsPanel";
import { resolveBrowserProfiles } from "@afar-research/contracts";
import type { ThreadActionSelection, ThreadPullRequestLink } from "@afar-research/contracts";
import type { BrowserProfile } from "@afar-research/contracts";
import { CloudScreenView } from "../browser/CloudScreenView";
import { LinkPullRequestDialogHost } from "./pullRequest/LinkPullRequestDialog";
import { ChatHeader } from "./chat/ChatHeader";
import { PanelLayoutControls, RightPanelMaximizeControl } from "./chat/PanelLayoutControls";
import { type ExpandedImagePreview } from "./chat/ExpandedImagePreview";
import { type ExpandedFilePreview } from "./chat/ExpandedFilePreview";
import { EnvironmentRecoveryButton } from "./EnvironmentRecoveryButton";
import { NoActiveThreadState } from "./NoActiveThreadState";
import { ThreadUnavailableState } from "./ThreadUnavailableState";
import { presentUnreadableThread } from "./ThreadUnreadableState.logic";
import { ComposerSurface } from "./chat/ComposerSurface";
import { ThreadUnreadableState } from "./ThreadUnreadableState";
import {
  composerFileAsPathAttachment,
  resolveComposerAttachmentsForSend,
  type ComposerPathAttachment,
} from "../lib/composerAttachments";
import { EmptyThread } from "./EmptyThread";
import { deriveEmptyThreadState, legacyWorkingDirectoryNotice } from "./emptyThread.logic";
import {
  customActionSkills,
  initialActionSelection,
  listComposerActionEntries,
  resolveActionSkillInvocations,
} from "./chat/composerActions.logic";
import { useProjectAlwaysIngestAttachments } from "../hooks/useProjectAlwaysIngestAttachments";
import { draftLogicalProjectKey } from "../hooks/useHandleNewThread";
import { useNextThreadAction } from "../hooks/useNextThreadAction";
import { resolveDraftContextForEnvMode, resolveEffectiveEnvMode } from "./BranchToolbar.logic";
import { ProviderStatusBanner } from "./chat/ProviderStatusBanner";
import { ThreadErrorBanner } from "./chat/ThreadErrorBanner";
import type { ComposerBannerStackItem } from "./chat/ComposerBannerStack";
import {
  MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
  buildExpiredTerminalContextToastCopy,
  buildLocalDraftThread,
  buildThreadTurnInterruptInput,
  resolveStopRequestKey,
  collectUserMessageBlobPreviewUrls,
  createLocalDispatchSnapshot,
  deriveComposerSendState,
  hasServerAcknowledgedLocalDispatch,
  isTurnInProgress,
  getStartedThreadModelChangeBlockReason,
  LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
  LastInvokedScriptByProjectSchema,
  type LocalDispatchSnapshot,
  type ProposedPlanImplementationOutcome,
  observeProposedPlanImplementation,
  PullRequestDialogState,
  cloneComposerImageForRetry,
  deriveLockedProvider,
  readFileAsDataUrl,
  reconcileMountedTerminalThreadIds,
  navigateToForkedThread,
  resolveAttachmentOnlyPrompt,
  resolveSendEnvMode,
  resolveRouteEnvironmentUnavailability,
  type EnvironmentUnavailableState,
  shouldBlockOutgoingSend,
  resolveOutgoingSendDisposition,
  resolveOutgoingSendComposerSource,
  resolveTimelineProviderSkills,
  revokeBlobPreviewUrl,
  revokeUserMessagePreviewUrls,
  waitForProposedPlanImplementationOutcome,
  waitForRevertToLand,
  planRevertedMessageAttachments,
  fetchRevertedImageAttachments,
  waitForStartedServerThread,
} from "./ChatView.logic";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { useComposerHandleContext } from "../composerHandleContext";
import { deriveWorkflowToolCards } from "./workflow/workflowCards.logic";
import {
  commandFailureMessage,
  sanitizeThreadErrorMessage,
  threadCommandFailureMessage,
} from "~/rpc/transportError";
import { RightPanelSheet } from "./RightPanelSheet";
import { previewEnvironment } from "../state/preview";
import { useAtomCommand } from "../state/use-atom-command";
import { useReconnectEnvironment } from "../hooks/useReconnectEnvironment";
import { resolveAssetUrl, useAssetUrlStates } from "../assets/assetUrls";
import { assetEnvironment } from "../state/assets";
import { useAtomQueryRunner } from "../state/use-atom-query-runner";
import { readPreparedConnection } from "../state/session";
import { useAutoBalancedDraftEnvironment } from "../hooks/useAutoBalancedDraftEnvironment";
import {
  WorkspaceShellComposer,
  WorkspaceShellHeader,
  WorkspaceShellRegion,
} from "./WorkspaceShellPrimitives";
import { resolveComposerTimelineInset } from "./composerFooterLayout";
import { restrictToMachinePool } from "@afar-research/afar";
import { useMachinePool } from "../hooks/useMachinePool";

const EMPTY_ACTIVITIES: OrchestrationThreadActivity[] = [];
const EMPTY_SUBAGENTS = [] as const;
const EMPTY_PROVIDERS: ServerProvider[] = [];
const EMPTY_COMPOSER_BANNER_ITEMS: readonly ComposerBannerStackItem[] = [];
const EMPTY_PROVIDER_SKILLS: ServerProvider["skills"] = [];
const EMPTY_PENDING_USER_INPUT_ANSWERS: Record<string, PendingUserInputDraftAnswer> = {};
const importPreviewPanel = () =>
  import("./preview/PreviewPanel").then((module) => ({ default: module.PreviewPanel }));
const PreviewPanel = lazy(importPreviewPanel);
const MessagesTimeline = lazy(() =>
  import("./chat/MessagesTimeline").then((module) => ({ default: module.MessagesTimeline })),
);
const AgentConversationPanel = lazy(() =>
  import("./AgentConversationPanel").then((module) => ({
    default: module.AgentConversationPanel,
  })),
);
type RightPanelCloseCommand = Extract<
  WorkspaceSurfaceCommand<RightPanelSurface>,
  { readonly type: "close" | "close-others" | "close-to-right" | "close-all" }
>;
const EMPTY_PENDING_FILE_SURFACE_IDS: ReadonlySet<string> = new Set();
const TYPE_TO_FOCUS_EDITABLE_SELECTOR = [
  "input",
  "textarea",
  "select",
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  '[role="textbox"]',
].join(",");
const TYPE_TO_FOCUS_INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "summary",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(",");
const TYPE_TO_FOCUS_FLOATING_LAYER_SELECTOR = [
  '[data-slot="dialog"]',
  '[data-slot="menu-popup"]',
  '[data-slot="select-popup"]',
  '[data-slot="popover-popup"]',
  '[data-slot="combobox-popup"]',
  '[data-slot="autocomplete-popup"]',
].join(",");

function eventPathContainsSelector(event: Event, selector: string): boolean {
  const path = event.composedPath();
  if (path.length === 0 && event.target) {
    path.push(event.target);
  }
  return path.some((target) => target instanceof Element && target.closest(selector));
}

function shouldTypeToFocusComposer(event: KeyboardEvent): boolean {
  if (event.defaultPrevented || event.isComposing) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (event.key.length !== 1) return false;

  if (eventPathContainsSelector(event, TYPE_TO_FOCUS_EDITABLE_SELECTOR)) return false;
  if (eventPathContainsSelector(event, TYPE_TO_FOCUS_INTERACTIVE_SELECTOR)) return false;
  if (document.querySelector(TYPE_TO_FOCUS_FLOATING_LAYER_SELECTOR)) return false;

  return true;
}

const SCRIPT_TERMINAL_COLS = 120;
const SCRIPT_TERMINAL_ROWS = 30;

type ChatViewProps =
  | {
      environmentId: EnvironmentId;
      threadId: ThreadId;
      onDiffPanelOpen?: () => void;
      reserveTitleBarControlInset?: boolean;
      routeKind: "server";
      draftId?: never;
    }
  | {
      environmentId: EnvironmentId;
      threadId: ThreadId;
      onDiffPanelOpen?: () => void;
      reserveTitleBarControlInset?: boolean;
      routeKind: "draft";
      draftId: DraftId;
    };

interface TerminalLaunchContext {
  threadId: ThreadId;
  cwd: string;
  worktreePath: string | null;
}

function useLocalDispatchState(input: {
  activeThread: Thread | undefined;
  activeLatestTurn: Thread["latestTurn"] | null;
  phase: SessionPhase;
  activePendingApproval: ApprovalRequestId | null;
  activePendingUserInput: ApprovalRequestId | null;
  threadError: string | null | undefined;
}) {
  const [localDispatch, setLocalDispatch] = useState<LocalDispatchSnapshot | null>(null);

  const resetLocalDispatch = useCallback(() => {
    setLocalDispatch(null);
  }, []);

  const serverAcknowledgedLocalDispatch = useMemo(
    () =>
      hasServerAcknowledgedLocalDispatch({
        localDispatch,
        phase: input.phase,
        latestTurn: input.activeLatestTurn,
        session: input.activeThread?.session ?? null,
        hasPendingApproval: input.activePendingApproval !== null,
        hasPendingUserInput: input.activePendingUserInput !== null,
        threadError: input.threadError,
      }),
    [
      input.activeLatestTurn,
      input.activePendingApproval,
      input.activePendingUserInput,
      input.activeThread?.session,
      input.phase,
      input.threadError,
      localDispatch,
    ],
  );
  const activeLocalDispatch = serverAcknowledgedLocalDispatch ? null : localDispatch;
  const beginLocalDispatch = useCallback(
    (options?: { preparingWorktree?: boolean }) => {
      const preparingWorktree = Boolean(options?.preparingWorktree);
      setLocalDispatch((current) => {
        const active = serverAcknowledgedLocalDispatch ? null : current;
        if (active) {
          return active.preparingWorktree === preparingWorktree
            ? active
            : { ...active, preparingWorktree };
        }
        return createLocalDispatchSnapshot(input.activeThread, options);
      });
    },
    [input.activeThread, serverAcknowledgedLocalDispatch],
  );

  return {
    beginLocalDispatch,
    resetLocalDispatch,
    localDispatchStartedAt: activeLocalDispatch?.startedAt ?? null,
    isPreparingWorktree: activeLocalDispatch?.preparingWorktree ?? false,
    isSendBusy: activeLocalDispatch !== null,
  };
}

/** Same terminal ids (order ignored) — avoids reconcile when only server session ordering differs. */
// Incognito is a per-tab choice made inside the browser, not a profile a
// workspace surface launches under, so the picker lists the persistent ones.
// Hoisted so `useEnvironmentSettings` memoizes one array across renders rather
// than handing the right panel a new prop identity every time.
const selectBrowserProfileOptions = (settings: {
  readonly browserProfiles: ReadonlyArray<BrowserProfile>;
}) =>
  resolveBrowserProfiles(settings.browserProfiles)
    .filter((profile) => profile.kind !== "incognito")
    .map((profile) => ({ id: profile.id, name: profile.name }));

function ChatViewContent(props: ChatViewProps) {
  const {
    environmentId,
    threadId,
    routeKind,
    onDiffPanelOpen,
    reserveTitleBarControlInset = true,
  } = props;
  const draftId = routeKind === "draft" ? props.draftId : null;
  const routeThreadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const goalControl = useProviderGoal(routeKind === "server" ? routeThreadRef : null);
  const compactionControl = useManualCompaction(routeKind === "server" ? routeThreadRef : null);
  const conversationCommandsInFlight = useRef(new Set<string>());
  const temporaryWorktreeBranches = useRef(new Map<string, string>());
  const routeThreadKey = useMemo(() => scopedThreadKey(routeThreadRef), [routeThreadRef]);
  const currentRouteThreadKey = useRef(routeThreadKey);
  currentRouteThreadKey.current = routeThreadKey;
  const updateProject = useAtomCommand(projectEnvironment.update, { reportFailure: false });
  const upsertKeybinding = useAtomCommand(serverEnvironment.upsertKeybinding, {
    reportFailure: false,
  });
  const openTerminal = useAtomCommand(terminalEnvironment.open, "terminal open");
  const deleteDesignTargets = useAtomCommand(projectEnvironment.deleteDesignTargets, {
    reportFailure: false,
  });
  // Only ever fired unawaited, to refresh a Provider Instance catalog that
  // refused a send. Nothing on the send path waits on it.
  const refreshPromptProvider = useAtomCommand(serverEnvironment.refreshProviders, {
    reportFailure: false,
  });
  const writeTerminal = useAtomCommand(terminalEnvironment.write, "terminal write");
  const closeTerminalMutation = useAtomCommand(terminalEnvironment.close, "terminal close");
  // Fire-and-forget: nothing on the send path awaits this, and a failure is
  // left for the turn to report rather than surfacing on an unsent thread.
  const warmThreadSession = useAtomCommand(threadEnvironment.warmSession, {
    reportFailure: false,
  });
  const createThread = useAtomCommand(threadEnvironment.create, { reportFailure: false });
  const startDraftGoal = useAtomCommand(threadEnvironment.controlGoal, { reportFailure: false });
  const forkThread = useAtomCommand(threadEnvironment.fork, { reportFailure: false });
  const deleteThread = useAtomCommand(threadEnvironment.delete, { reportFailure: false });
  const updateThreadMetadata = useAtomCommand(threadEnvironment.updateMetadata, {
    reportFailure: false,
  });
  const setThreadRuntimeMode = useAtomCommand(threadEnvironment.setRuntimeMode, {
    reportFailure: false,
  });
  const setThreadInteractionMode = useAtomCommand(threadEnvironment.setInteractionMode, {
    reportFailure: false,
  });
  const startThreadTurn = useAtomCommand(threadEnvironment.startTurn, { reportFailure: false });
  const interruptThreadTurn = useAtomCommand(threadEnvironment.interruptTurn, {
    reportFailure: false,
  });
  const respondToThreadApproval = useAtomCommand(threadEnvironment.respondToApproval, {
    reportFailure: false,
  });
  const respondToThreadUserInput = useAtomCommand(threadEnvironment.respondToUserInput, {
    reportFailure: false,
  });
  const revertThreadCheckpoint = useAtomCommand(threadEnvironment.revertCheckpoint, {
    reportFailure: false,
  });
  // A revert prunes its discarded messages' stored image bytes, so those are
  // fetched through the asset surface before the rewind runs.
  const createRevertAssetUrl = useAtomQueryRunner(assetEnvironment.createUrl, {
    reportFailure: false,
  });
  const openPreview = useAtomCommand(previewEnvironment.open, { reportFailure: false });
  const closePreview = useAtomCommand(previewEnvironment.close, "preview close");
  const { environments } = useEnvironments();
  const primaryEnvironment = usePrimaryEnvironment();
  const environmentById = useMemo(
    () => new Map(environments.map((environment) => [environment.environmentId, environment])),
    [environments],
  );
  const composerDraftTarget: ScopedThreadRef | DraftId =
    routeKind === "server" ? routeThreadRef : props.draftId;
  const threadHistory = usePaginatedThreadHistory(routeKind === "server" ? routeThreadRef : null);
  const serverThread = threadHistory.data;
  const serverThreadReadState = useEnvironmentThread(
    routeKind === "server" ? environmentId : null,
    routeKind === "server" ? threadId : null,
  );
  const serverThreadShell = useThreadShell(routeKind === "server" ? routeThreadRef : null);
  const markThreadVisited = useUiStateStore((store) => store.markThreadVisited);
  const activeThreadLastVisitedAt = useUiStateStore((store) =>
    routeKind === "server" ? store.threadLastVisitedAtById[routeThreadKey] : undefined,
  );
  const settings = useEnvironmentSettings(environmentId);
  const setStickyComposerModelSelection = useComposerDraftStore(
    (store) => store.setStickyModelSelection,
  );
  const timestampFormat = settings.timestampFormat;
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  // Granular store selectors — avoid subscribing to prompt changes.
  const composerRuntimeMode = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.runtimeMode ?? null,
  );
  const composerInteractionMode = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.interactionMode ?? null,
  );
  // The Action chip the next send carries; it belongs to the draft and clears with the send.
  const composerAction = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.action ?? null,
  );
  const composerActiveProvider = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.activeProvider ?? null,
  );
  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
  const addComposerDraftFiles = useComposerDraftStore((store) => store.addFiles);
  const setComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.setTerminalContexts,
  );
  const setComposerDraftPreviewAnnotations = useComposerDraftStore(
    (store) => store.setPreviewAnnotations,
  );
  const setComposerDraftReviewComments = useComposerDraftStore((store) => store.setReviewComments);
  const setComposerDraftModelSelection = useComposerDraftStore((store) => store.setModelSelection);
  const setComposerDraftRuntimeMode = useComposerDraftStore((store) => store.setRuntimeMode);
  const setComposerDraftAction = useComposerDraftStore((store) => store.setComposerAction);
  const setComposerDraftInteractionMode = useComposerDraftStore(
    (store) => store.setInteractionMode,
  );
  const clearComposerDraftContent = useComposerDraftStore((store) => store.clearComposerContent);
  const discardComposerDraftContent = useComposerDraftStore(
    (store) => store.discardComposerContent,
  );
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const getDraftSessionByLogicalProjectKey = useComposerDraftStore(
    (store) => store.getDraftSessionByLogicalProjectKey,
  );
  const getDraftSession = useComposerDraftStore((store) => store.getDraftSession);
  const setLogicalProjectDraftThreadId = useComposerDraftStore(
    (store) => store.setLogicalProjectDraftThreadId,
  );
  const draftThread = useComposerDraftStore((store) =>
    routeKind === "server"
      ? store.getDraftSessionByRef(routeThreadRef)
      : draftId
        ? store.getDraftSession(draftId)
        : null,
  );
  const promptRef = useRef("");
  const composerImagesRef = useRef<ComposerImageAttachment[]>([]);
  const composerFilesRef = useRef<ComposerFileAttachment[]>([]);
  const composerTerminalContextsRef = useRef<TerminalContextDraft[]>([]);
  const localComposerRef = useRef<ChatComposerHandle | null>(null);
  const composerRef = useComposerHandleContext() ?? localComposerRef;
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);
  const [expandedFile, setExpandedFile] = useState<ExpandedFilePreview | null>(null);
  const pendingDesignOperationRef = useRef<DesignOperationSubmission | undefined>(undefined);
  const [optimisticUserMessages, setOptimisticUserMessages] = useState<ChatMessage[]>([]);
  const optimisticUserMessagesRef = useRef(optimisticUserMessages);
  optimisticUserMessagesRef.current = optimisticUserMessages;
  const [localDraftErrorsByDraftId, setLocalDraftErrorsByDraftId] = useState<
    Record<string, string | null>
  >({});
  const [localServerErrorsByThreadKey, setLocalServerErrorsByThreadKey] = useState<
    Record<string, string | null>
  >({});
  const [isConnecting, _setIsConnecting] = useState(false);
  const [isForkingThread, setIsForkingThread] = useState(false);
  const [isRevertingCheckpoint, setIsRevertingCheckpoint] = useState(false);
  const [maximizedRightPanelThreadKey, setMaximizedRightPanelThreadKey] = useState<string | null>(
    null,
  );
  const [respondingRequestIds, setRespondingRequestIds] = useState<ApprovalRequestId[]>([]);
  const [respondingUserInputRequestIds, setRespondingUserInputRequestIds] = useState<
    ApprovalRequestId[]
  >([]);
  const [pendingUserInputAnswersByRequestId, setPendingUserInputAnswersByRequestId] = useState<
    Record<string, Record<string, PendingUserInputDraftAnswer>>
  >({});
  const [pendingUserInputQuestionIndexByRequestId, setPendingUserInputQuestionIndexByRequestId] =
    useState<Record<string, number>>({});
  const workspacePanelLayout = useWorkspacePanelLayout();
  const narrowPanelViewport = useMediaQuery(RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY);
  const shouldUseRightPanelSheet = narrowPanelViewport || workspacePanelLayout.useSheet;
  const [terminalFocusRequestId, setTerminalFocusRequestId] = useState(0);
  const [pullRequestDialogState, setPullRequestDialogState] =
    useState<PullRequestDialogState | null>(null);
  const [terminalUiLaunchContext, setTerminalUiLaunchContext] =
    useState<TerminalLaunchContext | null>(null);
  const [attachmentPreviewHandoffByMessageId, setAttachmentPreviewHandoffByMessageId] = useState<
    Record<string, string[]>
  >({});
  const [pendingServerThreadEnvMode, setPendingServerThreadEnvMode] =
    useState<DraftThreadEnvMode | null>(null);
  const [pendingServerThreadBranch, setPendingServerThreadBranch] = useState<string | null>();
  const [
    pendingServerThreadStartFromOriginByThreadId,
    setPendingServerThreadStartFromOriginByThreadId,
  ] = useState<Record<string, boolean>>({});
  const [lastInvokedScriptByProjectId, setLastInvokedScriptByProjectId] = useLocalStorage(
    LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
    {},
    LastInvokedScriptByProjectSchema,
  );
  const legendListRef = useRef<LegendListRef | null>(null);
  const [composerOverlayElement, setComposerOverlayElement] = useState<HTMLDivElement | null>(null);
  const [composerOverlayHeight, setComposerOverlayHeight] = useState(0);
  // The space the timeline reserves for the composer, held through a resting
  // composer's shorter overlay so resting and expanding never move the rows.
  const [composerTimelineInset, setComposerTimelineInset] = useState(0);
  const composerTimelineInsetRef = useRef(0);
  const composerRestingRef = useRef(false);
  const [timelineOverflows, setTimelineOverflows] = useState(false);
  const attachmentPreviewHandoffByMessageIdRef = useRef<Record<string, string[]>>({});
  const attachmentPreviewPromotionInFlightByMessageIdRef = useRef<Record<string, true>>({});
  const sendInFlightRef = useRef(false);
  const terminalUiOpenByThreadRef = useRef<Record<string, boolean>>({});

  // The resize observer below and the composer's resting transition both
  // publish here, so the live height and the reservation never disagree.
  const publishComposerOverlayHeight = useCallback(
    (height: number) => {
      const nextHeight = Math.ceil(height);
      if (nextHeight <= 0) return;
      setComposerOverlayHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight,
      );
      const isResting = composerRestingRef.current;
      // The inline controls row exists only while resting, so it is not part
      // of the expansion the reservation estimates.
      const inlineRestingRow = isResting
        ? composerOverlayElement?.querySelector<HTMLElement>(
            '[data-chat-composer-inline-resting-row="true"]',
          )
        : null;
      const nextInset = resolveComposerTimelineInset({
        currentInset: composerTimelineInsetRef.current,
        overlayHeight: nextHeight,
        isResting,
        restingOnlyHeight: inlineRestingRow?.offsetHeight ?? 0,
      });
      if (composerTimelineInsetRef.current !== nextInset) {
        composerTimelineInsetRef.current = nextInset;
        setComposerTimelineInset(nextInset);
      }
    },
    [composerOverlayElement],
  );
  // The composer reports its resting flag from a layout effect, which runs
  // before this component's layout effects and before any resize observation,
  // so every published height sees the flag for its own layout.
  const onComposerRestingChange = useCallback((resting: boolean) => {
    composerRestingRef.current = resting;
  }, []);

  useLayoutEffect(() => {
    if (!composerOverlayElement) return;

    const updateHeight = () => {
      publishComposerOverlayHeight(composerOverlayElement.getBoundingClientRect().height);
    };

    updateHeight();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateHeight);
    observer.observe(composerOverlayElement);
    return () => observer.disconnect();
  }, [composerOverlayElement, publishComposerOverlayHeight]);

  const terminalUiState = useTerminalUiStateStore((state) =>
    selectThreadTerminalUiState(state.terminalUiStateByThreadKey, routeThreadRef),
  );
  const openTerminalThreadKeys = useTerminalUiStateStore(
    useShallow((state) => selectOpenTerminalThreadKeys(state.terminalUiStateByThreadKey)),
  );
  const storeSetTerminalOpen = useTerminalUiStateStore((s) => s.setTerminalOpen);
  const storeEnsureTerminal = useTerminalUiStateStore((state) => state.ensureTerminal);
  const storeSplitTerminal = useTerminalUiStateStore((s) => s.splitTerminal);
  const storeSplitTerminalVertical = useTerminalUiStateStore((s) => s.splitTerminalVertical);
  const storeNewTerminal = useTerminalUiStateStore((s) => s.newTerminal);
  const storeSetActiveTerminal = useTerminalUiStateStore((s) => s.setActiveTerminal);
  const storeCloseTerminal = useTerminalUiStateStore((s) => s.closeTerminal);
  const serverThreadRefs = useThreadRefs();
  const serverThreadKeys = useMemo(() => serverThreadRefs.map(scopedThreadKey), [serverThreadRefs]);
  const draftThreadsByThreadKey = useComposerDraftStore((store) => store.draftThreadsByThreadKey);
  const draftThreadKeys = useMemo(
    () =>
      Object.values(draftThreadsByThreadKey).map((draftThread) =>
        scopedThreadKey(scopeThreadRef(draftThread.environmentId, draftThread.threadId)),
      ),
    [draftThreadsByThreadKey],
  );
  const [mountedTerminalThreadKeys, setMountedTerminalThreadKeys] = useState<string[]>([]);
  const mountedTerminalThreadRefs = useMemo(
    () =>
      mountedTerminalThreadKeys.flatMap((mountedThreadKey) => {
        const mountedThreadRef = parseScopedThreadKey(mountedThreadKey);
        return mountedThreadRef ? [{ key: mountedThreadKey, threadRef: mountedThreadRef }] : [];
      }),
    [mountedTerminalThreadKeys],
  );

  const fallbackDraftProjectRef = draftThread
    ? scopeProjectRef(draftThread.environmentId, draftThread.projectId)
    : null;
  const fallbackDraftProject = useProject(fallbackDraftProjectRef);
  const localDraftError =
    routeKind === "server" && serverThread
      ? null
      : ((draftId ? localDraftErrorsByDraftId[draftId] : null) ?? null);
  const localServerError = localServerErrorsByThreadKey[routeThreadKey] ?? null;
  const localDraftThread = useMemo(
    () =>
      draftThread
        ? buildLocalDraftThread(
            threadId,
            draftThread,
            fallbackDraftProject?.defaultModelSelection ?? {
              instanceId: ProviderInstanceId.make("codex"),
              model: DEFAULT_MODEL,
            },
          )
        : undefined,
    [draftThread, fallbackDraftProject?.defaultModelSelection, threadId],
  );
  const isServerThread = routeKind === "server" && serverThread !== null;
  const activeThread = isServerThread ? serverThread : localDraftThread;
  const threadError = isServerThread
    ? (localServerError ?? serverThread?.session?.lastError ?? null)
    : localDraftError;
  const runtimeMode = composerRuntimeMode ?? activeThread?.runtimeMode ?? DEFAULT_RUNTIME_MODE;
  const interactionMode =
    composerInteractionMode ?? activeThread?.interactionMode ?? DEFAULT_INTERACTION_MODE;
  // The Brain work and the Design Operation the composer's chip carries, if any.
  const composerBrainAction = brainActionOf(composerAction);
  const isLocalDraftThread = !isServerThread && localDraftThread !== undefined;
  const canCheckoutPullRequestIntoThread = isLocalDraftThread;
  const activeThreadId = activeThread?.id ?? null;
  const runningTerminalIds = useThreadRunningTerminalIds({
    environmentId: activeThread?.environmentId ?? null,
    threadId: activeThreadId,
  });
  const activeThreadKnownSessionsRaw = useKnownTerminalSessions({
    environmentId: activeThread?.environmentId ?? null,
    threadId: activeThreadId,
  });
  const activeThreadKnownSessions = useMemo(() => {
    if (activeThreadId === null) {
      return [];
    }
    return activeThreadKnownSessionsRaw.filter(
      (session) => session.target.threadId === activeThreadId,
    );
  }, [activeThreadId, activeThreadKnownSessionsRaw]);
  const activeServerOrderedTerminalIds = useMemo(
    () => activeThreadKnownSessions.map((session) => session.target.terminalId),
    [activeThreadKnownSessions],
  );
  const activeKnownTerminalIds = useMemo(
    () => [...new Set([...activeServerOrderedTerminalIds, ...terminalUiState.terminalIds])],
    [activeServerOrderedTerminalIds, terminalUiState.terminalIds],
  );
  const activeTerminalLabelsById = useMemo(() => {
    const labels = new Map<string, string>();
    for (const session of activeThreadKnownSessions) {
      labels.set(
        session.target.terminalId,
        resolveTerminalSessionLabel(session.target.terminalId, session.state.summary),
      );
    }
    return labels;
  }, [activeThreadKnownSessions]);
  const activeThreadRef = useMemo(
    () => (activeThread ? scopeThreadRef(activeThread.environmentId, activeThread.id) : null),
    [activeThread],
  );
  const previewMiniPlayer = usePreviewMiniPlayerStore((state) =>
    selectThreadPreviewMiniPlayer(state.byThreadKey, activeThreadRef),
  );
  const activeThreadKey = activeThreadRef ? scopedThreadKey(activeThreadRef) : null;
  const activeWorkspaceSurfaceScope = useMemo(
    () =>
      activeThread
        ? {
            type: "thread" as const,
            workspace: {
              environmentId: activeThread.environmentId,
              projectId: activeThread.projectId,
            },
            threadId: activeThread.id,
          }
        : null,
    [activeThread],
  );
  // A held reservation belongs to the previous thread's draft. Rebuild it from
  // this thread's overlay so a tall draft elsewhere does not pad this one.
  useLayoutEffect(() => {
    if (!composerOverlayElement) return;
    composerTimelineInsetRef.current = 0;
    publishComposerOverlayHeight(composerOverlayElement.getBoundingClientRect().height);
  }, [activeThreadKey, composerOverlayElement, publishComposerOverlayHeight]);
  const [timelineAnchor, setTimelineAnchor] = useState<{
    readonly threadKey: string | null;
    readonly messageId: MessageId | null;
  }>({ threadKey: activeThreadKey, messageId: null });
  if (timelineAnchor.threadKey !== activeThreadKey) {
    setTimelineAnchor({ threadKey: activeThreadKey, messageId: null });
  }
  const timelineAnchorMessageId = timelineAnchor.messageId;
  const activeRightPanelKind = useRightPanelStore((state) =>
    selectActiveRightPanel(state.byThreadKey, activeThreadRef),
  );
  const diffOpen = activeRightPanelKind === "diff";
  const rightPanelState = useRightPanelStore((state) =>
    selectThreadRightPanelState(state.byThreadKey, activeThreadRef),
  );
  const activeRightPanelSurface = useRightPanelStore((state) =>
    selectActiveRightPanelSurface(state.byThreadKey, activeThreadRef),
  );
  const activePreviewState = useThreadPreviewSurfaceState(activeThreadRef);
  const panelTerminalIds = useMemo(
    () =>
      new Set(
        rightPanelState.surfaces.flatMap((surface) =>
          surface.kind === "terminal" ? surface.terminalIds : [],
        ),
      ),
    [rightPanelState.surfaces],
  );
  const browserAvailable = isPreviewSupportedInRuntime(activeThreadRef?.environmentId);
  const hasCloudDisplay = useEnvironmentHasCloudDisplay(activeThreadRef?.environmentId ?? null);
  const previewPanelOpen = activeRightPanelKind === "preview" && browserAvailable;
  const rightPanelOpen = rightPanelState.isOpen;
  const canMaximizeRightPanel = rightPanelOpen && !shouldUseRightPanelSheet;
  const rightPanelMaximized =
    canMaximizeRightPanel && maximizedRightPanelThreadKey === routeThreadKey;
  const inlineRightPanelOwnsTitleBar = rightPanelOpen && !shouldUseRightPanelSheet;

  useEffect(() => {
    if (!activeThreadRef) return;
    useRightPanelStore
      .getState()
      .reconcileBrowserSurfaces(activeThreadRef, Object.keys(activePreviewState.sessions));
  }, [activePreviewState.sessions, activeThreadRef]);

  const existingOpenTerminalThreadKeys = useMemo(() => {
    const existingThreadKeys = new Set<string>([...serverThreadKeys, ...draftThreadKeys]);
    return openTerminalThreadKeys.filter((nextThreadKey) => existingThreadKeys.has(nextThreadKey));
  }, [draftThreadKeys, openTerminalThreadKeys, serverThreadKeys]);
  const activeLatestTurn = activeThread?.latestTurn ?? null;
  useEffect(() => {
    setMountedTerminalThreadKeys((currentThreadIds) => {
      const nextThreadIds = reconcileMountedTerminalThreadIds({
        currentThreadIds,
        openThreadIds: existingOpenTerminalThreadKeys,
        activeThreadId: activeThreadKey,
        activeThreadTerminalOpen: Boolean(activeThreadKey && terminalUiState.terminalOpen),
        maxHiddenThreadCount: MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
      });
      return currentThreadIds.length === nextThreadIds.length &&
        currentThreadIds.every((nextThreadId, index) => nextThreadId === nextThreadIds[index])
        ? currentThreadIds
        : nextThreadIds;
    });
  }, [activeThreadKey, existingOpenTerminalThreadKeys, terminalUiState.terminalOpen]);
  const latestTurnSettled = isLatestTurnSettled(activeLatestTurn, activeThread?.session ?? null);
  const activeProjectRef = activeThread
    ? scopeProjectRef(activeThread.environmentId, activeThread.projectId)
    : null;
  const activeProject = useProject(activeProjectRef);
  // The project's threads, already in memory, for the empty thread's pipeline state.
  const projectThreadShells = useThreadShellsForProjectRefs(
    useMemo(() => (activeProjectRef === null ? [] : [activeProjectRef]), [activeProjectRef]),
  );
  const activeEnvironmentShell = useEnvironmentQuery(
    activeThread ? environmentShell.stateAtom(activeThread.environmentId) : null,
  );
  const activeEnvironmentBootstrapComplete = activeEnvironmentShell.data?.snapshot._tag === "Some";
  const activeProjectKey = activeProject
    ? `${activeProject.environmentId}:${activeProject.workspaceRoot}`
    : null;
  const [pendingFileSurfaceIdsByProject, setPendingFileSurfaceIdsByProject] = useState<
    ReadonlyMap<string, ReadonlySet<string>>
  >(() => new Map());
  const pendingFileSurfaceIds = activeProjectKey
    ? (pendingFileSurfaceIdsByProject.get(activeProjectKey) ?? EMPTY_PENDING_FILE_SURFACE_IDS)
    : EMPTY_PENDING_FILE_SURFACE_IDS;
  const handleFilePendingChange = useCallback(
    (relativePath: string, pending: boolean) => {
      if (!activeProjectKey) return;
      setPendingFileSurfaceIdsByProject((currentByProject) => {
        const current = currentByProject.get(activeProjectKey) ?? EMPTY_PENDING_FILE_SURFACE_IDS;
        const surfaceId = `file:${relativePath}`;
        if (current.has(surfaceId) === pending) return currentByProject;
        const next = new Set(current);
        if (pending) next.add(surfaceId);
        else next.delete(surfaceId);
        const nextByProject = new Map(currentByProject);
        if (next.size === 0) nextByProject.delete(activeProjectKey);
        else nextByProject.set(activeProjectKey, next);
        return nextByProject;
      });
    },
    [activeProjectKey],
  );
  const configuredPreviewUrls = useMemo(
    () => getConfiguredPreviewUrls(activeProject?.scripts),
    [activeProject?.scripts],
  );

  useEffect(() => {
    if (!activeThreadRef || !activeEnvironmentBootstrapComplete) return;
    useRightPanelStore.getState().reconcileFileSurfaces(activeThreadRef, activeProject !== null);
  }, [activeEnvironmentBootstrapComplete, activeProject, activeThreadRef]);

  // Compute the list of environments this logical project spans, used to
  // drive the environment picker in BranchToolbar.
  const allProjects = useProjects();
  const primaryEnvironmentId = primaryEnvironment?.environmentId ?? null;
  const activeEnvironment =
    activeThread == null ? null : (environmentById.get(activeThread.environmentId) ?? null);
  const activeEnvironmentConnectionPhase = activeEnvironment?.connection.phase ?? "unavailable";
  const activeEnvironmentUnavailable =
    activeEnvironment !== null && activeEnvironmentConnectionPhase !== "ready";
  const activeEnvironmentUnavailableLabel = activeEnvironment?.label ?? null;
  const activeEnvironmentUnavailableState = useMemo<EnvironmentUnavailableState | null>(() => {
    if (!activeEnvironmentUnavailable || !activeEnvironmentUnavailableLabel || !activeEnvironment) {
      return null;
    }

    return {
      environmentId: activeEnvironment.environmentId,
      label: activeEnvironmentUnavailableLabel,
      connection: activeEnvironment.connection,
    };
  }, [activeEnvironment, activeEnvironmentUnavailable, activeEnvironmentUnavailableLabel]);
  /**
   * The route's own Environment, which outlives the active thread: when a thread cannot
   * be read at all, it is the only thing left that can explain why.
   */
  const routeEnvironmentUnavailableState = useMemo(
    () =>
      resolveRouteEnvironmentUnavailability({
        routeKind,
        environment: environmentById.get(environmentId) ?? null,
      }),
    [environmentById, environmentId, routeKind],
  );
  const handleReconnectActiveEnvironment = useReconnectEnvironment();
  const projectGroupingSettings = selectProjectGroupingSettings(settings);
  const logicalProjectEnvironments = useMemo(() => {
    if (!activeProject) return [];
    const logicalKey = deriveLogicalProjectKeyFromSettings(activeProject, projectGroupingSettings);
    const memberProjects = allProjects.filter(
      (p) => deriveLogicalProjectKeyFromSettings(p, projectGroupingSettings) === logicalKey,
    );
    const seen = new Set<string>();
    const envs: Array<{
      environmentId: EnvironmentId;
      projectId: ProjectId;
      label: string;
      isPrimary: boolean;
    }> = [];
    for (const p of memberProjects) {
      if (seen.has(p.environmentId)) continue;
      seen.add(p.environmentId);
      const isPrimary = p.environmentId === primaryEnvironmentId;
      const label = environmentById.get(p.environmentId)?.label ?? p.environmentId;
      envs.push({
        environmentId: p.environmentId,
        projectId: p.id,
        label,
        isPrimary,
      });
    }
    // Sort: primary first, then alphabetical
    envs.sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
    return envs;
  }, [activeProject, allProjects, projectGroupingSettings, primaryEnvironmentId, environmentById]);
  const hasMultipleEnvironments = logicalProjectEnvironments.length > 1;

  const openPullRequestDialog = useCallback(
    (reference?: string) => {
      if (!canCheckoutPullRequestIntoThread) {
        return;
      }
      setPullRequestDialogState({
        initialReference: reference ?? null,
        key: Date.now(),
      });
    },
    [canCheckoutPullRequestIntoThread],
  );

  const closePullRequestDialog = useCallback(() => {
    setPullRequestDialogState(null);
  }, []);

  const openOrReuseProjectDraftThread = useCallback(
    async (input: { branch: string; worktreePath: string | null; envMode: DraftThreadEnvMode }) => {
      if (!activeProject) {
        throw new Error("No active project is available for this pull request.");
      }
      const activeProjectRef = scopeProjectRef(activeProject.environmentId, activeProject.id);
      const logicalProjectKey = deriveLogicalProjectKeyFromSettings(
        activeProject,
        projectGroupingSettings,
      );
      const draftKey = draftLogicalProjectKey(logicalProjectKey, activeProject.environmentId);
      const storedDraftSession = getDraftSessionByLogicalProjectKey(draftKey);
      if (storedDraftSession) {
        setDraftThreadContext(storedDraftSession.draftId, input);
        setLogicalProjectDraftThreadId(draftKey, activeProjectRef, storedDraftSession.draftId, {
          threadId: storedDraftSession.threadId,
          ...input,
        });
        if (routeKind !== "draft" || draftId !== storedDraftSession.draftId) {
          await navigate({
            to: "/draft/$draftId",
            params: buildDraftThreadRouteParams(storedDraftSession.draftId),
          });
        }
        return storedDraftSession.threadId;
      }

      const activeDraftSession = routeKind === "draft" && draftId ? getDraftSession(draftId) : null;
      if (!isServerThread && activeDraftSession?.logicalProjectKey === draftKey && draftId) {
        setDraftThreadContext(draftId, input);
        setLogicalProjectDraftThreadId(draftKey, activeProjectRef, draftId, {
          threadId: activeDraftSession.threadId,
          createdAt: activeDraftSession.createdAt,
          runtimeMode: activeDraftSession.runtimeMode,
          interactionMode: activeDraftSession.interactionMode,
          ...input,
        });
        return activeDraftSession.threadId;
      }

      const nextDraftId = newDraftId();
      const nextThreadId = newThreadId();
      setLogicalProjectDraftThreadId(draftKey, activeProjectRef, nextDraftId, {
        threadId: nextThreadId,
        createdAt: new Date().toISOString(),
        runtimeMode: DEFAULT_RUNTIME_MODE,
        interactionMode: DEFAULT_INTERACTION_MODE,
        ...input,
      });
      await navigate({
        to: "/draft/$draftId",
        params: buildDraftThreadRouteParams(nextDraftId),
      });
      return nextThreadId;
    },
    [
      activeProject,
      draftId,
      getDraftSession,
      getDraftSessionByLogicalProjectKey,
      isServerThread,
      navigate,
      projectGroupingSettings,
      routeKind,
      setDraftThreadContext,
      setLogicalProjectDraftThreadId,
    ],
  );

  const handlePreparedPullRequestThread = useCallback(
    async (input: { branch: string; worktreePath: string | null }) => {
      await openOrReuseProjectDraftThread({
        branch: input.branch,
        worktreePath: input.worktreePath,
        envMode: input.worktreePath ? "worktree" : "local",
      });
    },
    [openOrReuseProjectDraftThread],
  );

  useEffect(() => {
    if (!serverThread?.id) return;
    const threadUpdatedAt = Date.parse(serverThread.updatedAt);
    if (Number.isNaN(threadUpdatedAt)) return;
    const lastVisitedAt = activeThreadLastVisitedAt ? Date.parse(activeThreadLastVisitedAt) : NaN;
    if (!Number.isNaN(lastVisitedAt) && lastVisitedAt >= threadUpdatedAt) return;

    markThreadVisited(
      scopedThreadKey(scopeThreadRef(serverThread.environmentId, serverThread.id)),
      serverThread.updatedAt,
    );
  }, [
    activeThreadLastVisitedAt,
    markThreadVisited,
    serverThread?.environmentId,
    serverThread?.id,
    serverThread?.updatedAt,
  ]);

  const selectedProviderByThreadId = composerActiveProvider ?? null;
  const threadProvider =
    activeThread?.modelSelection.instanceId ??
    activeProject?.defaultModelSelection?.instanceId ??
    null;
  // Once a thread selects an environment, never substitute the primary
  // environment's config while the selected environment is still loading.
  const serverConfig = activeThread
    ? (activeEnvironment?.serverConfig ?? null)
    : (primaryEnvironment?.serverConfig ?? null);
  // Usually empty: one shared empty list keeps the composer's memo holding
  // across environment deltas. A notice is rebuilt on each delta while the
  // environment is unavailable; it carries React nodes, so `useStableValue`
  // cannot hold it still.
  const composerBannerItems = useMemo<readonly ComposerBannerStackItem[]>(() => {
    if (!activeEnvironmentUnavailableState) return EMPTY_COMPOSER_BANNER_ITEMS;
    const connection = activeEnvironmentUnavailableState.connection;
    const recovery = presentEnvironmentRecovery(connection);
    return [
      {
        id: `environment-unavailable:${activeEnvironmentUnavailableState.environmentId}`,
        variant: connection.error ? "error" : "warning",
        icon: <WifiOffIcon />,
        title: `${activeEnvironmentUnavailableState.label}: ${recovery.statusText}`,
        description: `${connection.error ?? "Reconnect this environment before sending messages or running actions."} ${recovery.recoveryDetail}`,
        actions: (
          <EnvironmentRecoveryButton
            connection={connection}
            size="xs"
            onRetry={() => {
              void handleReconnectActiveEnvironment(
                activeEnvironmentUnavailableState.environmentId,
              );
            }}
            onOpenConnectionSettings={() => {
              void navigate({ to: "/settings/connections" });
            }}
          />
        ),
      },
    ];
  }, [activeEnvironmentUnavailableState, handleReconnectActiveEnvironment, navigate]);
  const providerStatuses = serverConfig?.providers ?? EMPTY_PROVIDERS;
  const lockedProvider = deriveLockedProvider({
    thread: activeThread,
    selectedProvider: selectedProviderByThreadId,
    threadProvider,
    providers: providerStatuses,
  });
  const unlockedSelectedProvider = resolveSelectableProvider(
    providerStatuses,
    selectedProviderByThreadId ?? threadProvider ?? ProviderDriverKind.make("codex"),
  );
  const selectedProvider: ProviderDriverKind = lockedProvider ?? unlockedSelectedProvider;
  const conversationProviderInstanceId =
    activeThread?.session?.providerInstanceId ?? activeThread?.modelSelection.instanceId;
  const canForkNativeHistory =
    conversationProviderInstanceId !== undefined &&
    providerConversationOperationSupported(
      providerStatuses,
      conversationProviderInstanceId,
      "threadFork",
    );
  const canRevertConversation =
    conversationProviderInstanceId !== undefined &&
    providerConversationOperationSupported(
      providerStatuses,
      conversationProviderInstanceId,
      "conversationRevert",
    );
  const phase = derivePhase(activeThread?.session ?? null, activeThread?.latestTurn);
  const threadActivities = activeThread?.activities ?? EMPTY_ACTIVITIES;
  const subagentProjection = useMemo(
    () =>
      deriveCodexSubagentProjection(
        activeThread?.session?.providerName === "codex" ? ProviderDriverKind.make("codex") : null,
        threadActivities,
      ),
    [activeThread?.session?.providerName, threadActivities],
  );
  const projectedSubagents = subagentProjection?.agents ?? EMPTY_SUBAGENTS;
  const subagentsById = useMemo(
    () => new Map(projectedSubagents.map((agent) => [agent.id, agent])),
    [projectedSubagents],
  );
  const agentLabelsById = useMemo(
    () => new Map(projectedSubagents.map((agent) => [agent.id, agent.role ?? agent.identity])),
    [projectedSubagents],
  );
  const activeSubagent =
    activeRightPanelSurface?.kind === "agent"
      ? (subagentsById.get(activeRightPanelSurface.agentId) ?? null)
      : null;
  useEffect(() => {
    if (!activeThreadRef) return;
    useRightPanelStore.getState().reconcileAgentSurfaces(
      activeThreadRef,
      projectedSubagents.map((agent) => agent.id),
    );
  }, [activeThreadRef, projectedSubagents]);
  const workLogEntries = useMemo(() => deriveWorkLogEntries(threadActivities), [threadActivities]);
  // Both derivations answer with a fresh array on every provider delta. The
  // composer is memoed on prop identity, so they are stabilised here rather
  // than re-rendering it at stream frequency.
  const pendingApprovals = useStableValue(
    useMemo(() => derivePendingApprovals(threadActivities), [threadActivities]),
  );
  const pendingUserInputs = useStableValue(
    useMemo(() => derivePendingUserInputs(threadActivities), [threadActivities]),
  );
  const activePendingUserInput = pendingUserInputs[0] ?? null;
  const activePendingDraftAnswers = useMemo(
    () =>
      activePendingUserInput
        ? (pendingUserInputAnswersByRequestId[activePendingUserInput.requestId] ??
          EMPTY_PENDING_USER_INPUT_ANSWERS)
        : EMPTY_PENDING_USER_INPUT_ANSWERS,
    [activePendingUserInput, pendingUserInputAnswersByRequestId],
  );
  const activePendingQuestionIndex = activePendingUserInput
    ? (pendingUserInputQuestionIndexByRequestId[activePendingUserInput.requestId] ?? 0)
    : 0;
  const activePendingProgress = useMemo(
    () =>
      activePendingUserInput
        ? derivePendingUserInputProgress(
            activePendingUserInput.questions,
            activePendingDraftAnswers,
            activePendingQuestionIndex,
          )
        : null,
    [activePendingDraftAnswers, activePendingQuestionIndex, activePendingUserInput],
  );
  const activePendingResolvedAnswers = useMemo(
    () =>
      activePendingUserInput
        ? buildPendingUserInputAnswers(activePendingUserInput.questions, activePendingDraftAnswers)
        : null,
    [activePendingDraftAnswers, activePendingUserInput],
  );
  const activePendingIsResponding = activePendingUserInput
    ? respondingUserInputRequestIds.includes(activePendingUserInput.requestId)
    : false;
  const activeProposedPlan = useMemo(() => {
    if (!latestTurnSettled) {
      return null;
    }
    return findLatestProposedPlan(
      activeThread?.proposedPlans ?? [],
      activeLatestTurn?.turnId ?? null,
    );
  }, [activeLatestTurn?.turnId, activeThread?.proposedPlans, latestTurnSettled]);
  const proposedPlanHandoffPending = useProposedPlanHandoffStore((state) =>
    activeThreadRef && activeProposedPlan
      ? isProposedPlanHandoffPending(state, {
          sourceThreadRef: activeThreadRef,
          sourcePlanId: activeProposedPlan.id,
        })
      : false,
  );
  const agentTaskLists = useMemo(
    () => deriveAgentTaskListStates(threadActivities),
    [threadActivities],
  );
  const latestAgentTaskList =
    (activeLatestTurn
      ? agentTaskLists.findLast((taskList) => taskList.turnId === activeLatestTurn.turnId)
      : undefined) ??
    agentTaskLists.at(-1) ??
    null;
  const selectedAgentTaskList =
    (activeRightPanelSurface?.kind === "tasks" && activeRightPanelSurface.resourceId
      ? agentTaskLists.find(
          (taskList) => taskList.instanceId === activeRightPanelSurface.resourceId,
        )
      : undefined) ?? latestAgentTaskList;
  const hasAgentTaskList = agentTaskLists.length > 0;
  const taskCounts = useMemo(
    () =>
      selectedAgentTaskList
        ? {
            completed: selectedAgentTaskList.completedCount,
            total: selectedAgentTaskList.totalCount,
          }
        : undefined,
    [selectedAgentTaskList],
  );
  useEffect(() => {
    if (!activeThreadRef || !activeEnvironmentBootstrapComplete) return;
    useRightPanelStore.getState().reconcileTasksSurface(activeThreadRef, hasAgentTaskList);
  }, [activeEnvironmentBootstrapComplete, activeThreadRef, hasAgentTaskList]);
  const planJourneyPresentation = resolvePlanJourneyPresentation({
    interactionMode,
    latestTurnSettled,
    hasPendingUserInput: pendingUserInputs.length > 0,
    hasActionableProposedPlan: hasActionableProposedPlan(activeProposedPlan),
    suppressPlanReady: proposedPlanHandoffPending,
  });
  const showPlanFollowUpPrompt = planJourneyPresentation.showProposedPlanFollowUp;
  const activePendingApproval = pendingApprovals[0] ?? null;
  const {
    beginLocalDispatch,
    resetLocalDispatch,
    localDispatchStartedAt,
    isPreparingWorktree,
    isSendBusy,
  } = useLocalDispatchState({
    activeThread,
    activeLatestTurn,
    phase,
    activePendingApproval: activePendingApproval?.requestId ?? null,
    activePendingUserInput: activePendingUserInput?.requestId ?? null,
    threadError,
  });
  // Every outgoing send claims and releases the thread the same way; the rule
  // for when the local stand-in outlives the send lives in one module now.
  const sendLifecycle = useMemo(
    () =>
      createOutgoingSendLifecycle({
        setInFlight: (inFlight) => {
          sendInFlightRef.current = inFlight;
        },
        beginLocalDispatch,
        resetLocalDispatch,
      }),
    [beginLocalDispatch, resetLocalDispatch],
  );
  const turnInProgress = isTurnInProgress({
    phase,
    latestTurn: activeThread?.latestTurn ?? null,
    latestUserMessageAt:
      activeThread?.messages.findLast((message) => message.role === "user")?.createdAt ?? null,
  });
  const isWorking = turnInProgress || isSendBusy || isConnecting;
  const activeWorkStartedAt = deriveActiveWorkStartedAt(
    activeLatestTurn,
    activeThread?.session ?? null,
    localDispatchStartedAt,
  );
  useEffect(() => {
    attachmentPreviewHandoffByMessageIdRef.current = attachmentPreviewHandoffByMessageId;
  }, [attachmentPreviewHandoffByMessageId]);
  const clearAttachmentPreviewHandoff = useCallback(
    (messageId: MessageId, previewUrls?: ReadonlyArray<string>) => {
      delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
      const currentPreviewUrls =
        previewUrls ?? attachmentPreviewHandoffByMessageIdRef.current[messageId] ?? [];
      setAttachmentPreviewHandoffByMessageId((existing) => {
        if (!(messageId in existing)) {
          return existing;
        }
        const next = { ...existing };
        delete next[messageId];
        attachmentPreviewHandoffByMessageIdRef.current = next;
        return next;
      });
      for (const previewUrl of currentPreviewUrls) {
        revokeBlobPreviewUrl(previewUrl);
      }
    },
    [],
  );
  const clearAttachmentPreviewHandoffs = useCallback(() => {
    attachmentPreviewPromotionInFlightByMessageIdRef.current = {};
    for (const previewUrls of Object.values(attachmentPreviewHandoffByMessageIdRef.current)) {
      for (const previewUrl of previewUrls) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    attachmentPreviewHandoffByMessageIdRef.current = {};
    setAttachmentPreviewHandoffByMessageId({});
  }, []);
  useEffect(() => {
    return () => {
      clearAttachmentPreviewHandoffs();
      for (const message of optimisticUserMessagesRef.current) {
        revokeUserMessagePreviewUrls(message);
      }
    };
  }, [clearAttachmentPreviewHandoffs]);
  const handoffAttachmentPreviews = useCallback((messageId: MessageId, previewUrls: string[]) => {
    if (previewUrls.length === 0) return;

    const previousPreviewUrls = attachmentPreviewHandoffByMessageIdRef.current[messageId] ?? [];
    const nextPreviewUrlSet = new Set(previewUrls);
    for (const previewUrl of previousPreviewUrls) {
      if (!nextPreviewUrlSet.has(previewUrl)) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    setAttachmentPreviewHandoffByMessageId((existing) => {
      const next = {
        ...existing,
        [messageId]: previewUrls,
      };
      attachmentPreviewHandoffByMessageIdRef.current = next;
      return next;
    });
  }, []);
  const serverMessages = activeThread?.messages;
  const serverAttachmentIds = useMemo(() => {
    const attachmentIds = new Set<string>();
    for (const message of serverMessages ?? []) {
      for (const attachment of message.attachments ?? []) {
        attachmentIds.add(attachment.id);
      }
    }
    return [...attachmentIds];
  }, [serverMessages]);
  const serverAttachmentResources = useMemo(
    () =>
      activeThreadId === null
        ? []
        : serverAttachmentIds.map((attachmentId) => ({
            _tag: "attachment" as const,
            threadId: activeThreadId,
            attachmentId,
          })),
    [activeThreadId, serverAttachmentIds],
  );
  const serverAttachmentStates = useAssetUrlStates(environmentId, serverAttachmentResources);
  const serverAttachmentStateById = useMemo(
    () =>
      new Map(
        serverAttachmentIds.flatMap((attachmentId, index) => {
          const state = serverAttachmentStates[index];
          return state ? [[attachmentId, state] as const] : [];
        }),
      ),
    [serverAttachmentIds, serverAttachmentStates],
  );
  const displayServerMessages = useMemo<ReadonlyArray<ChatMessage>>(() => {
    if (!serverMessages) return [];
    return serverMessages.map((message) => {
      if (!message.attachments || message.attachments.length === 0) {
        return message;
      }
      return {
        ...message,
        attachments: message.attachments.map((attachment) => {
          const state = serverAttachmentStateById.get(attachment.id);
          if (state?.status === "ready") return { ...attachment, previewUrl: state.url };
          if (state?.status === "unavailable") return { ...attachment, previewUnavailable: true };
          return attachment;
        }),
      };
    });
  }, [serverAttachmentStateById, serverMessages]);
  useEffect(() => {
    if (typeof Image === "undefined" || displayServerMessages.length === 0) {
      return;
    }

    const cleanups: Array<() => void> = [];
    const userMessagesById = new Map<string, ChatMessage>(
      displayServerMessages
        .filter((message) => message.role === "user")
        .map((message) => [String(message.id), message] as const),
    );

    for (const [messageId, handoffPreviewUrls] of Object.entries(
      attachmentPreviewHandoffByMessageId,
    )) {
      if (attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId]) {
        continue;
      }

      const serverMessage = userMessagesById.get(messageId);
      if (!serverMessage?.attachments || serverMessage.attachments.length === 0) {
        continue;
      }

      const serverPreviewUrls = serverMessage.attachments.flatMap((attachment) =>
        attachment.type === "image" && attachment.previewUrl ? [attachment.previewUrl] : [],
      );
      if (
        serverPreviewUrls.length === 0 ||
        serverPreviewUrls.length !== handoffPreviewUrls.length ||
        serverPreviewUrls.some((previewUrl) => previewUrl.startsWith("blob:"))
      ) {
        continue;
      }

      attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId] = true;

      let cancelled = false;
      const imageInstances: HTMLImageElement[] = [];

      const preloadServerPreviews = Promise.all(
        serverPreviewUrls.map(
          (previewUrl) =>
            new Promise<void>((resolve, reject) => {
              const image = new Image();
              imageInstances.push(image);
              const handleLoad = () => resolve();
              const handleError = () =>
                reject(new Error(`Failed to load server preview for ${messageId}.`));
              image.addEventListener("load", handleLoad, { once: true });
              image.addEventListener("error", handleError, { once: true });
              image.src = previewUrl;
            }),
        ),
      );

      void preloadServerPreviews
        .then(() => {
          if (cancelled) {
            return;
          }
          clearAttachmentPreviewHandoff(messageId as MessageId, handoffPreviewUrls);
        })
        .catch(() => {
          if (!cancelled) {
            delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
          }
        });

      cleanups.push(() => {
        cancelled = true;
        delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
        for (const image of imageInstances) {
          image.src = "";
        }
      });
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [attachmentPreviewHandoffByMessageId, clearAttachmentPreviewHandoff, displayServerMessages]);
  // Keyed on the activity itself: once landed it keeps its identity, so the
  // snapshot only changes when the context window actually moves.
  const latestContextWindowActivity = useMemo(
    () => findLatestContextWindowActivity(threadActivities),
    [threadActivities],
  );
  const activeContextWindow = useMemo(
    () => deriveContextWindowSnapshotFromActivity(latestContextWindowActivity),
    [latestContextWindowActivity],
  );
  const followUpBehavior = useClientSettings((settings) => settings.followUpBehavior);
  const titleGenerationPending = activeThread?.titleRegeneration != null;
  const timelineMessages = useMemo(() => {
    const messages = orderTimelineMessages(displayServerMessages);
    const serverMessagesWithPreviewHandoff =
      Object.keys(attachmentPreviewHandoffByMessageId).length === 0
        ? messages
        : // Spread only fires for the few messages that actually changed;
          // unchanged ones early-return their original reference.
          // In-place mutation would break React's immutable state contract.
          messages.map((message) => {
            if (
              message.role !== "user" ||
              !message.attachments ||
              message.attachments.length === 0
            ) {
              return message;
            }
            const handoffPreviewUrls = attachmentPreviewHandoffByMessageId[message.id];
            if (!handoffPreviewUrls || handoffPreviewUrls.length === 0) {
              return message;
            }

            let changed = false;
            let imageIndex = 0;
            const attachments = message.attachments.map((attachment) => {
              if (attachment.type !== "image") {
                return attachment;
              }
              const handoffPreviewUrl = handoffPreviewUrls[imageIndex];
              imageIndex += 1;
              if (!handoffPreviewUrl || attachment.previewUrl === handoffPreviewUrl) {
                return attachment;
              }
              changed = true;
              return {
                ...attachment,
                previewUrl: handoffPreviewUrl,
              };
            });

            return changed ? { ...message, attachments } : message;
          });

    if (optimisticUserMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    // An optimistic copy is dropped once the server has the same message.
    const serverMessageIds = new Set(displayServerMessages.map((message) => message.id));
    const pendingMessages = orderTimelineMessages(
      optimisticUserMessages.filter((message) => !serverMessageIds.has(message.id)),
    );
    if (pendingMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    return orderTimelineMessages([...serverMessagesWithPreviewHandoff, ...pendingMessages]);
  }, [attachmentPreviewHandoffByMessageId, displayServerMessages, optimisticUserMessages]);
  /**
   * Every settled Design Operation has written, moved, or removed something
   * under the Design Workspace, so the Design System listing the `@` picker and
   * the composer's held mentions read from is describing the past until it is
   * taken again. A Rename is the sharp case: the picker kept offering the old
   * name at a path the Design System had already left.
   */
  const latestSettledDesignOperation = useMemo(
    () => latestSettledDesignOperationId(activeThread?.activities ?? []),
    [activeThread?.activities],
  );
  useEffect(() => {
    if (latestSettledDesignOperation === null || activeProject === null) return;
    appAtomRegistry.refresh(
      projectEnvironment.discoverDesignSystemMentions({
        environmentId: activeProject.environmentId,
        input: { cwd: activeProject.workspaceRoot },
      }),
    );
    appAtomRegistry.refresh(
      getProjectEntriesQueryAtom(activeProject.environmentId, activeProject.workspaceRoot),
    );
  }, [activeProject, latestSettledDesignOperation]);

  const designDeleteReportEntries = useMemo(
    () => deriveDesignDeleteReportEntries(activeThread?.activities ?? []),
    [activeThread?.activities],
  );
  const workflowCards = useMemo(
    () => deriveWorkflowToolCards(activeThread?.activities ?? []),
    [activeThread?.activities],
  );
  const timelineEntries = useMemo(
    () =>
      deriveTimelineEntries(timelineMessages, activeThread?.proposedPlans ?? [], workLogEntries, {
        designDeleteReports: designDeleteReportEntries,
        workflowCards,
      }),
    [
      activeThread?.proposedPlans,
      designDeleteReportEntries,
      timelineMessages,
      workLogEntries,
      workflowCards,
    ],
  );
  const { turnDiffSummaries, inferredCheckpointTurnCountByTurnId } =
    useTurnDiffSummaries(activeThread);
  // The newest ready checkpoint is what a turn produced; the panel opens on it
  // once, and only for a turn that changed enough to be worth looking at.
  const latestReadyTurnDiffSummary = useMemo(
    () => turnDiffSummaries.find((summary) => summary.status === "ready") ?? null,
    [turnDiffSummaries],
  );
  useProactivePanel({
    threadRef: activeThreadRef,
    latestSummary: latestReadyTurnDiffSummary,
    hasLinkedPullRequest: visibleThreadPullRequests(activeThread?.pullRequests ?? []).length > 0,
    panelAlreadyOpen: rightPanelOpen,
  });
  const turnDiffSummaryByAssistantMessageId = useMemo(() => {
    const byMessageId = new Map<MessageId, TurnDiffSummary>();
    for (const summary of turnDiffSummaries) {
      if (!summary.assistantMessageId) continue;
      byMessageId.set(summary.assistantMessageId, summary);
    }
    return byMessageId;
  }, [turnDiffSummaries]);
  const checkpointTurnCountByUserMessageId = useMemo(() => {
    const byUserMessageId = new Map<MessageId, number>();
    for (let index = 0; index < timelineEntries.length; index += 1) {
      const entry = timelineEntries[index];
      if (!entry || entry.kind !== "message" || entry.message.role !== "user") {
        continue;
      }

      for (let nextIndex = index + 1; nextIndex < timelineEntries.length; nextIndex += 1) {
        const nextEntry = timelineEntries[nextIndex];
        if (!nextEntry || nextEntry.kind !== "message") {
          continue;
        }
        if (nextEntry.message.role === "user") {
          break;
        }
        const summary = turnDiffSummaryByAssistantMessageId.get(nextEntry.message.id);
        if (!summary || summary.status !== "ready") {
          continue;
        }
        const turnCount =
          summary.checkpointTurnCount ?? inferredCheckpointTurnCountByTurnId[summary.turnId];
        if (typeof turnCount !== "number") {
          break;
        }
        byUserMessageId.set(entry.message.id, Math.max(0, turnCount - 1));
        break;
      }
    }

    return byUserMessageId;
  }, [inferredCheckpointTurnCountByTurnId, timelineEntries, turnDiffSummaryByAssistantMessageId]);

  const gitCwd = activeProject
    ? projectScriptCwd({
        project: { cwd: activeProject.workspaceRoot },
        worktreePath: activeThread?.worktreePath ?? null,
      })
    : null;
  const gitStatusQuery = useEnvironmentQuery(
    gitCwd === null
      ? null
      : vcsEnvironment.status({
          environmentId,
          input: { cwd: gitCwd },
        }),
  );
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const availableEditors = useAtomValue(primaryServerAvailableEditorsAtom);
  // Prefer an instance-id match so a custom Codex instance (e.g.
  // `codex_personal`) surfaces its own status/message in the banner rather
  // than the default Codex's. Falls back to first-match-by-kind when no
  // saved instance id is available or the instance no longer exists.
  const selectedProviderInstanceId =
    providerStatuses.find((status) => status.instanceId === selectedProviderByThreadId)
      ?.instanceId ?? null;
  const activeProviderInstanceId =
    selectedProviderInstanceId ??
    activeThread?.session?.providerInstanceId ??
    activeThread?.modelSelection.instanceId ??
    activeProject?.defaultModelSelection?.instanceId ??
    null;
  const activeProviderStatus = useMemo(() => {
    if (activeProviderInstanceId) {
      return (
        providerStatuses.find((status) => status.instanceId === activeProviderInstanceId) ?? null
      );
    }
    const defaultInstanceId = defaultInstanceIdForDriver(selectedProvider);
    return providerStatuses.find((status) => status.instanceId === defaultInstanceId) ?? null;
  }, [activeProviderInstanceId, providerStatuses, selectedProvider]);
  const activeProjectCwd = activeProject?.workspaceRoot ?? null;
  const timelineProviderSkillDiscovery = useProviderSkillDiscovery({
    environmentId,
    instanceId: activeProviderStatus?.instanceId ?? null,
    cwd: activeProjectCwd,
  });
  const timelineProviderSkills = resolveTimelineProviderSkills({
    statusSkills: activeProviderStatus?.skills ?? EMPTY_PROVIDER_SKILLS,
    discoveredSkills: timelineProviderSkillDiscovery.skills,
    discoveryReady:
      activeProviderStatus !== null &&
      activeProjectCwd !== null &&
      !timelineProviderSkillDiscovery.isPending &&
      timelineProviderSkillDiscovery.error === null,
  });
  const activeThreadWorktreePath = activeThread?.worktreePath ?? null;
  // Every thread runs at its checkout root: the worktree, or the project itself.
  const activeWorkspaceRoot = activeThreadWorktreePath ?? activeProjectCwd ?? undefined;
  const activeTerminalLaunchContext =
    terminalUiLaunchContext?.threadId === activeThreadId ? terminalUiLaunchContext : null;
  // Default true while loading to avoid toolbar flicker.
  const isGitRepo = gitStatusQuery.data?.isRepo ?? true;
  const terminalShortcutLabelOptions = useMemo(
    () => ({
      context: {
        terminalFocus: true,
        terminalOpen: Boolean(terminalUiState.terminalOpen),
      },
    }),
    [terminalUiState.terminalOpen],
  );
  const splitTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.split", terminalShortcutLabelOptions),
    [keybindings, terminalShortcutLabelOptions],
  );
  const splitTerminalVerticalShortcutLabel = useMemo(
    () =>
      shortcutLabelForCommand(keybindings, "terminal.splitVertical", terminalShortcutLabelOptions),
    [keybindings, terminalShortcutLabelOptions],
  );
  const newTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.new", terminalShortcutLabelOptions),
    [keybindings, terminalShortcutLabelOptions],
  );
  const closeTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.close", terminalShortcutLabelOptions),
    [keybindings, terminalShortcutLabelOptions],
  );
  const onToggleDiff = useCallback(() => {
    if (!isServerThread) {
      return;
    }
    if (!diffOpen) {
      onDiffPanelOpen?.();
    }
    if (activeThreadRef) {
      useRightPanelStore.getState().toggle(activeThreadRef, "diff");
    }
  }, [activeThreadRef, diffOpen, isServerThread, onDiffPanelOpen]);

  const envLocked = Boolean(
    activeThread &&
    (activeThread.messages.length > 0 ||
      (activeThread.session !== null && activeThread.session.status !== "stopped")),
  );

  // Handle environment change for draft threads.  When the user picks a
  // different environment we update the draft context to point at the physical
  // project in that environment while keeping the same logical project.
  const onEnvironmentChange = useCallback(
    (nextEnvironmentId: EnvironmentId) => {
      if (envLocked || !draftId) return;
      const target = logicalProjectEnvironments.find(
        (env) => env.environmentId === nextEnvironmentId,
      );
      if (!target) return;
      setDraftThreadContext(draftId, {
        projectRef: scopeProjectRef(target.environmentId, target.projectId),
      });
    },
    [draftId, envLocked, logicalProjectEnvironments, setDraftThreadContext],
  );

  // Automatic placement, when this client has been told to make the choice. It runs once per
  // draft and defers to any machine the user picks afterwards.
  // A project with a Machine Pool is placed only within it (ADR-0149).
  const projectMachinePool = useMachinePool(
    activeProject?.environmentId ?? null,
    activeProject?.id ?? null,
  );
  const balancedCandidates = useMemo(
    () =>
      !activeProject
        ? logicalProjectEnvironments
        : // Until the pool is read there is nowhere to place, so nothing is placed.
          projectMachinePool === null
          ? []
          : restrictToMachinePool(
              logicalProjectEnvironments,
              projectMachinePool,
              activeProject.environmentId,
            ),
    [activeProject, logicalProjectEnvironments, projectMachinePool],
  );
  useAutoBalancedDraftEnvironment({
    draftId,
    locked: envLocked,
    environmentId,
    candidates: balancedCandidates,
    onChoose: onEnvironmentChange,
  });

  const activeTerminalGroup =
    terminalUiState.terminalGroups.find(
      (group) => group.id === terminalUiState.activeTerminalGroupId,
    ) ??
    terminalUiState.terminalGroups.find((group) =>
      group.terminalIds.includes(terminalUiState.activeTerminalId),
    ) ??
    null;
  const hasReachedSplitLimit =
    (activeTerminalGroup?.terminalIds.length ?? 0) >= MAX_TERMINALS_PER_GROUP;
  const serverThreadId = serverThread?.id ?? null;
  const serverThreadEnvironmentId = serverThread?.environmentId ?? null;
  const applyThreadError = useCallback(
    (targetThreadId: ThreadId | null, nextError: string | null) => {
      if (!targetThreadId) return;
      if (
        targetThreadId === routeThreadRef.threadId &&
        serverThreadEnvironmentId === routeThreadRef.environmentId &&
        serverThreadId === targetThreadId
      ) {
        setLocalServerErrorsByThreadKey((existing) => {
          if ((existing[routeThreadKey] ?? null) === nextError) {
            return existing;
          }
          return {
            ...existing,
            [routeThreadKey]: nextError,
          };
        });
        return;
      }
      const localDraftErrorKey = draftId ?? targetThreadId;
      setLocalDraftErrorsByDraftId((existing) => {
        if ((existing[localDraftErrorKey] ?? null) === nextError) {
          return existing;
        }
        return {
          ...existing,
          [localDraftErrorKey]: nextError,
        };
      });
    },
    [draftId, routeThreadKey, routeThreadRef, serverThreadEnvironmentId, serverThreadId],
  );

  const setThreadError = useCallback(
    (targetThreadId: ThreadId | null, error: string | null) => {
      applyThreadError(targetThreadId, sanitizeThreadErrorMessage(error));
    },
    [applyThreadError],
  );

  /**
   * Report a failed command on the thread's error banner.
   *
   * Command rejections carry their reason in a receipt rather than in
   * `Error.message`, so every failed dispatch has to be described through the
   * shared helper; reading `.message` directly reports the empty string and
   * leaves the banner closed.
   */
  const setThreadFailure = useCallback(
    (
      targetThreadId: ThreadId | null,
      failure: Parameters<typeof squashAtomCommandFailure>[0],
      fallback: string,
    ) => {
      applyThreadError(
        targetThreadId,
        threadCommandFailureMessage(squashAtomCommandFailure(failure), fallback),
      );
    },
    [applyThreadError],
  );

  // Readiness is read from the Provider Instance catalog the Environment
  // already pushed, the same list the model picker renders. It deliberately
  // does not refresh the instance first: a refresh probes the provider, which
  // for Codex spawns an app-server and reads its account and catalogs, and no
  // send should wait on that. The Environment owns provider refreshes and
  // streams new snapshots into this catalog on its own, so the client does not
  // have to ask before it can trust what it already has.
  const checkProviderReadinessForPrompt = useCallback(
    (selectedProviderInstanceId: ProviderInstanceId, targetThreadId: ThreadId): boolean => {
      const readiness = derivePromptProviderReadiness({
        selectedProviderInstanceId,
        providers: providerStatuses,
        session: readThreadDetail(scopeThreadRef(environmentId, targetThreadId))?.session ?? null,
      });
      if (readiness.selectedProviderReady) return true;

      // The catalog said no, which may only mean it is behind: a `codex login`
      // run in the user's own terminal is not observed, and the Environment's
      // own re-probe is on a several-minute tick. Ask for a fresh snapshot so
      // the refusal is self-healing rather than sticky — the chat view has no
      // other way to re-probe, and the alternative is sending the user to
      // Settings to press Refresh. It is deliberately not awaited: this send is
      // already refused, and the answer arrives on the config stream in time
      // for the next one, so the happy path still costs nothing.
      void refreshPromptProvider({
        environmentId,
        input: { instanceId: selectedProviderInstanceId },
      });
      setThreadError(
        targetThreadId,
        readiness.noReadyProvider
          ? "The selected provider is not ready."
          : "The selected provider is not ready. Choose another ready provider before sending.",
      );
      return false;
    },
    [environmentId, providerStatuses, refreshPromptProvider, setThreadError],
  );

  const focusComposer = useCallback(() => {
    composerRef.current?.focusAtEnd();
  }, [composerRef]);
  useEffect(() => subscribeSnapShotComposerFocus(focusComposer), [focusComposer]);
  const scheduleComposerFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      focusComposer();
    });
  }, [focusComposer]);
  const addTerminalContextToDraft = useCallback(
    (selection: TerminalContextSelection) => {
      composerRef.current?.addTerminalContext(selection);
    },
    [composerRef],
  );
  const setTerminalOpen = useCallback(
    (open: boolean) => {
      if (!activeThreadRef) return;
      storeSetTerminalOpen(activeThreadRef, open);
    },
    [activeThreadRef, storeSetTerminalOpen],
  );
  const toggleTerminalVisibility = useCallback(() => {
    if (!activeThreadRef) return;
    const nextOpen = !terminalUiState.terminalOpen;
    if (nextOpen && terminalUiState.terminalIds.length === 0) {
      if (!activeThreadId || !activeProject) {
        return;
      }
      const cwdForOpen = gitCwd ?? activeProject.workspaceRoot;
      if (!cwdForOpen) {
        return;
      }
      const terminalId = nextTerminalId([...activeKnownTerminalIds, ...panelTerminalIds]);
      storeEnsureTerminal(activeThreadRef, terminalId, { open: true });
      void openTerminal({
        environmentId,
        input: {
          threadId: activeThreadId,
          terminalId,
          cwd: cwdForOpen,
          ...(activeThreadWorktreePath != null ? { worktreePath: activeThreadWorktreePath } : {}),
          env: projectScriptRuntimeEnv({
            project: { cwd: activeProject.workspaceRoot },
            worktreePath: activeThreadWorktreePath,
          }),
        },
      });
      return;
    }
    setTerminalOpen(nextOpen);
  }, [
    activeKnownTerminalIds,
    activeProject,
    activeThreadId,
    activeThreadRef,
    activeThreadWorktreePath,
    environmentId,
    gitCwd,
    openTerminal,
    panelTerminalIds,
    setTerminalOpen,
    storeEnsureTerminal,
    terminalUiState.terminalIds.length,
    terminalUiState.terminalOpen,
  ]);
  const splitTerminal = useCallback(
    (direction: "horizontal" | "vertical" = "horizontal") => {
      if (!activeThreadRef || hasReachedSplitLimit || !activeThreadId || !activeProject) {
        return;
      }
      const cwdForOpen = gitCwd ?? activeProject.workspaceRoot;
      if (!cwdForOpen) {
        return;
      }
      const terminalId = nextTerminalId(activeKnownTerminalIds);
      if (direction === "vertical") {
        storeSplitTerminalVertical(activeThreadRef, terminalId);
      } else {
        storeSplitTerminal(activeThreadRef, terminalId);
      }
      setTerminalFocusRequestId((value) => value + 1);
      void openTerminal({
        environmentId,
        input: {
          threadId: activeThreadId,
          terminalId,
          cwd: cwdForOpen,
          ...(activeThreadWorktreePath != null ? { worktreePath: activeThreadWorktreePath } : {}),
          env: projectScriptRuntimeEnv({
            project: { cwd: activeProject.workspaceRoot },
            worktreePath: activeThreadWorktreePath,
          }),
        },
      });
    },
    [
      activeProject,
      activeKnownTerminalIds,
      activeThreadId,
      activeThreadRef,
      openTerminal,
      activeThreadWorktreePath,
      environmentId,
      gitCwd,
      hasReachedSplitLimit,
      storeSplitTerminal,
      storeSplitTerminalVertical,
    ],
  );
  const createNewTerminal = useCallback(() => {
    if (!activeThreadRef || !activeThreadId || !activeProject) {
      return;
    }
    const cwdForOpen = gitCwd ?? activeProject.workspaceRoot;
    if (!cwdForOpen) {
      return;
    }
    const terminalId = nextTerminalId(activeKnownTerminalIds);
    storeNewTerminal(activeThreadRef, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
    void openTerminal({
      environmentId,
      input: {
        threadId: activeThreadId,
        terminalId,
        cwd: cwdForOpen,
        ...(activeThreadWorktreePath != null ? { worktreePath: activeThreadWorktreePath } : {}),
        env: projectScriptRuntimeEnv({
          project: { cwd: activeProject.workspaceRoot },
          worktreePath: activeThreadWorktreePath,
        }),
      },
    });
  }, [
    activeProject,
    activeKnownTerminalIds,
    activeThreadId,
    activeThreadRef,
    openTerminal,
    activeThreadWorktreePath,
    environmentId,
    gitCwd,
    storeNewTerminal,
  ]);
  const closeTerminal = useCallback(
    (terminalId: string) => {
      if (!activeThreadId || !activeThreadRef) return;
      void requestScopedTerminalProcessClose({
        threadRef: activeThreadRef,
        terminalId,
        close: closeTerminalMutation,
        write: writeTerminal,
      });
      storeCloseTerminal(activeThreadRef, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [
      activeThreadId,
      activeThreadRef,
      closeTerminalMutation,
      environmentId,
      storeCloseTerminal,
      writeTerminal,
    ],
  );
  const runProjectScript = useCallback(
    async (
      script: ProjectScript,
      options?: {
        cwd?: string;
        env?: Record<string, string>;
        worktreePath?: string | null;
        preferNewTerminal?: boolean;
        rememberAsLastInvoked?: boolean;
      },
    ) => {
      if (!activeThreadId || !activeProject || !activeThread) return;
      if (options?.rememberAsLastInvoked !== false) {
        setLastInvokedScriptByProjectId((current) => {
          if (current[activeProject.id] === script.id) return current;
          return { ...current, [activeProject.id]: script.id };
        });
      }
      const targetCwd = options?.cwd ?? gitCwd ?? activeProject.workspaceRoot;
      const baseTerminalId =
        terminalUiState.activeTerminalId || activeKnownTerminalIds[0] || DEFAULT_THREAD_TERMINAL_ID;
      const isBaseTerminalBusy = runningTerminalIds.includes(baseTerminalId);
      const wantsNewTerminal = Boolean(options?.preferNewTerminal) || isBaseTerminalBusy;
      const shouldCreateNewTerminal = wantsNewTerminal;
      const targetWorktreePath = options?.worktreePath ?? activeThread.worktreePath ?? null;

      setTerminalUiLaunchContext({
        threadId: activeThreadId,
        cwd: targetCwd,
        worktreePath: targetWorktreePath,
      });
      setTerminalOpen(true);
      if (!activeThreadRef) {
        return;
      }
      setTerminalFocusRequestId((value) => value + 1);

      const runtimeEnv = projectScriptRuntimeEnv({
        project: {
          cwd: activeProject.workspaceRoot,
        },
        worktreePath: targetWorktreePath,
        ...(options?.env ? { extraEnv: options.env } : {}),
      });
      const targetTerminalId = shouldCreateNewTerminal
        ? nextTerminalId(activeKnownTerminalIds)
        : baseTerminalId;
      const openTerminalInput: TerminalOpenInput = shouldCreateNewTerminal
        ? {
            threadId: activeThreadId,
            terminalId: targetTerminalId,
            cwd: targetCwd,
            ...(targetWorktreePath !== null ? { worktreePath: targetWorktreePath } : {}),
            env: runtimeEnv,
            cols: SCRIPT_TERMINAL_COLS,
            rows: SCRIPT_TERMINAL_ROWS,
          }
        : {
            threadId: activeThreadId,
            terminalId: targetTerminalId,
            cwd: targetCwd,
            ...(targetWorktreePath !== null ? { worktreePath: targetWorktreePath } : {}),
            env: runtimeEnv,
          };

      if (shouldCreateNewTerminal) {
        storeNewTerminal(activeThreadRef, targetTerminalId);
      } else {
        storeSetActiveTerminal(activeThreadRef, targetTerminalId);
      }

      const openResult = await openTerminal({ environmentId, input: openTerminalInput });
      if (openResult._tag === "Failure") {
        if (!isAtomCommandInterrupted(openResult)) {
          setThreadFailure(activeThreadId, openResult, `Failed to run script "${script.name}".`);
        }
        return;
      }

      const writeResult = await writeTerminal({
        environmentId,
        input: {
          threadId: activeThreadId,
          terminalId: targetTerminalId,
          data: `${script.command}\r`,
        },
      });
      if (writeResult._tag === "Failure" && !isAtomCommandInterrupted(writeResult)) {
        setThreadFailure(activeThreadId, writeResult, `Failed to run script "${script.name}".`);
      }
    },
    [
      activeProject,
      activeThread,
      activeThreadId,
      activeThreadRef,
      gitCwd,
      setTerminalOpen,
      setThreadFailure,
      storeNewTerminal,
      storeSetActiveTerminal,
      setLastInvokedScriptByProjectId,
      environmentId,
      openTerminal,
      activeKnownTerminalIds,
      runningTerminalIds,
      terminalUiState.activeTerminalId,
      writeTerminal,
    ],
  );

  const persistProjectScripts = useCallback(
    async (input: {
      projectId: ProjectId;
      expectedRevision: string;
      projectCwd: string;
      previousScripts: ReadonlyArray<ProjectScript>;
      nextScripts: ReadonlyArray<ProjectScript>;
      keybinding?: string | null;
      keybindingCommand: KeybindingCommand;
    }): Promise<AtomCommandResult<void, unknown>> => {
      const updateResult = mapAtomCommandResult(
        await updateProject({
          environmentId,
          input: {
            projectId: input.projectId,
            expectedRevision: input.expectedRevision,
            scripts: input.nextScripts,
          },
        }),
        () => undefined,
      );
      if (updateResult._tag === "Failure") {
        return updateResult;
      }

      const keybindingRule = decodeProjectScriptKeybindingRule({
        keybinding: input.keybinding,
        command: input.keybindingCommand,
      });

      if (isElectron && keybindingRule) {
        return mapAtomCommandResult(
          await upsertKeybinding({
            environmentId,
            input: keybindingRule,
          }),
          () => undefined,
        );
      }
      return updateResult;
    },
    [environmentId, updateProject, upsertKeybinding],
  );
  const saveProjectScript = useCallback(
    async (input: NewProjectScriptInput): Promise<AtomCommandResult<void, unknown>> => {
      if (!activeProject) {
        return AsyncResult.success(undefined);
      }
      const nextId = nextProjectScriptId(
        input.name,
        activeProject.scripts.map((script) => script.id),
      );
      const nextScript: ProjectScript = {
        id: nextId,
        name: input.name,
        command: input.command,
        icon: input.icon,
        runOnWorktreeCreate: input.runOnWorktreeCreate,
      };
      const nextScripts = input.runOnWorktreeCreate
        ? [
            ...activeProject.scripts.map((script) =>
              script.runOnWorktreeCreate ? { ...script, runOnWorktreeCreate: false } : script,
            ),
            nextScript,
          ]
        : [...activeProject.scripts, nextScript];

      return persistProjectScripts({
        projectId: activeProject.id,
        expectedRevision: activeProject.updatedAt,
        projectCwd: activeProject.workspaceRoot,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: input.keybinding,
        keybindingCommand: commandForProjectScript(nextId),
      });
    },
    [activeProject, persistProjectScripts],
  );
  const updateProjectScript = useCallback(
    async (
      scriptId: string,
      input: NewProjectScriptInput,
    ): Promise<AtomCommandResult<void, unknown>> => {
      if (!activeProject) {
        return AsyncResult.success(undefined);
      }
      const existingScript = activeProject.scripts.find((script) => script.id === scriptId);
      if (!existingScript) {
        return AsyncResult.failure(Cause.fail(new Error("Script not found.")));
      }

      const updatedScript: ProjectScript = {
        ...existingScript,
        name: input.name,
        command: input.command,
        icon: input.icon,
        runOnWorktreeCreate: input.runOnWorktreeCreate,
      };
      const nextScripts = activeProject.scripts.map((script) =>
        script.id === scriptId
          ? updatedScript
          : input.runOnWorktreeCreate
            ? { ...script, runOnWorktreeCreate: false }
            : script,
      );

      return persistProjectScripts({
        projectId: activeProject.id,
        expectedRevision: activeProject.updatedAt,
        projectCwd: activeProject.workspaceRoot,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: input.keybinding,
        keybindingCommand: commandForProjectScript(scriptId),
      });
    },
    [activeProject, persistProjectScripts],
  );
  const deleteProjectScript = useCallback(
    async (scriptId: string): Promise<AtomCommandResult<void, unknown>> => {
      if (!activeProject) {
        return AsyncResult.success(undefined);
      }
      const nextScripts = activeProject.scripts.filter((script) => script.id !== scriptId);

      const deletedName = activeProject.scripts.find((s) => s.id === scriptId)?.name;

      const result = await persistProjectScripts({
        projectId: activeProject.id,
        expectedRevision: activeProject.updatedAt,
        projectCwd: activeProject.workspaceRoot,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: null,
        keybindingCommand: commandForProjectScript(scriptId),
      });
      if (result._tag === "Success") {
        toastManager.add({
          type: "success",
          title: `Deleted action "${deletedName ?? "Unknown"}"`,
        });
      } else if (!isAtomCommandInterrupted(result)) {
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Could not delete action",
            description: commandFailureMessage(
              squashAtomCommandFailure(result),
              "An unexpected error occurred.",
            ),
          }),
        );
      }
      return result;
    },
    [activeProject, persistProjectScripts],
  );

  const handleComposerActionChange = useCallback(
    (action: ThreadActionSelection | null) => {
      setComposerDraftAction(composerDraftTarget, action);
    },
    [composerDraftTarget, setComposerDraftAction],
  );

  const handleRuntimeModeChange = useCallback(
    (mode: RuntimeMode) => {
      if (mode === runtimeMode) return;
      setComposerDraftRuntimeMode(composerDraftTarget, mode);
      if (isLocalDraftThread) {
        setDraftThreadContext(composerDraftTarget, { runtimeMode: mode });
      }
      scheduleComposerFocus();
    },
    [
      isLocalDraftThread,
      runtimeMode,
      scheduleComposerFocus,
      composerDraftTarget,
      setComposerDraftRuntimeMode,
      setDraftThreadContext,
    ],
  );

  const handleInteractionModeChange = useCallback(
    (mode: ProviderInteractionMode) => {
      if (mode === interactionMode) return;
      setComposerDraftInteractionMode(composerDraftTarget, mode);
      if (isLocalDraftThread) {
        setDraftThreadContext(composerDraftTarget, { interactionMode: mode });
      }
      scheduleComposerFocus();
    },
    [
      interactionMode,
      isLocalDraftThread,
      scheduleComposerFocus,
      composerDraftTarget,
      setComposerDraftInteractionMode,
      setDraftThreadContext,
    ],
  );
  const toggleInteractionMode = useCallback(() => {
    handleInteractionModeChange(interactionMode === "plan" ? "default" : "plan");
  }, [handleInteractionModeChange, interactionMode]);
  // Profiles belong to the Environment's browser (ADR-0137).
  const browserProfileOptions = useEnvironmentSettings(
    activeThreadRef?.environmentId ?? null,
    selectBrowserProfileOptions,
  );
  // The surface picker is the screen the user presses Browser from, so the
  // panel's chunk is fetched while they are still choosing rather than being
  // charged to the press itself.
  useEffect(() => {
    if (!rightPanelOpen || activeRightPanelSurface !== null) return;
    void importPreviewPanel().catch(() => undefined);
  }, [activeRightPanelSurface, rightPanelOpen]);
  const createBrowserSurface = useCallback(
    (profileId?: string) => {
      if (!activeThreadRef) return;
      void addBrowserSurface({
        threadRef: activeThreadRef,
        openPreview,
        ...(profileId === undefined ? {} : { profileId }),
      });
    },
    [activeThreadRef, openPreview],
  );
  /**
   * The default-profile entry point takes no argument. Handing
   * `createBrowserSurface` straight to an `onClick` passed React's event as
   * `profileId`, which reached the wire schema and killed the open.
   */
  const createDefaultBrowserSurface = useCallback(() => {
    createBrowserSurface();
  }, [createBrowserSurface]);
  const addDiffSurface = useCallback(() => {
    if (!activeThreadRef || !isServerThread || !isGitRepo) return;
    useRightPanelStore.getState().open(activeThreadRef, "diff");
    onDiffPanelOpen?.();
  }, [activeThreadRef, isGitRepo, isServerThread, onDiffPanelOpen]);
  const addFilesSurface = useCallback(() => {
    if (!activeThreadRef || !activeProject) return;
    useRightPanelStore.getState().open(activeThreadRef, "files");
  }, [activeProject, activeThreadRef]);
  const addPullRequestsSurface = useCallback(() => {
    if (!activeThreadRef) return;
    useRightPanelStore.getState().open(activeThreadRef, "pull-requests");
  }, [activeThreadRef]);
  const addContributionsSurface = useCallback(() => {
    if (!activeThreadRef) return;
    useRightPanelStore.getState().open(activeThreadRef, "contributions");
  }, [activeThreadRef]);
  const openThreadPullRequest = useCallback(
    (link: ThreadPullRequestLink) => {
      if (!activeThreadRef || !activeProject) return;
      // The Project lends its checkout and credentials; the link's own host routes the read, so
      // a pull request outside this Project's repository is still read where it lives.
      useRightPanelStore.getState().openPullRequest(activeThreadRef, {
        projectId: activeProject.id,
        host: link.host,
        repository: link.repository,
        number: link.number,
        url: link.url,
      });
    },
    [activeProject, activeThreadRef],
  );
  const experimentSurfaceActions = useMemo(
    () =>
      !activeThreadRef
        ? []
        : [
            {
              label: "Experiments",
              description: "Open this checkout's Experiment Workspace.",
              onClick: () => useRightPanelStore.getState().openExperiment(activeThreadRef, null),
            },
          ],
    [activeThreadRef],
  );
  const openFileSurface = useCallback(
    (relativePath: string) => {
      if (!activeThreadRef || !activeProject) return;
      useRightPanelStore.getState().openFile(activeThreadRef, relativePath);
    },
    [activeProject, activeThreadRef],
  );
  const togglePreviewPanel = useCallback(() => {
    if (!activeThreadRef || !browserAvailable) return;
    if (previewPanelOpen) {
      useRightPanelStore.getState().close(activeThreadRef);
      return;
    }
    const activeTabId = activePreviewState.activeTabId;
    if (activeTabId) {
      useRightPanelStore.getState().openBrowser(activeThreadRef, activeTabId);
    } else {
      createBrowserSurface();
    }
  }, [
    activePreviewState.activeTabId,
    activeThreadRef,
    browserAvailable,
    createBrowserSurface,
    previewPanelOpen,
  ]);
  const closePreviewPanel = useCallback(() => {
    if (activeThreadRef) {
      setMaximizedRightPanelThreadKey(null);
      useRightPanelStore.getState().close(activeThreadRef);
    }
  }, [activeThreadRef]);
  const addTerminalSurface = useCallback(() => {
    if (!activeThreadRef || !activeThreadId || !activeProject) return;
    const cwd = gitCwd ?? activeProject.workspaceRoot;
    const terminalId = nextTerminalId([...activeKnownTerminalIds, ...panelTerminalIds]);
    useRightPanelStore.getState().openTerminal(activeThreadRef, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
    void openTerminal({
      environmentId: activeThreadRef.environmentId,
      input: {
        threadId: activeThreadId,
        terminalId,
        cwd,
        ...(activeThreadWorktreePath != null ? { worktreePath: activeThreadWorktreePath } : {}),
        env: projectScriptRuntimeEnv({
          project: { cwd: activeProject.workspaceRoot },
          worktreePath: activeThreadWorktreePath,
        }),
      },
    });
  }, [
    activeKnownTerminalIds,
    activeProject,
    activeThreadId,
    activeThreadRef,
    activeThreadWorktreePath,
    gitCwd,
    openTerminal,
    panelTerminalIds,
  ]);
  const splitPanelTerminal = useCallback(
    (direction: "horizontal" | "vertical" = "horizontal") => {
      if (
        !activeThreadRef ||
        !activeThreadId ||
        !activeProject ||
        activeRightPanelSurface?.kind !== "terminal" ||
        activeRightPanelSurface.terminalIds.length >= MAX_TERMINALS_PER_GROUP
      ) {
        return;
      }
      const terminalId = nextTerminalId([...activeKnownTerminalIds, ...panelTerminalIds]);
      const cwd = gitCwd ?? activeProject.workspaceRoot;
      useRightPanelStore
        .getState()
        .splitTerminal(activeThreadRef, activeRightPanelSurface.id, terminalId, direction);
      setTerminalFocusRequestId((value) => value + 1);
      void openTerminal({
        environmentId: activeThreadRef.environmentId,
        input: {
          threadId: activeThreadId,
          terminalId,
          cwd,
          ...(activeThreadWorktreePath != null ? { worktreePath: activeThreadWorktreePath } : {}),
          env: projectScriptRuntimeEnv({
            project: { cwd: activeProject.workspaceRoot },
            worktreePath: activeThreadWorktreePath,
          }),
        },
      });
    },
    [
      activeKnownTerminalIds,
      activeProject,
      activeRightPanelSurface,
      activeThreadId,
      activeThreadRef,
      activeThreadWorktreePath,
      gitCwd,
      openTerminal,
      panelTerminalIds,
    ],
  );
  const splitPanelTerminalVertical = useCallback(() => {
    splitPanelTerminal("vertical");
  }, [splitPanelTerminal]);
  const activatePanelTerminal = useCallback(
    (terminalId: string) => {
      if (!activeThreadRef || activeRightPanelSurface?.kind !== "terminal") return;
      useRightPanelStore
        .getState()
        .activateTerminal(activeThreadRef, activeRightPanelSurface.id, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeRightPanelSurface, activeThreadRef],
  );
  const closePanelTerminal = useCallback(
    (terminalId: string) => {
      if (!activeThreadRef || activeRightPanelSurface?.kind !== "terminal") return;
      void requestScopedTerminalProcessClose({
        threadRef: activeThreadRef,
        terminalId,
        close: closeTerminalMutation,
        write: writeTerminal,
      });
      storeCloseTerminal(activeThreadRef, terminalId);
      useRightPanelStore
        .getState()
        .closeTerminal(activeThreadRef, activeRightPanelSurface.id, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [
      activeRightPanelSurface,
      activeThreadRef,
      closeTerminalMutation,
      storeCloseTerminal,
      writeTerminal,
    ],
  );
  const activateRightPanelSurface = useCallback(
    (surface: RightPanelSurface) => {
      if (!activeThreadRef) return;
      useRightPanelStore.getState().activateSurface(activeThreadRef, surface.id);
      if (surface.kind === "preview" && surface.resourceId) {
        setActivePreviewTab(activeThreadRef, surface.resourceId);
      }
      if (surface.kind === "terminal") {
        setTerminalFocusRequestId((value) => value + 1);
      }
      if (surface.kind === "diff" && !diffOpen) {
        onDiffPanelOpen?.();
      }
    },
    [activeThreadRef, diffOpen, onDiffPanelOpen],
  );
  const toggleRightPanel = useCallback(() => {
    if (!activeThreadRef) return;
    if (rightPanelOpen) {
      closePreviewPanel();
      return;
    }
    useRightPanelStore.getState().toggleVisibility(activeThreadRef);
  }, [activeThreadRef, closePreviewPanel, rightPanelOpen]);
  const toggleRightPanelMaximized = useCallback(() => {
    if (!canMaximizeRightPanel) return;
    setMaximizedRightPanelThreadKey((threadKey) =>
      threadKey === routeThreadKey ? null : routeThreadKey,
    );
  }, [canMaximizeRightPanel, routeThreadKey]);
  const cleanupRightPanelSurfaces = useCallback(
    (surfaces: readonly RightPanelSurface[]) => {
      if (!activeThreadRef) return;
      for (const surface of surfaces) {
        if (surface.kind === "preview" && surface.resourceId) {
          void closePreviewSession({
            closePreview,
            snapshot: activePreviewState.sessions[surface.resourceId] ?? null,
            tabId: surface.resourceId,
            threadRef: activeThreadRef,
          });
        }
        if (surface.kind === "terminal") {
          for (const terminalId of surface.terminalIds) {
            storeCloseTerminal(activeThreadRef, terminalId);
            void requestScopedTerminalProcessClose({
              threadRef: activeThreadRef,
              terminalId,
              close: closeTerminalMutation,
              write: writeTerminal,
            });
          }
        }
      }
    },
    [
      activeThreadRef,
      activePreviewState.sessions,
      closePreview,
      closeTerminalMutation,
      storeCloseTerminal,
      writeTerminal,
    ],
  );
  const syncActivePreviewSurface = useCallback(() => {
    if (!activeThreadRef) return;
    const nextActiveSurface = selectActiveRightPanelSurface(
      useRightPanelStore.getState().byThreadKey,
      activeThreadRef,
    );
    if (nextActiveSurface?.kind === "preview" && nextActiveSurface.resourceId) {
      setActivePreviewTab(activeThreadRef, nextActiveSurface.resourceId);
    }
  }, [activeThreadRef]);
  const dispatchRightPanelClose = useCallback(
    (command: RightPanelCloseCommand) => {
      if (!activeThreadRef) return;
      const store = useRightPanelStore.getState();
      const current = selectThreadRightPanelState(store.byThreadKey, activeThreadRef);
      const transition = transitionWorkspaceSurfaceState(current, command);
      cleanupRightPanelSurfaces(transition.removedDescriptors);
      store.dispatchSurfaceCommand(activeThreadRef, command);
      syncActivePreviewSurface();
    },
    [activeThreadRef, cleanupRightPanelSurfaces, syncActivePreviewSurface],
  );
  const closeRightPanelSurface = useCallback(
    (surface: RightPanelSurface) => {
      dispatchRightPanelClose({ type: "close", surfaceId: surface.id });
    },
    [dispatchRightPanelClose],
  );
  const closeOtherRightPanelSurfaces = useCallback(
    (surface: RightPanelSurface) => {
      dispatchRightPanelClose({ type: "close-others", surfaceId: surface.id });
    },
    [dispatchRightPanelClose],
  );
  const closeRightPanelSurfacesToRight = useCallback(
    (surface: RightPanelSurface) => {
      dispatchRightPanelClose({ type: "close-to-right", surfaceId: surface.id });
    },
    [dispatchRightPanelClose],
  );
  const closeAllRightPanelSurfaces = useCallback(() => {
    dispatchRightPanelClose({ type: "close-all" });
  }, [dispatchRightPanelClose]);
  const copyRightPanelFilePath = useCallback((relativePath: string) => {
    if (typeof window === "undefined" || !navigator.clipboard?.writeText) {
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Failed to copy path",
          description: "Clipboard API unavailable.",
        }),
      );
      return;
    }

    void navigator.clipboard.writeText(relativePath).then(
      () => {
        toastManager.add({
          type: "success",
          title: "Path copied",
          description: relativePath,
        });
      },
      (error) => {
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to copy path",
            description: error instanceof Error ? error.message : "An error occurred.",
          }),
        );
      },
    );
  }, []);
  useEffect(
    () =>
      subscribePreviewAction((action) => {
        if (action === "toggle-panel") togglePreviewPanel();
      }),
    [togglePreviewPanel],
  );
  const persistThreadSettingsForNextTurn = useCallback(
    async (input: {
      threadId: ThreadId;
      expectedRevision: string | number;
      createdAt: string;
      modelSelection?: ModelSelection;
      runtimeMode: RuntimeMode;
      interactionMode: ProviderInteractionMode;
      forceInteractionMode?: boolean;
    }): Promise<AtomCommandResult<void, unknown>> => {
      if (!serverThread) {
        return AsyncResult.success(undefined);
      }

      let result: AtomCommandResult<void, unknown> = AsyncResult.success(undefined);
      let expectedRevision = input.expectedRevision;
      if (
        input.modelSelection !== undefined &&
        (input.modelSelection.model !== serverThread.modelSelection.model ||
          input.modelSelection.instanceId !== serverThread.modelSelection.instanceId ||
          JSON.stringify(input.modelSelection.options ?? null) !==
            JSON.stringify(serverThread.modelSelection.options ?? null))
      ) {
        const commandResult = await updateThreadMetadata({
          environmentId,
          input: {
            threadId: input.threadId,
            expectedRevision,
            modelSelection: input.modelSelection,
          },
        });
        if (commandResult._tag === "Failure") {
          return mapAtomCommandResult(commandResult, () => undefined);
        }
        expectedRevision = commandResult.value.result.currentRevision;
      }

      if (input.runtimeMode !== serverThread.runtimeMode) {
        const commandResult = await setThreadRuntimeMode({
          environmentId,
          input: {
            threadId: input.threadId,
            expectedRevision,
            runtimeMode: input.runtimeMode,
            createdAt: input.createdAt,
          },
        });
        if (commandResult._tag === "Failure") {
          return mapAtomCommandResult(commandResult, () => undefined);
        }
        expectedRevision = commandResult.value.result.currentRevision;
      }

      if (input.forceInteractionMode || input.interactionMode !== serverThread.interactionMode) {
        result = mapAtomCommandResult(
          await setThreadInteractionMode({
            environmentId,
            input: {
              threadId: input.threadId,
              expectedRevision,
              interactionMode: input.interactionMode,
              createdAt: input.createdAt,
            },
          }),
          () => undefined,
        );
      }
      return result;
    },
    [
      environmentId,
      serverThread,
      setThreadInteractionMode,
      setThreadRuntimeMode,
      updateThreadMetadata,
    ],
  );

  const {
    followsLiveEdge: timelineFollowsLiveEdge,
    showScrollToBottom,
    scrollToEnd,
    onAnchorReady: onTimelineAnchorReady,
    onAnchorSizeChanged: onTimelineAnchorSizeChanged,
    onIsAtEndChange,
    cancelLiveFollowForUserNavigation: cancelTimelineLiveFollowForUserNavigation,
    keepPositionForOutgoingTurn,
  } = useTimelineScrollAnchoring({
    listRef: legendListRef,
    composerInset: composerTimelineInset,
    activeThreadId: activeThread?.id ?? null,
    timelineEntries,
  });

  // The composer stages attachments and carries their bytes on the turn, so
  // there is no upload capability to discover and nothing to wait on.
  const attachmentUploadsCapabilityKnown = true;
  const supportsAttachmentUploads = true;
  // Attaching to a provider question is a capability AFAR does not model.
  const supportsQuestionAttachments = false;
  const maxFileAttachmentBytes = PROVIDER_SEND_TURN_MAX_FILE_BYTES;

  /**
   * What ArrowUp recall reads. Narrowed and held still so a provider delta
   * does not re-render the composer: the list only moves when a prompt is sent.
   */
  const composerPromptHistoryMessages = useStableValue(
    useMemo(
      () =>
        timelineMessages.flatMap((message) =>
          message.role === "user"
            ? [{ id: message.id, role: message.role, text: message.text ?? "" }]
            : [],
        ),
      [timelineMessages],
    ),
  );
  const requestContextCompaction = useStableCallback(() => {
    void compactionControl.request();
  });
  const openProviderSetup = useStableCallback(() => {
    // Open the Provider Instance this conversation is bound to, so setup
    // lands on the provider the user was just blocked by rather than on
    // whichever instance the Environment lists first.
    void navigate({
      to: "/settings/providers",
      ...(conversationProviderInstanceId
        ? { search: { instanceId: conversationProviderInstanceId } }
        : {}),
    });
  });
  const getTimelineScrollableNode = useCallback(
    () => (legendListRef.current?.getScrollableNode() as HTMLElement | null) ?? null,
    [],
  );
  // The pill shows exactly when the timeline is away from its end.
  const isTimelineAtLogicalEnd = useCallback(() => !showScrollToBottom, [showScrollToBottom]);
  // Where a resting composer relocates its controls. AFAR has no context strip
  // to host them, so the composer keeps them in an inline row while resting.
  const restingControlsHost = null;
  const restingControlsHaveLeadingContext = false;
  const onRestingControlsVisibilityChange = useCallback(() => {}, []);
  // Page scrolling is the timeline's own; the composer only reports the keys.
  const onPageScrollKeyDown = useCallback(() => {}, []);
  const onPageScrollKeyUp = useCallback(() => {}, []);
  const onPageScrollRelease = useCallback(() => {}, []);

  useEffect(() => {
    setPullRequestDialogState(null);
  }, [activeThread?.id]);

  useEffect(() => {
    if (!activeThread?.id || terminalUiState.terminalOpen) return;
    const frame = window.requestAnimationFrame(() => {
      focusComposer();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeThread?.id, focusComposer, terminalUiState.terminalOpen]);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (activeThread.messages.length === 0) {
      return;
    }
    const serverIds = new Set(activeThread.messages.map((message) => message.id));
    const removedMessages = optimisticUserMessages.filter((message) => serverIds.has(message.id));
    if (removedMessages.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setOptimisticUserMessages((existing) => {
        const next = existing.filter((message) => !serverIds.has(message.id));
        return next.length === existing.length ? existing : next;
      });
    }, 0);
    for (const removedMessage of removedMessages) {
      const previewUrls = collectUserMessageBlobPreviewUrls(removedMessage);
      if (previewUrls.length > 0) {
        handoffAttachmentPreviews(removedMessage.id, previewUrls);
        continue;
      }
      revokeUserMessagePreviewUrls(removedMessage);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [activeThread?.id, activeThread?.messages, handoffAttachmentPreviews, optimisticUserMessages]);

  useEffect(() => {
    setOptimisticUserMessages((existing) => {
      for (const message of existing) {
        revokeUserMessagePreviewUrls(message);
      }
      return [];
    });
    resetLocalDispatch();
    setExpandedImage(null);
    setExpandedFile(null);
  }, [draftId, resetLocalDispatch, threadId]);

  // The Project Study stays on offer while the project awaits it, and on the
  // ingest thread until a study starts, skipped or not.
  const openProjectStudy = useProjectStudyDialogStore((store) => store.open);
  const studyOffer =
    activeProject != null &&
    offersProjectStudy(activeProject) &&
    (projectAwaitsStudy(activeProject) ||
      activeThread?.title === PROJECT_REFERENCES_INGEST_THREAD_TITLE)
      ? {
          project: scopeProjectRef(activeProject.environmentId, activeProject.id),
          awaiting: projectAwaitsStudy(activeProject),
        }
      : null;

  const closeExpandedImage = useCallback(() => {
    setExpandedImage(null);
  }, []);
  const closeExpandedFile = useCallback(() => {
    setExpandedFile(null);
  }, []);

  const activeWorktreePath = activeThread?.worktreePath ?? null;
  const derivedEnvMode: DraftThreadEnvMode = resolveEffectiveEnvMode({
    activeWorktreePath,
    hasServerThread: isServerThread,
    draftThreadEnvMode: isLocalDraftThread ? draftThread?.envMode : undefined,
  });
  const canOverrideServerThreadEnvMode = Boolean(
    isServerThread &&
    activeThread &&
    activeThread.messages.length === 0 &&
    activeThread.worktreePath === null &&
    !envLocked,
  );
  const envMode: DraftThreadEnvMode = canOverrideServerThreadEnvMode
    ? (pendingServerThreadEnvMode ?? draftThread?.envMode ?? derivedEnvMode)
    : derivedEnvMode;
  const activeThreadBranch =
    canOverrideServerThreadEnvMode && pendingServerThreadBranch !== undefined
      ? pendingServerThreadBranch
      : (activeThread?.branch ?? null);
  const startFromOrigin = isLocalDraftThread
    ? (draftThread?.startFromOrigin ?? false)
    : canOverrideServerThreadEnvMode
      ? (pendingServerThreadStartFromOriginByThreadId[activeThread?.id ?? ""] ??
        settings.newWorktreesStartFromOrigin)
      : false;
  const sendEnvMode = resolveSendEnvMode({
    requestedEnvMode: envMode,
    isGitRepo,
  });

  // What this thread offers the Actions that require something. A provider
  // that has not reported native subagent support still offers them; the
  // Environment refuses an Action where it cannot run.
  const onPrimaryCheckout = !activeThread?.worktreePath && sendEnvMode === "local";
  const nativeSubagents =
    activeProviderStatus?.conversationCapabilities?.nativeSubagents !== "unsupported";
  const actionContext = useMemo(
    () => ({ onPrimaryCheckout, nativeSubagents }),
    [onPrimaryCheckout, nativeSubagents],
  );
  const composerCustomActionSkills = useMemo(
    () => customActionSkills(timelineProviderSkills),
    [timelineProviderSkills],
  );
  const nextActionId = useNextThreadAction({
    environmentId: activeProject?.environmentId ?? null,
    projectRoot: activeProject?.workspaceRoot ?? null,
  });
  const { alwaysIngest: alwaysIngestAttachments, enableAlwaysIngest } =
    useProjectAlwaysIngestAttachments(
      activeProject?.environmentId ?? null,
      activeProject?.id ?? null,
    );
  const verbActionEntries = useMemo(
    () =>
      listComposerActionEntries({ context: actionContext, customSkills: [] }).filter(
        (entry) => entry.group === "verb",
      ),
    [actionContext],
  );
  const emptyThreadState = useMemo(
    () =>
      deriveEmptyThreadState({
        project: activeProject ?? null,
        threads: projectThreadShells.filter((thread) => thread.id !== activeThread?.id),
      }),
    [activeProject, activeThread?.id, projectThreadShells],
  );
  const legacyNotice = legacyWorkingDirectoryNotice(activeThread?.legacyWorkingDirectory);

  useEffect(() => {
    setPendingServerThreadEnvMode(null);
    setPendingServerThreadBranch(undefined);
  }, [activeThread?.id]);

  useEffect(() => {
    if (canOverrideServerThreadEnvMode) {
      return;
    }
    setPendingServerThreadEnvMode(null);
    setPendingServerThreadBranch(undefined);
  }, [canOverrideServerThreadEnvMode]);

  useEffect(() => {
    if (!activeThreadId) {
      setTerminalUiLaunchContext(null);
      return;
    }
    setTerminalUiLaunchContext((current) => {
      if (!current) return current;
      if (current.threadId === activeThreadId) return current;
      return null;
    });
  }, [activeThreadId]);

  useEffect(() => {
    if (!activeThreadId || !activeProjectCwd) {
      return;
    }
    setTerminalUiLaunchContext((current) => {
      if (!current || current.threadId !== activeThreadId) {
        return current;
      }
      const settledCwd =
        activeWorkspaceRoot ??
        projectScriptCwd({
          project: { cwd: activeProjectCwd },
          worktreePath: activeThreadWorktreePath,
        });
      if (
        settledCwd === current.cwd &&
        (activeThreadWorktreePath ?? null) === current.worktreePath
      ) {
        return null;
      }
      return current;
    });
  }, [activeProjectCwd, activeThreadId, activeThreadWorktreePath, activeWorkspaceRoot]);

  useEffect(() => {
    if (terminalUiState.terminalOpen) {
      return;
    }
    setTerminalUiLaunchContext((current) =>
      current?.threadId === activeThreadId ? null : current,
    );
  }, [activeThreadId, terminalUiState.terminalOpen]);

  useEffect(() => {
    if (!activeThreadKey) return;
    const previous = terminalUiOpenByThreadRef.current[activeThreadKey] ?? false;
    const current = Boolean(terminalUiState.terminalOpen);

    if (!previous && current) {
      terminalUiOpenByThreadRef.current[activeThreadKey] = current;
      setTerminalFocusRequestId((value) => value + 1);
      return;
    } else if (previous && !current) {
      terminalUiOpenByThreadRef.current[activeThreadKey] = current;
      const frame = window.requestAnimationFrame(() => {
        focusComposer();
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    }

    terminalUiOpenByThreadRef.current[activeThreadKey] = current;
  }, [activeThreadKey, focusComposer, terminalUiState.terminalOpen]);

  /**
   * Puts queued messages back into the composer, e.g. after Stop. Assigned
   * below, once the composer refs it writes to exist.
   */
  const restoreQueuedMessagesRef = useRef<(messages: ReadonlyArray<QueuedComposerMessage>) => void>(
    () => {},
  );

  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if (!activeThreadId || isCommandPaletteOpen()) {
        return;
      }
      // noVNC listens on its canvas after this capture listener. Leave its keys
      // alone so the Cloud Display receives them instead of the chat composer.
      if (eventPathContainsSelector(event, "[data-cloud-display-input]")) return;
      const terminalFocusOwner = getTerminalFocusOwner();
      if (event.defaultPrevented && terminalFocusOwner === null) {
        return;
      }
      const shortcutContext = {
        terminalFocus: terminalFocusOwner !== null,
        terminalOpen: Boolean(terminalUiState.terminalOpen),
        modelPickerOpen: composerRef.current?.isModelPickerOpen() ?? false,
      };

      if (
        !shortcutContext.terminalFocus &&
        !shortcutContext.modelPickerOpen &&
        shouldTypeToFocusComposer(event)
      ) {
        if (composerRef.current?.insertTextAtEnd(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      const command = resolveShortcutCommand(event, keybindings, {
        context: shortcutContext,
      });
      if (!command) return;

      if (command === "terminal.toggle") {
        event.preventDefault();
        event.stopPropagation();
        toggleTerminalVisibility();
        return;
      }

      if (command === "rightPanel.toggle") {
        event.preventDefault();
        event.stopPropagation();
        toggleRightPanel();
        return;
      }

      if (isThreadTerminalCommand(command)) {
        event.preventDefault();
        event.stopPropagation();
        const placement = placeThreadTerminalCommand({
          command,
          focusOwner: terminalFocusOwner,
          drawerOpen: Boolean(terminalUiState.terminalOpen),
          rightPanelSurfaceKind: activeRightPanelSurface?.kind,
        });
        if (!placement) return;
        if (placement.openDrawerFirst) {
          setTerminalOpen(true);
        }
        if (placement.target === "right-panel") {
          if (command === "terminal.split") splitPanelTerminal();
          else if (command === "terminal.splitVertical") splitPanelTerminal("vertical");
          else if (command === "terminal.new") addTerminalSurface();
          else if (activeRightPanelSurface?.kind === "terminal") {
            closePanelTerminal(activeRightPanelSurface.activeTerminalId);
          }
          return;
        }
        if (command === "terminal.split") splitTerminal();
        else if (command === "terminal.splitVertical") splitTerminal("vertical");
        else if (command === "terminal.new") createNewTerminal();
        else closeTerminal(terminalUiState.activeTerminalId);
        return;
      }

      if (command === "thread.steerQueuedMessage") {
        // Read at call time so the handler identity stays stable and this
        // listener is not torn down and rebuilt on every queue change. The
        // shortcut sends whichever prompt is next, wherever it is held.
        const next = queuedMessagesRef.current[0];
        if (!next) return;
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) queuedMessageActionsRef.current.steer(next.id);
        return;
      }

      if (command === "diff.toggle") {
        event.preventDefault();
        event.stopPropagation();
        onToggleDiff();
        return;
      }

      if (command === "pullRequest.copyReference") {
        // Nothing linked is nothing to copy: the binding is left to whatever else wants it
        // rather than answering with an empty clipboard.
        const reference =
          resolveThreadCurrentPullRequestLink(activeThread?.pullRequests ?? [])?.url ?? null;
        if (reference === null) return;
        event.preventDefault();
        event.stopPropagation();
        void navigator.clipboard?.writeText(reference);
        return;
      }

      if (command === "modelPicker.toggle") {
        event.preventDefault();
        event.stopPropagation();
        composerRef.current?.toggleModelPicker();
        return;
      }

      const scriptId = projectScriptIdFromCommand(command);
      if (!scriptId || !activeProject) return;
      const script = activeProject.scripts.find((entry) => entry.id === scriptId);
      if (!script) return;
      event.preventDefault();
      event.stopPropagation();
      void runProjectScript(script);
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [
    activeProject,
    activeRightPanelSurface,
    addTerminalSurface,
    terminalUiState.terminalOpen,
    terminalUiState.activeTerminalId,
    activeThreadId,
    closeTerminal,
    closePanelTerminal,
    createNewTerminal,
    setTerminalOpen,
    runProjectScript,
    splitTerminal,
    splitPanelTerminal,
    keybindings,
    onToggleDiff,
    toggleRightPanel,
    toggleTerminalVisibility,
    composerRef,
  ]);

  const onForkAssistantMessage = useCallback(
    async (assistantMessageId: MessageId, throughTurnId: TurnId) => {
      if (!activeThread || !canForkNativeHistory || isForkingThread) return;
      const targetThreadId = newThreadId();
      setIsForkingThread(true);
      setThreadError(activeThread.id, null);
      const result = await forkThread({
        environmentId,
        input: {
          commandId: newCommandId(),
          sourceThreadId: activeThread.id,
          targetThreadId,
          expectedRevision: activeThread.updatedAt,
          assistantMessageId,
          throughTurnId,
        },
      });
      setIsForkingThread(false);
      if (result._tag === "Failure") {
        if (!isAtomCommandInterrupted(result)) {
          setThreadFailure(activeThread.id, result, "Failed to fork this thread.");
        }
        return;
      }
      await navigateToForkedThread({
        threadRef: scopeThreadRef(activeThread.environmentId, targetThreadId),
        waitForThread: waitForStartedServerThread,
        navigate: async (threadRef) => {
          await navigate({
            to: "/$environmentId/$threadId",
            params: buildThreadRouteParams(threadRef),
          });
        },
      });
    },
    [
      activeThread,
      canForkNativeHistory,
      environmentId,
      forkThread,
      isForkingThread,
      navigate,
      setThreadError,
      setThreadFailure,
    ],
  );

  const [pendingRevert, setPendingRevert] = useState<{
    readonly turnCount: number;
    readonly messageId: MessageId;
    readonly routeThreadKey: string;
  } | null>(null);

  if (pendingRevert && pendingRevert.routeThreadKey !== routeThreadKey) {
    setPendingRevert(null);
  }

  /**
   * Rewind this thread to the end of `turnCount` turns.
   *
   * Called first without `restoreFiles` to raise the confirmation, then again
   * with the user's answer. On success the rewound prompt goes back into the
   * composer; nothing is re-executed, so sending it again is the user's call.
   */
  const onRevertToTurnCount = useCallback(
    async (turnCount: number, messageId: MessageId, restoreFiles?: boolean): Promise<void> => {
      if (!activeThread || !canRevertConversation || isRevertingCheckpoint) return;
      const message = activeThread.messages.find((entry) => entry.id === messageId);
      if (!message || message.role !== "user") return;

      if (
        activeThread.session?.status !== "ready" ||
        phase === "running" ||
        isSendBusy ||
        isConnecting
      ) {
        setThreadError(activeThread.id, "Wait for the thread to be idle before reverting.");
        return;
      }

      if (restoreFiles === undefined) {
        setPendingRevert({ turnCount, messageId, routeThreadKey });
        return;
      }

      setIsRevertingCheckpoint(true);
      setThreadError(activeThread.id, null);
      try {
        // Rescue the bytes first: after the revert lands these attachments are
        // pruned and their asset URLs resolve to nothing.
        const attachmentPlan = planRevertedMessageAttachments(message.attachments);
        const connection = readPreparedConnection(environmentId);
        const restoredImages =
          connection === null
            ? []
            : await fetchRevertedImageAttachments({
                images: attachmentPlan.images,
                makeId: () => randomHex(16),
                ownerIdentity: routeThreadKey,
                resolveUrl: async (attachmentId) => {
                  const asset = await createRevertAssetUrl({
                    environmentId,
                    input: {
                      resource: {
                        _tag: "attachment",
                        threadId: activeThread.id,
                        attachmentId,
                      },
                    },
                  });
                  return asset._tag === "Failure"
                    ? null
                    : resolveAssetUrl(connection.httpBaseUrl, asset.value.relativeUrl);
                },
              });

        // The receipt only says the command was accepted. The rewind runs in
        // the CheckpointReactor, which refuses by appending an activity rather
        // than by failing the command, so the prompt must not return to the
        // composer until the read model shows the rewind actually landed.
        const landed = waitForRevertToLand({
          threadRef: routeThreadRef,
          messageId,
          turnCount,
        });
        const result = await revertThreadCheckpoint({
          environmentId,
          input: {
            commandId: newCommandId(),
            threadId: activeThread.id,
            expectedRevision: activeThread.updatedAt,
            turnCount,
            restoreFiles,
          },
        });
        if (result._tag === "Failure") {
          if (!isAtomCommandInterrupted(result)) {
            setThreadFailure(activeThread.id, result, "Failed to revert this thread.");
          }
          return;
        }
        await landed;

        // The rewound prompt comes back as text the user can edit and send
        // again. Its attachments do not: the revert prunes their stored bytes,
        // and the composer would hold records the Environment no longer has.
        const restoredPrompt = message.text.trim();
        if (restoredPrompt.length > 0) {
          const currentPrompt = promptRef.current;
          const nextPrompt =
            currentPrompt.length > 0 ? `${currentPrompt}\n\n${restoredPrompt}` : restoredPrompt;
          setComposerDraftPrompt(composerDraftTarget, nextPrompt);
          promptRef.current = nextPrompt;
          composerRef.current?.resetCursorState({ prompt: nextPrompt, cursor: nextPrompt.length });
        }
        if (restoredImages.length > 0) {
          addComposerDraftImages(composerDraftTarget, [...restoredImages], {
            allowDuplicates: true,
          });
        }
        if (attachmentPlan.notRestored.length > 0) {
          // A file or folder attachment is a live reference to a host path, so
          // the user's own file is untouched; only the record of attaching it
          // is gone, and saying so beats silently dropping it.
          setThreadError(
            activeThread.id,
            `Re-attach ${attachmentPlan.notRestored.join(", ")} if the next prompt still needs ${
              attachmentPlan.notRestored.length === 1 ? "it" : "them"
            }.`,
          );
        }
      } catch (cause) {
        setThreadError(
          activeThread.id,
          cause instanceof Error ? cause.message : "Failed to revert this thread.",
        );
      } finally {
        setIsRevertingCheckpoint(false);
      }
    },
    [
      activeThread,
      canRevertConversation,
      composerDraftTarget,
      composerRef,
      environmentId,
      isConnecting,
      isRevertingCheckpoint,
      isSendBusy,
      phase,
      addComposerDraftImages,
      createRevertAssetUrl,
      revertThreadCheckpoint,
      routeThreadKey,
      routeThreadRef,
      setComposerDraftPrompt,
      setThreadError,
      setThreadFailure,
    ],
  );

  // Read through a ref at call time so the timeline row context keeps one
  // stable callback identity and is not invalidated on every ChatView render.
  const onRevertToTurnCountRef = useRef(onRevertToTurnCount);
  onRevertToTurnCountRef.current = onRevertToTurnCount;
  const onRevertTimelineTurn = useCallback((turnCount: number, messageId: MessageId) => {
    void onRevertToTurnCountRef.current(turnCount, messageId);
  }, []);

  const sendOutgoingPrompt = async (
    e?: { preventDefault: () => void },
    outgoingOverrides?: {
      readonly prompt?: string;
      readonly pathAttachments?: ReadonlyArray<ComposerPathAttachment>;
    },
    submissionIntent: ComposerSubmissionIntent = "foreground",
    /** Set when this send is replaying a queued message rather than the composer draft. */
    queuedMessage?: QueuedComposerMessage,
  ) => {
    e?.preventDefault();
    // The Action chip this send carries, read at call time: an Action that
    // runs at once sends in the same tick its chip was set. A released queued
    // message carries the chip it was queued with.
    const actionForSend: ThreadActionSelection | null =
      queuedMessage !== undefined
        ? (queuedMessage.action ?? null)
        : (useComposerDraftStore.getState().getComposerDraft(composerDraftTarget)?.action ?? null);
    const designOperationForSend = designOperationOf(actionForSend);
    // A released queued message is its own send, complete with the Workspace
    // Action it was queued as. Nothing about it is rebuilt from the composer,
    // which was cleared when it was queued and may hold a different send by
    // now — including the pending-operation refs, which belong to whatever the
    // user is composing rather than to the message coming back out of the queue.
    const queuedOperation = queuedMessage?.operation;
    let designOperation =
      queuedMessage === undefined
        ? pendingDesignOperationRef.current
        : queuedOperation !== undefined && isDesignOperationSubmission(queuedOperation)
          ? queuedOperation
          : undefined;
    // A Design send *is* its operation. The composer's text is the brief — or
    // the display name for Create, or the new name for Rename — and the mention
    // chip is the target. `buildDesignComposerSubmission` is the one place that
    // turns the two into a submission, so what the send button allows and what
    // the payload carries cannot disagree.
    // Delete is host-applied and never becomes a queued submission, so it leaves
    // here before the send path that arranges a prompt for a provider turn.
    if (
      queuedMessage === undefined &&
      designOperation === undefined &&
      designOperationForSend === "delete-design-system"
    ) {
      await submitDesignDeleteTargets(
        composerRef.current?.getDesignCollection().deleteTargets ?? [],
      );
      return;
    }
    if (
      queuedMessage === undefined &&
      designOperation === undefined &&
      designOperationForSend !== null &&
      designOperationForSend !== "delete-design-system"
    ) {
      const built = buildDesignComposerSubmission({
        mode: designOperationForSend,
        text: promptRef.current,
        collection: composerRef.current?.getDesignCollection() ?? EMPTY_DESIGN_COMPOSER_COLLECTION,
      });
      // A submission the contract would reject is not a send; the composer's
      // own `canSend` has already refused it, and this is the same rule reached
      // through a keybinding or a queued dispatch.
      if (built === null) return;
      await dispatchDesignOperation(built, submissionIntent);
      return;
    }
    const blocked = shouldBlockOutgoingSend({
      isSendBusy,
      isConnecting,
      sendInFlight: sendInFlightRef.current,
      environmentUnavailable: activeEnvironmentUnavailable,
      phase,
    });
    const hasActivePendingUserInput = Boolean(activePendingProgress);
    // The composer is only asked for its context once a send could actually
    // reach it, exactly as the guards below used to order it.
    const composerSendCtx =
      activeThread && !blocked && !hasActivePendingUserInput
        ? composerRef.current?.getSendContext()
        : undefined;
    const sendCtx = !composerSendCtx
      ? composerSendCtx
      : // A replayed queued message sends its own snapshot; the composer was
        // cleared when it was queued and may hold a newer message by now.
        queuedMessage
        ? queuedMessageSendContext(composerSendCtx, queuedMessage)
        : composerSendCtx;
    const sendCandidate = sendCtx
      ? (() => {
          const source = resolveOutgoingSendComposerSource({
            ...(outgoingOverrides?.prompt !== undefined
              ? { overridePrompt: outgoingOverrides.prompt }
              : {}),
            ...(outgoingOverrides?.pathAttachments !== undefined
              ? { overridePathAttachments: outgoingOverrides.pathAttachments }
              : {}),
            // The composer's own prompt, not the live ref: a text-free Brain
            // operation reports none, and reading the ref behind it would put
            // the hidden text back into the turn.
            composerPrompt: sendCtx.prompt,
            composerPathAttachments: sendCtx.files.flatMap((file) => {
              const attachment = composerFileAsPathAttachment(file);
              return attachment === null ? [] : [attachment];
            }),
          });
          const state = deriveComposerSendState({
            prompt: source.prompt,
            imageCount: sendCtx.images.length + source.pathAttachments.length,
            terminalContexts: sendCtx.terminalContexts,
            elementContextCount: sendCtx.previewAnnotations.length + sendCtx.reviewComments.length,
          });
          return {
            ctx: sendCtx,
            // A released queued message sends its own snapshot, so it must not
            // consume the live composer on its way out: by now that composer
            // holds whatever the user typed after queuing, and clearing it —
            // draft, chips and all — would throw away a message they have not
            // sent yet.
            source: queuedMessage ? { ...source, consumeComposerDraft: false } : source,
            // The composer offers a Brain or Design send on what the mode
            // collected rather than on typed text, so the disposition has to
            // judge it by the same rule or it would refuse what the button
            // offered.
            state: {
              ...state,
              // A send carrying a Design Operation is admitted by that
              // operation and nothing else. It exists only because
              // `buildDesignComposerSubmission` already accepted the composer's
              // collection, or because it came off the queue having been
              // accepted once. Re-judging it on typed text is what silently
              // refused an Add Example whose target and evidence were both
              // present: a URL is neither composer text nor an attachment, so
              // there was nothing here left to count.
              //
              // One rule for a fresh send and a release alike: the release
              // carries the chips it was queued with, so neither has to be
              // judged by a composer the other one emptied.
              hasSendableContent:
                designOperation !== undefined ||
                brainAwareComposerCanSend({
                  action: brainActionOf(actionForSend),
                  collection: queuedMessage?.brainCollection ?? sendCtx.brainCollection,
                  hasComposerContent: state.hasSendableContent,
                }),
            },
          };
        })()
      : null;
    // What the Brain rail gathered, which the send arranges its prompt from.
    // A released queued message brings its own snapshot, taken when the user
    // pressed Enter: queuing cleared the composer, and the rail may since have
    // moved.
    const brainActionForSend = brainActionOf(actionForSend);
    const brainCollection =
      queuedMessage?.brainCollection ??
      (brainActionForSend === null || sendCtx === undefined ? undefined : sendCtx.brainCollection);
    // Whether this send's text is the composer's arrangement rather than the
    // user's own words. A Brain send carries no operation — it is an ordinary
    // turn — but it still owns its prompt, so the chips clear with it and a
    // conversation command is never parsed out of it.
    const brainArrangedSend =
      brainActionForSend !== null &&
      brainCollection !== undefined &&
      brainAwareComposerCanSend({
        action: brainActionForSend,
        collection: brainCollection,
        hasComposerContent: sendCandidate?.state.hasSendableContent ?? false,
      });
    const threadQueueOperation = designOperation;

    const disposition = resolveOutgoingSendDisposition({
      hasActiveThread: Boolean(activeThread),
      blocked,
      hasActivePendingUserInput,
      composer:
        sendCandidate === null
          ? null
          : {
              trimmedPrompt: sendCandidate.state.trimmedPrompt,
              hasSendableContent: sendCandidate.state.hasSendableContent,
              expiredTerminalContextCount: sendCandidate.state.expiredTerminalContextCount,
              consumeComposerDraft: sendCandidate.source.consumeComposerDraft,
              attachedContentCount:
                sendCandidate.ctx.images.length +
                sendCandidate.source.pathAttachments.length +
                sendCandidate.state.sendableTerminalContexts.length +
                sendCandidate.ctx.previewAnnotations.length +
                sendCandidate.ctx.reviewComments.length,
            },
      showPlanFollowUpPrompt,
      hasActiveProposedPlan: Boolean(activeProposedPlan),
      hasActiveProject: Boolean(activeProject),
    });

    if (disposition.type === "ignore") {
      return;
    }
    if (disposition.type === "advance-pending-user-input") {
      onAdvanceActivePendingUserInput();
      return;
    }
    if (disposition.type === "nothing-to-send") {
      if (disposition.expiredTerminalContextCount > 0) {
        const toastCopy = buildExpiredTerminalContextToastCopy(
          disposition.expiredTerminalContextCount,
          "empty",
        );
        toastManager.add(
          stackedThreadToast({
            type: "warning",
            title: toastCopy.title,
            description: toastCopy.description,
          }),
        );
      }
      return;
    }
    if (disposition.type === "apply-interaction-mode") {
      handleInteractionModeChange(disposition.mode);
      promptRef.current = "";
      clearComposerDraftContent(composerDraftTarget);
      composerRef.current?.resetCursorState();
      return;
    }
    // The disposition already established these; the guard is what narrows them.
    if (!activeThread || !activeProject || sendCandidate === null) {
      return;
    }
    const isFirstMessage = !isServerThread || activeThread.messages.length === 0;
    const baseBranchForWorktree =
      isFirstMessage && sendEnvMode === "worktree" && !activeThread.worktreePath
        ? activeThreadBranch
        : null;
    const worktreeBranch = () => {
      const existing =
        serverThread?.worktreeBootstrap?.branch ??
        temporaryWorktreeBranches.current.get(routeThreadKey);
      if (existing) return existing;
      const branch = buildTemporaryWorktreeBranchName(randomHex);
      temporaryWorktreeBranches.current.set(routeThreadKey, branch);
      return branch;
    };
    const buildBootstrap = (
      title: string,
      modelSelection: ModelSelection,
    ): ThreadTurnStartBootstrap | undefined =>
      isLocalDraftThread || baseBranchForWorktree
        ? {
            ...(isLocalDraftThread
              ? {
                  createThread: {
                    projectId: activeProject.id,
                    title,
                    modelSelection,
                    runtimeMode,
                    interactionMode,
                    branch: activeThreadBranch,
                    worktreePath: activeThread.worktreePath,
                    createdAt: activeThread.createdAt,
                  },
                }
              : {}),
            ...(baseBranchForWorktree
              ? {
                  prepareWorktree: {
                    projectCwd: activeProject.workspaceRoot,
                    baseBranch: baseBranchForWorktree,
                    branch: worktreeBranch(),
                    location: draftThread?.worktreeLocation ?? { type: "host-store" },
                    ...(startFromOrigin ? { startFromOrigin: true } : {}),
                  },
                  runSetupScript: true,
                }
              : {}),
          }
        : undefined;
    if (conversationCommandsInFlight.current.has(routeThreadKey)) return;
    const conversationCommand =
      outgoingOverrides ||
      threadQueueOperation ||
      brainArrangedSend ||
      (isServerThread ? goalControl.driver : sendCandidate.ctx.selectedProvider) === "claudeAgent"
        ? null
        : parseConversationCommand(sendCandidate.source.prompt);
    if (conversationCommand) {
      conversationCommandsInFlight.current.add(routeThreadKey);
      try {
        if (
          (isServerThread
            ? (goalControl.driver ?? compactionControl.driver)
            : sendCandidate.ctx.selectedProvider) !== "codex"
        )
          throw new Error("These commands are available for Codex conversations.");
        if (
          sendCandidate.ctx.images.length +
            sendCandidate.source.pathAttachments.length +
            sendCandidate.ctx.terminalContexts.length +
            sendCandidate.ctx.previewAnnotations.length +
            sendCandidate.ctx.reviewComments.length >
          0
        )
          throw new Error(
            "Remove attachments before running /goal or /compact. These commands only use text.",
          );
        if (conversationCommand.type === "invalid") throw new Error(conversationCommand.error);
        setThreadError(activeThread.id, null);
        if (conversationCommand.type === "goal") {
          if (isLocalDraftThread) {
            const selection = sendCandidate.ctx.selectedModelSelection;
            if (
              !providerConversationOperationSupported(
                providerStatuses,
                selection.instanceId,
                "threadGoal",
              )
            )
              throw new Error("Native Goal support is unavailable for this conversation.");
            if (sendEnvMode === "worktree" && !activeThread.worktreePath && !activeThreadBranch)
              throw new Error("Select a base branch before starting a Goal in New worktree mode.");
            if (!checkProviderReadinessForPrompt(selection.instanceId, activeThread.id)) return;
            const result = await startDraftGoal({
              environmentId,
              input: {
                threadId: activeThread.id,
                expectedRevision: activeThread.updatedAt,
                action: "start",
                objective: conversationCommand.objective,
                bootstrap: buildBootstrap(truncate(conversationCommand.objective), selection),
              },
            });
            if (result._tag === "Failure") throw squashAtomCommandFailure(result);
          } else {
            await goalControl.send({ action: "start", objective: conversationCommand.objective });
          }
        } else if (!isLocalDraftThread) {
          if (!compactionControl.enabled) throw new Error(compactionControl.disabledReason);
          await compactionControl.request();
        }
        // A command can finish after the user has typed their next message.
        if (
          currentRouteThreadKey.current === routeThreadKey &&
          promptRef.current === sendCandidate.source.prompt
        ) {
          promptRef.current = "";
          setComposerDraftPrompt(composerDraftTarget, "");
          composerRef.current?.resetCursorState();
        }
      } catch (cause) {
        // `cause` is a squashed command failure thrown above, so it can be a
        // rejection whose reason lives in its receipt rather than its message.
        applyThreadError(
          activeThread.id,
          threadCommandFailureMessage(cause, "Codex command failed."),
        );
      } finally {
        conversationCommandsInFlight.current.delete(routeThreadKey);
      }
      return;
    }
    if (disposition.type === "submit-plan-follow-up") {
      if (!activeProposedPlan) {
        return;
      }
      if (
        !checkProviderReadinessForPrompt(
          sendCandidate.ctx.selectedModelSelection.instanceId,
          activeThread.id,
        )
      ) {
        return;
      }
      const followUp = buildPlanFollowUpTurnStart({
        draftText: sendCandidate.state.trimmedPrompt,
        planMarkdown: activeProposedPlan.planMarkdown,
        sourceThreadId: activeThread.id,
        sourcePlanId: activeProposedPlan.id,
      });
      promptRef.current = "";
      discardComposerDraftContent(composerDraftTarget);
      composerRef.current?.resetCursorState();
      await onSubmitPlanFollowUp({
        text: followUp.text,
        interactionMode: followUp.interactionMode,
        ...(followUp.interactionMode === "default"
          ? { sourceProposedPlan: followUp.sourceProposedPlan }
          : {}),
        designSystemMentions: [],
      });
      return;
    }

    const {
      images: composerImages,
      previewAnnotations: composerPreviewAnnotations,
      reviewComments: composerReviewComments,
      selectedModel: ctxSelectedModel,
      selectedModelSelection: ctxSelectedModelSelection,
    } = sendCandidate.ctx;
    const composerFiles = [...sendCandidate.source.pathAttachments];
    const promptForSend = sendCandidate.source.prompt;
    const consumeComposerDraft = sendCandidate.source.consumeComposerDraft;
    const {
      trimmedPrompt: trimmed,
      sendableTerminalContexts: sendableComposerTerminalContexts,
      expiredTerminalContextCount,
    } = sendCandidate.state;
    const threadIdForSend = activeThread.id;
    // What this send does about the turn that is already running: join it, or
    // wait for it. A released queued message is always joining one — that is
    // what releasing it means — and otherwise the setting decides, with the
    // alternate send doing the opposite for this one message.
    const liveTurnIdForSteer =
      activeThread.session?.status === "running" ? activeThread.session.activeTurnId : null;
    const steersLiveTurn =
      isServerThread &&
      liveTurnIdForSteer !== null &&
      (queuedMessage !== undefined ||
        resolveFollowUpAction({
          setting: followUpBehavior,
          inverted: submissionIntent === "alternate",
          canSteer: true,
        }) === "steer");

    // A follow-up typed while the turn runs is held in the client queue, which
    // releases it once the thread goes idle. The setting picks the default and
    // the alternate send does the opposite for one message. A queued message
    // being replayed is past this point already.
    if (!queuedMessage && !steersLiveTurn && phase === "running" && activeThreadKey) {
      useQueuedMessageStore.getState().enqueue(activeThreadKey, {
        prompt: promptForSend,
        images: [...composerImages],
        // The composer's own file attachments, which is what a restore puts back.
        files: [...sendCandidate.ctx.files],
        terminalContexts: [...sendableComposerTerminalContexts],
        previewAnnotations: [...composerPreviewAnnotations],
        reviewComments: [...composerReviewComments],
        // The operation moves with the message rather than staying behind in
        // the composer. A Workspace Action *is* the send, so a queued one that
        // left its identity behind would be released as bare text — and for
        // Lint and Delete, as nothing at all.
        ...(threadQueueOperation !== undefined ? { operation: threadQueueOperation } : {}),
        // The rail's mode and chips wait with the message and the send arranges
        // them on release, so the queue holds what the user provided rather
        // than what the App wrote on their behalf.
        ...(actionForSend !== null ? { action: actionForSend } : {}),
        ...(brainCollection !== undefined ? { brainCollection } : {}),
        submissionIntent,
        createdAt: new Date().toISOString(),
      });
      promptRef.current = "";
      // Attachments move with the message; the refs clear now too, so a Stop
      // before the composer's sync effect runs does not restore them twice.
      composerImagesRef.current = [];
      composerFilesRef.current = [];
      composerTerminalContextsRef.current = [];
      clearComposerDraftContent(composerDraftTarget);
      // Queuing consumes the composer exactly as sending does. The chips and
      // the pending operation are not draft content, so clearing the draft
      // leaves them behind — and the next send, queued or not, would carry the
      // operation the user has already queued a second time.
      if (threadQueueOperation !== undefined || brainArrangedSend) {
        composerRef.current?.clearBrainComposerCollection();
        composerRef.current?.replaceDesignEvidenceUrls([]);
        if (pendingDesignOperationRef.current === designOperation) {
          pendingDesignOperationRef.current = undefined;
        }
      }
      return;
    }

    // In worktree mode, require an explicit base branch so we don't silently
    // fall back to local execution when branch selection is missing.
    const shouldCreateWorktree =
      isFirstMessage && sendEnvMode === "worktree" && !activeThread.worktreePath;
    if (shouldCreateWorktree && !activeThreadBranch) {
      setThreadError(threadIdForSend, "Select a base branch before sending in New worktree mode.");
      return;
    }
    // The catalog this reads is already on the client, so the refusal costs
    // nothing and lands before any optimistic row is drawn — no send should
    // wait on a provider probe, and none should flash a message it then takes
    // back. The Environment keeps this catalog current on its own.
    if (!checkProviderReadinessForPrompt(ctxSelectedModelSelection.instanceId, threadIdForSend)) {
      return;
    }
    // Every early return above leaves a queued message in the queue for a
    // later retry. From here on a failure hands it back.
    if (queuedMessage) {
      const claimed = activeThreadKey
        ? useQueuedMessageStore.getState().remove(activeThreadKey, queuedMessage.id)
        : null;
      if (!claimed) return;
    }
    // Stop drains the queue, but this send already holds its message and is no
    // longer in it. The counter is what tells the send, on the far side of the
    // attachment and settings round trips below, that the turn it is about to
    // start is one the user has since cancelled.
    const drainGenerationAtTake = useQueuedMessageStore.getState().drainGeneration;
    const drainedSinceTake = () =>
      queuedMessage !== undefined &&
      useQueuedMessageStore.getState().drainGeneration !== drainGenerationAtTake;
    // A queued send that fails goes back to the head of the queue, held. The
    // messages behind it keep their order and wait; the composer is not
    // touched, which keeps a failure after navigation off the new thread's
    // draft. The user retries with Send now or edits with Cancel.
    const abortQueuedReplay = () => {
      if (queuedMessage && activeThreadKey) {
        useQueuedMessageStore.getState().holdAtFront(activeThreadKey, queuedMessage);
      }
    };
    sendLifecycle.begin({ preparingWorktree: Boolean(baseBranchForWorktree) });

    const composerImagesSnapshot = [...composerImages];
    const composerFilesSnapshot = [...composerFiles];
    // What the composer itself holds, which is what a failed send puts back.
    const composerDraftFilesSnapshot = [...sendCandidate.ctx.files];
    const composerTerminalContextsSnapshot = [...sendableComposerTerminalContexts];
    const composerPreviewAnnotationsSnapshot = [...composerPreviewAnnotations];
    const composerDesignSystemMentionsSnapshot = [...sendCandidate.ctx.designSystemMentions];
    const composerReviewCommentsSnapshot: ReviewCommentContext[] = [...composerReviewComments];
    const materializedTerminalPrompt = appendTerminalContextsToPrompt(
      promptForSend,
      composerTerminalContextsSnapshot,
    );
    const messageTextWithPreviewAnnotations = composerPreviewAnnotationsSnapshot.reduce(
      (text, annotation) => appendPreviewAnnotationPrompt(text, annotation),
      materializedTerminalPrompt,
    );
    const messageTextForSend = appendReviewCommentsToPrompt(
      messageTextWithPreviewAnnotations,
      composerReviewCommentsSnapshot,
    );
    const messageIdForSend = newMessageId();
    const messageCreatedAt = new Date().toISOString();
    const attachmentOnlyPrompt = resolveAttachmentOnlyPrompt({
      imageCount: composerImagesSnapshot.length,
      fileCount: composerFilesSnapshot.filter((attachment) => attachment.type === "file").length,
      folderCount: composerFilesSnapshot.filter((attachment) => attachment.type === "folder")
        .length,
    });
    const outgoingMessageText = formatOutgoingPrompt({
      text:
        messageTextForSend ||
        (composerImagesSnapshot.length + composerFilesSnapshot.length > 0
          ? attachmentOnlyPrompt
          : ""),
      action: actionForSend,
      ...(brainCollection !== undefined ? { brainCollection } : {}),
      ...(designOperation !== undefined && designOperation.action !== "delete-design-system"
        ? { designOperation }
        : {}),
    });
    // An Action's arranged prompt leads with its skill chips. Bind each chip to
    // the catalog row so the provider invokes the Skill Bundle natively; without
    // it the token is text the agent may answer around.
    const composerSkillInvocationsForDispatch = planProviderSkillDispatch({
      prompt: outgoingMessageText,
      sourcePrompt: messageTextForSend,
      skillInvocations: resolveActionSkillInvocations(timelineProviderSkills, actionForSend),
    }).dispatched;
    // An Ingest send may list any number of sources: every byte goes ahead
    // through the signed upload, and an image is a source file like any other,
    // so the turn stays small and the agent reads each one from its path.
    const ingestSend = brainActionForSend === "ingest";
    const turnAttachmentsPromise = Promise.all([
      Promise.all(
        (ingestSend ? [] : composerImagesSnapshot).map(async (image) => ({
          type: "image" as const,
          name: image.name,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          dataUrl: await readFileAsDataUrl(image.file),
          ...(image.source ? { source: image.source } : {}),
        })),
      ),
      resolveComposerAttachmentsForSend(
        ingestSend
          ? [
              ...composerImagesSnapshot.map(
                (image): ComposerPathAttachment => ({
                  id: image.id,
                  source: "browser-file",
                  type: "file",
                  name: image.name,
                  file: image.file,
                }),
              ),
              ...composerFilesSnapshot,
            ]
          : composerFilesSnapshot,
        {
          uploadAhead: ingestSend,
          // An Ingest source is a Project Reference, uploaded at any size.
          uploadFile: (file) =>
            ingestSend
              ? uploadProjectReferenceFile(environmentId, file)
              : uploadComposerAttachmentFile(environmentId, file),
        },
      ),
    ]).then(([images, files]) => [...images, ...files]);
    const optimisticAttachments = [
      ...composerImagesSnapshot.map((image) => ({
        type: "image" as const,
        id: image.id,
        name: image.name,
        mimeType: image.mimeType,
        sizeBytes: image.sizeBytes,
        previewUrl: image.previewUrl,
        ...(image.source ? { source: image.source } : {}),
      })),
      ...composerFilesSnapshot.map((attachment) =>
        attachment.type === "folder"
          ? {
              type: "folder" as const,
              id: attachment.id,
              name: attachment.name,
              sizeBytes:
                attachment.source === "browser-folder"
                  ? attachment.entries.reduce((total, entry) => total + entry.file.size, 0)
                  : 0,
              entries:
                attachment.source === "browser-folder"
                  ? attachment.entries.map((entry) => ({
                      relativePath: entry.relativePath,
                      sizeBytes: entry.file.size,
                    }))
                  : [],
            }
          : {
              type: "file" as const,
              id: attachment.id,
              name: attachment.name,
              sizeBytes: attachment.source === "browser-file" ? attachment.file.size : 0,
              ...(attachment.source === "browser-file" && attachment.file.type
                ? { mimeType: attachment.file.type }
                : {}),
            },
      ),
    ];
    keepPositionForOutgoingTurn();
    setOptimisticUserMessages((existing) => [
      ...existing,
      {
        id: messageIdForSend,
        role: "user",
        text: outgoingMessageText,
        ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
        designSystemMentions: composerDesignSystemMentionsSnapshot,
        // The operation the send carries, so a directly dispatched Workspace
        // Action draws as itself from the first frame rather than showing the
        // arranged prompt until the Environment's copy of the message lands.
        ...(threadQueueOperation !== undefined ? { operation: threadQueueOperation } : {}),
        turnId: null,
        createdAt: messageCreatedAt,
        updatedAt: messageCreatedAt,
        streaming: false,
      },
    ]);
    setThreadError(threadIdForSend, null);
    if (expiredTerminalContextCount > 0) {
      const toastCopy = buildExpiredTerminalContextToastCopy(
        expiredTerminalContextCount,
        "omitted",
      );
      toastManager.add(
        stackedThreadToast({
          type: "warning",
          title: toastCopy.title,
          description: toastCopy.description,
        }),
      );
    }
    if (consumeComposerDraft) {
      promptRef.current = "";
      clearComposerDraftContent(composerDraftTarget);
      composerRef.current?.clearBrainComposerCollection();
      composerRef.current?.resetCursorState();
      composerRef.current?.replaceDesignEvidenceUrls([]);
    }

    let firstComposerImageName: string | null = null;
    if (composerImagesSnapshot.length > 0) {
      const firstComposerImage = composerImagesSnapshot[0];
      if (firstComposerImage) {
        firstComposerImageName = firstComposerImage.name;
      }
    }
    const firstPathAttachmentName = composerFilesSnapshot[0]?.name ?? null;
    let titleSeed = trimmed;
    if (!titleSeed) {
      if (firstComposerImageName) {
        titleSeed = `Image: ${firstComposerImageName}`;
      } else if (firstPathAttachmentName) {
        titleSeed = `Attachment: ${firstPathAttachmentName}`;
      } else if (composerTerminalContextsSnapshot.length > 0) {
        titleSeed = formatTerminalContextLabel(composerTerminalContextsSnapshot[0]!);
      } else {
        titleSeed = "New thread";
      }
    }
    const title = truncate(titleSeed);
    const threadCreateModelSelection = createModelSelection(
      ctxSelectedModelSelection.instanceId,
      ctxSelectedModel || activeProject.defaultModelSelection?.model || DEFAULT_MODEL,
      ctxSelectedModelSelection.options,
    );

    let failure: AtomCommandResult<unknown, unknown> | null = null;
    let expectedThreadRevision: string | number = serverThread?.updatedAt ?? messageCreatedAt;
    // Auto-title from first message
    if (isFirstMessage && isServerThread) {
      const titleResult = await updateThreadMetadata({
        environmentId,
        input: {
          threadId: threadIdForSend,
          expectedRevision: expectedThreadRevision,
          title,
        },
      });
      if (titleResult._tag === "Failure") {
        failure = titleResult;
      } else {
        expectedThreadRevision = titleResult.value.result.currentRevision;
      }
    }

    if (failure === null && isServerThread) {
      const settingsResult = await persistThreadSettingsForNextTurn({
        threadId: threadIdForSend,
        expectedRevision: expectedThreadRevision,
        createdAt: messageCreatedAt,
        ...(ctxSelectedModel ? { modelSelection: ctxSelectedModelSelection } : {}),
        runtimeMode,
        interactionMode,
      });
      if (settingsResult?._tag === "Failure") {
        failure = settingsResult;
      }
    }

    const turnAttachmentsResult = await settlePromise(() => turnAttachmentsPromise);
    if (failure === null && turnAttachmentsResult._tag === "Failure") {
      failure = turnAttachmentsResult;
    }

    // Taking back the bubble a send drew before it failed. Both failure paths
    // need it: the send did not become a turn, so nothing of it belongs in the
    // conversation.
    const removeOptimisticUserMessage = (messageId: MessageId) =>
      setOptimisticUserMessages((existing) => {
        for (const message of existing.filter((item) => item.id === messageId)) {
          revokeUserMessagePreviewUrls(message);
        }
        const next = existing.filter((item) => item.id !== messageId);
        return next.length === existing.length ? existing : next;
      });

    // Stop landed while this send was materialising its attachments. The drain
    // could not reach the message — the send was already holding it — so the
    // send stands it down itself, and the composer, not a new turn, is where it
    // goes.
    if (drainedSinceTake() && queuedMessage) {
      removeOptimisticUserMessage(messageIdForSend);
      restoreQueuedMessagesToComposer([queuedMessage]);
      sendLifecycle.finish({
        turnStarted: false,
        dispatchedTo: threadIdForSend,
        activeThreadId: activeThread.id,
        phase,
      });
      return;
    }

    let turnStartSucceeded = false;
    if (failure === null && turnAttachmentsResult._tag === "Success") {
      const bootstrap = buildBootstrap(title, threadCreateModelSelection);
      // Worktree preparation is over; the stand-in stops saying otherwise.
      sendLifecycle.begin({ preparingWorktree: false });
      const turnStartInput = {
        threadId: threadIdForSend,
        message: {
          messageId: messageIdForSend,
          role: "user" as const,
          text: outgoingMessageText,
          attachments: turnAttachmentsResult.value,
          designSystemMentions: [],
          ...(composerSkillInvocationsForDispatch.length > 0
            ? { skillInvocations: composerSkillInvocationsForDispatch }
            : {}),
          // What the user picked, so the transcript and the Next Action read it back.
          ...(actionForSend !== null ? { action: actionForSend } : {}),
        },
        modelSelection: ctxSelectedModelSelection,
        titleSeed: title,
        runtimeMode,
        interactionMode,
        ...(designOperation !== undefined ? { designOperation } : {}),
        ...(bootstrap ? { bootstrap } : {}),
        createdAt: messageCreatedAt,
      };
      const startResult = await startThreadTurn({ environmentId, input: turnStartInput });
      if (startResult._tag === "Failure") {
        failure = startResult;
      } else {
        turnStartSucceeded = true;
        if (pendingDesignOperationRef.current === designOperation) {
          pendingDesignOperationRef.current = undefined;
        }
      }
    }

    if (failure !== null) {
      // A replayed queued message goes back to the head of its queue, held for
      // Send now, instead of through the draft compensation below: the user
      // may have moved on and the composer is not necessarily theirs any more.
      // Its bubble still has to go: leaving it drew a message that was never
      // sent beside the queued entry it is still waiting as, which reads as the
      // same send twice.
      if (queuedMessage) {
        removeOptimisticUserMessage(messageIdForSend);
        abortQueuedReplay();
        // Returning without this left the thread claimed: the next due message
        // saw a send still in flight and the queue never moved again.
        sendLifecycle.finish({
          turnStarted: false,
          dispatchedTo: threadIdForSend,
          activeThreadId: activeThread.id,
          phase,
        });
        return;
      }
      const failedDraft = useComposerDraftStore.getState().getComposerDraft(composerDraftTarget);
      compensateFailedSend(
        {
          failure,
          messageId: messageIdForSend,
          consumedComposerDraft: consumeComposerDraft,
          // A send that brought its own attachments took them from somewhere
          // other than the draft, so their absence is not what tells a stale
          // draft from a new one.
          ignorePathAttachments: outgoingOverrides?.pathAttachments !== undefined,
          occupancy: {
            promptLength: promptRef.current.length,
            imageCount: composerImagesRef.current.length,
            pathAttachmentCount: composerFilesRef.current.length,
            terminalContextCount: composerTerminalContextsRef.current.length,
            elementContextCount: 0,
            previewAnnotationCount: failedDraft?.previewAnnotations.length ?? 0,
            reviewCommentCount: failedDraft?.reviewComments.length ?? 0,
            designSystemMentionCount: 0,
          },
        },
        {
          optimistic: { removeUserMessage: removeOptimisticUserMessage },
          restoreDraft: () =>
            restoreComposerDraftAfterFailedSend(
              {
                prompt: promptForSend,
                images: composerImagesSnapshot,
                files: composerDraftFilesSnapshot,
                terminalContexts: composerTerminalContextsSnapshot,
                previewAnnotations: composerPreviewAnnotationsSnapshot,
                reviewComments: composerReviewCommentsSnapshot,
                designOperation,
              },
              {
                writeLiveComposer: (draft) => {
                  promptRef.current = draft.prompt;
                  composerImagesRef.current = draft.images;
                  composerFilesRef.current = draft.files;
                  composerTerminalContextsRef.current = draft.terminalContexts;
                },
                // The draft is one thing to the send; the store keeps it as
                // slices, and that is the store's business rather than the
                // restore's.
                writeDraft: (draft) => {
                  setComposerDraftPrompt(composerDraftTarget, draft.prompt);
                  addComposerDraftImages(composerDraftTarget, draft.images);
                  addComposerDraftFiles(composerDraftTarget, draft.files);
                  setComposerDraftTerminalContexts(composerDraftTarget, draft.terminalContexts);
                  setComposerDraftPreviewAnnotations(composerDraftTarget, draft.previewAnnotations);
                  setComposerDraftReviewComments(composerDraftTarget, draft.reviewComments);
                  // The send consumed its chip with the draft; a failed send puts it back.
                  setComposerDraftAction(composerDraftTarget, actionForSend);
                },
                resetCursorState: (state) => composerRef.current?.resetCursorState(state),
                rememberPendingOperations: ({ design }) => {
                  // An absent one stays absent: writing undefined would clear
                  // an operation the thread is still carrying.
                  if (design !== undefined) {
                    pendingDesignOperationRef.current = design;
                  }
                },
                cloneImageForRetry: cloneComposerImageForRetry,
              },
            ),
          reportThreadError: (message) => setThreadError(threadIdForSend, message),
        },
      );
    }
    sendLifecycle.finish({
      turnStarted: turnStartSucceeded,
      dispatchedTo: threadIdForSend,
      activeThreadId: activeThread.id,
      phase,
    });
  };

  // Bringing a Codex session up is dominated by the Agent Provider starting its
  // MCP servers, and the provider will not begin a turn until they have all
  // reported ready — measured at eleven to sixteen seconds on a first prompt.
  // Focus is the earliest honest signal that a prompt is coming, so the wait is
  // started here and overlaps the typing instead of following the send.
  //
  // Guarded to once per thread per mount: focus fires on every return to the
  // composer, and a live session needs no warming. The Environment reuses an
  // existing session regardless, so a duplicate is harmless rather than a
  // restart.
  const warmedThreadKeysRef = useRef(new Set<string>());
  const warmComposerThreadSession = useStableCallback(() => {
    if (!isServerThread || !activeThreadId || !activeThreadKey) return;
    if (activeThread?.session != null && activeThread.session.status !== "stopped") return;
    // A worktree-mode thread makes its worktree as part of the first turn.
    // Warming before that exists would bind the session to the project root and
    // the turn would then have to restart it against the new worktree, which is
    // slower than never warming at all.
    if (sendEnvMode === "worktree" && !activeThread?.worktreePath) return;
    if (warmedThreadKeysRef.current.has(activeThreadKey)) return;
    warmedThreadKeysRef.current.add(activeThreadKey);
    void warmThreadSession({ environmentId, input: { threadId: activeThreadId } });
  });

  const onSend = useStableCallback(sendOutgoingPrompt);
  /**
   * The composer reports a submission intent (foreground, background or
   * alternate). Only `alternate` changes the dispatch: it inverts the
   * follow-up behaviour for this one send, so a queue-by-default user steers
   * and a steer-by-default user queues. Background is still accepted and not
   * acted on, because AFAR has no background dispatch to route it to.
   */
  const handleComposerSend = useStableCallback(
    (e?: { preventDefault: () => void }, intent: ComposerSubmissionIntent = "foreground") => {
      void sendOutgoingPrompt(e, undefined, intent);
    },
  );

  // The work the last stop press was aimed at. The press shows as pending
  // until that work is gone, so a provider taking seconds to honour it is not
  // mistaken for a press that never landed.
  const [, setStopRequestKey] = useState<string | null>(null);
  const interruptActiveTurn = async () => {
    if (!activeThread) return;
    setStopRequestKey(resolveStopRequestKey(activeThread));
    // Stop also cancels the queue: the messages return to the composer rather
    // than starting a new turn the moment the interrupted one settles.
    restoreQueuedMessagesRef.current(
      useQueuedMessageStore
        .getState()
        .drain(scopedThreadKey(scopeThreadRef(activeThread.environmentId, activeThread.id))),
    );
    const result = await interruptThreadTurn({
      environmentId,
      input: buildThreadTurnInterruptInput(activeThread),
    });
    if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
      setStopRequestKey(null);
      setThreadFailure(activeThread.id, result, "Failed to interrupt the current turn.");
    }
  };

  const onInterrupt = useStableCallback(interruptActiveTurn);

  const queuedMessages = useQueuedMessages(activeThreadKey ?? "");
  /** Read by the steer shortcut, which must not re-bind on every queue change. */
  const queuedMessagesRef = useRef<ReadonlyArray<QueuedComposerMessage>>([]);
  queuedMessagesRef.current = queuedMessages;
  // Puts queued messages back into the composer, e.g. after Stop or a failed
  // send. Prompts join with blank lines; attachments and contexts are added.
  const restoreQueuedMessagesToComposer = (messages: ReadonlyArray<QueuedComposerMessage>) => {
    if (messages.length === 0) return;
    const prompts = [promptRef.current, ...messages.map((message) => message.prompt)]
      .map((prompt) => prompt.trim())
      .filter((prompt) => prompt.length > 0);
    const nextPrompt = prompts.join("\n\n");
    promptRef.current = nextPrompt;
    setComposerDraftPrompt(composerDraftTarget, nextPrompt);
    // The draft store silently drops attachments over the per-turn cap, and
    // revokes their preview URLs on the way out. Split the overflow back into
    // the queue so nothing is lost; the user can send the first batch and the
    // rest follows as a queued message.
    const {
      images: restoredImages,
      files: restoredFiles,
      overflowImages,
      overflowFiles,
    } = planQueuedAttachmentRestore({
      images: messages.flatMap((message) => message.images),
      files: messages.flatMap((message) => message.files),
      room:
        composerAttachmentLimit(composerBrainAction) -
        composerImagesRef.current.length -
        composerFilesRef.current.length,
    });
    // The composer syncs these refs from the draft in an effect; a send before
    // that effect runs must already see the restored content.
    composerImagesRef.current = [...composerImagesRef.current, ...restoredImages];
    composerFilesRef.current = [...composerFilesRef.current, ...restoredFiles];
    const attachmentLimit = composerAttachmentLimit(composerBrainAction);
    if (restoredImages.length > 0) {
      addComposerDraftImages(composerDraftTarget, restoredImages, { attachmentLimit });
    }
    if (restoredFiles.length > 0) {
      addComposerDraftFiles(composerDraftTarget, restoredFiles, { attachmentLimit });
    }
    if ((overflowImages.length > 0 || overflowFiles.length > 0) && activeThreadKey) {
      useQueuedMessageStore.getState().enqueue(activeThreadKey, {
        prompt: "",
        images: overflowImages,
        files: overflowFiles,
        terminalContexts: [],
        previewAnnotations: [],
        reviewComments: [],
        submissionIntent: "foreground",
        // Restoration is not a send. The user decides when the overflow goes.
        holdUntilUserAction: true,
        createdAt: new Date().toISOString(),
      });
      toastManager.add(
        stackedThreadToast({
          type: "info",
          title: "Some attachments stayed queued",
          description: `A message holds at most ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} attachments. Use Send now on the queued row when you want the rest to go.`,
        }),
      );
    }
    const restoredTerminalContexts = [
      ...composerTerminalContextsRef.current,
      ...messages.flatMap((message) => message.terminalContexts),
    ];
    composerTerminalContextsRef.current = restoredTerminalContexts;
    setComposerDraftTerminalContexts(composerDraftTarget, restoredTerminalContexts);
    const draft = useComposerDraftStore.getState().getComposerDraft(composerDraftTarget);
    setComposerDraftPreviewAnnotations(composerDraftTarget, [
      ...(draft?.previewAnnotations ?? []),
      ...messages.flatMap((message) => message.previewAnnotations),
    ]);
    // A review comment is a position in a diff the user picked deliberately and
    // cannot retype. It comes back with the rest of the snapshot.
    setComposerDraftReviewComments(composerDraftTarget, [
      ...(draft?.reviewComments ?? []),
      ...messages.flatMap((message) => message.reviewComments),
    ]);
    // The editor holds its own copy of the text; without this it keeps showing
    // the emptied composer the queued message was taken from.
    composerRef.current?.resetCursorState({
      cursor: collapseExpandedComposerCursor(nextPrompt, nextPrompt.length),
      prompt: nextPrompt,
      detectTrigger: true,
    });
    // A Design send comes back as the pending operation the next send carries,
    // exactly as a failed one does. A Brain send has no operation to come back
    // as: its draft restores like any other thread's, and the rail's own mode
    // is durable, so the composer is already where the user left it.
    const restoredOperations = messages.flatMap((message) =>
      message.operation === undefined ? [] : [message.operation],
    );
    // The composer holds one Design target and one brief, so only one operation
    // can come back into it. The oldest wins and the rest keep their briefs in
    // the joined prompt above, which is the only part of them the composer has
    // room for.
    const restoredDesign = restoredOperations.find(isDesignOperationSubmission);
    if (restoredDesign !== undefined && restoredDesign.action !== "delete-design-system") {
      pendingDesignOperationRef.current = restoredDesign;
    }
  };

  // Sends the oldest queued message once the thread is idle. Only one leaves
  // per idle window: the turn it starts is what holds the rest back.
  const sendQueuedMessage = useStableCallback((message: QueuedComposerMessage) => {
    void sendOutgoingPrompt(undefined, undefined, message.submissionIntent, message);
  });
  const nextQueuedMessage = queuedMessages[0] ?? null;
  // Approvals and questions block the agent; a steer landing on top of them
  // would answer nothing and confuse the turn, so the queue holds until the
  // user resolves them.
  const queueBlockedByPendingRequest =
    activePendingApproval !== null || pendingUserInputs.length > 0;
  // onSend bails early on transient gates and leaves the message queued.
  // Re-run when any of them clear so a due message does not wait for an
  // unrelated phase change.
  const queueSendGate = activeEnvironmentUnavailable || isPreparingWorktree;
  useEffect(() => {
    if (!nextQueuedMessage || isSendBusy || queueBlockedByPendingRequest || queueSendGate) return;
    if (sendInFlightRef.current) return;
    if (!isQueuedMessageDue({ message: nextQueuedMessage, phase })) return;
    sendQueuedMessage(nextQueuedMessage);
  }, [isSendBusy, nextQueuedMessage, phase, queueBlockedByPendingRequest, queueSendGate]);

  // The row handlers are read from refs at call-time so their identity stays
  // stable and does not bust TimelineRowCtx on every ChatView render.
  const queuedMessageActionsRef = useRef({
    steer: (_id: string) => {},
    remove: (_id: string) => {},
  });
  queuedMessageActionsRef.current = {
    steer: (id) => {
      const message = queuedMessages.find((entry) => entry.id === id);
      if (!message || sendInFlightRef.current || queueBlockedByPendingRequest) return;
      void sendOutgoingPrompt(undefined, undefined, message.submissionIntent, message);
    },
    remove: (id) => {
      if (!activeThreadKey) return;
      const message = useQueuedMessageStore.getState().remove(activeThreadKey, id);
      if (message) restoreQueuedMessagesToComposer([message]);
    },
  };
  const onSteerQueuedMessage = useCallback((id: string) => {
    queuedMessageActionsRef.current.steer(id);
  }, []);
  const onRemoveQueuedMessage = useCallback((id: string) => {
    queuedMessageActionsRef.current.remove(id);
  }, []);
  // Stop also cancels the queue: the messages return to the composer instead
  // of starting a new turn the moment the interrupted one settles.
  restoreQueuedMessagesRef.current = restoreQueuedMessagesToComposer;

  // The Design send path for every operation an Agent Provider carries out.
  // Delete has left before here: it fans out to host RPCs and never arranges a
  // prompt, which is why the parameter is the agent-backed submission alone.
  const dispatchDesignOperation = async (
    operation: DesignAgentOperationSubmission,
    /**
     * The intent the user pressed, carried through the re-entry. Dropping it
     * here made the alternate send do nothing for a Design Action: every Design
     * operation took the default follow-up behaviour, so a queue-by-default
     * user could not steer one and a steer-by-default user could not queue one.
     */
    submissionIntent: ComposerSubmissionIntent = "foreground",
  ) => {
    if (!activeThread) return;
    // The send arranges the prompt from the operation, the way a Brain send
    // does. Writing it into the composer first is what repainted the composer
    // with the arranged prompt for a frame on every Design send — and what left
    // a failed send offering it back as if the user had typed it.
    pendingDesignOperationRef.current = operation;
    await onSend(undefined, undefined, submissionIntent);
  };

  const formatDesignMutationError = (error: unknown): string => {
    if (
      error !== null &&
      typeof error === "object" &&
      "_tag" in error &&
      error._tag === "DesignSystemError" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      const recovery =
        "recovery" in error && typeof error.recovery === "string" ? ` ${error.recovery}` : "";
      return `${error.message}${recovery}`;
    }
    return commandFailureMessage(error, "Design System mutation failed.");
  };

  // No Design surface hosts an error slot any more, so a host-applied mutation
  // reports its failure the same way it reports its success.
  const reportDesignMutationFailure = (title: string, error: unknown) => {
    toastManager.add({
      type: "error",
      title,
      description: formatDesignMutationError(error),
    });
  };

  /**
   * The Delete Design Operation's send. One act, one call: the server fans out
   * to one delete per target and appends the single timeline entry that reports
   * them, so the record cannot be lost by this client going away mid-delete.
   *
   * There is no toast. The entry in the conversation is the only report, because
   * a dismissible notice is the wrong home for the record of destroyed work.
   */
  const submitDesignDeleteTargets = async (targets: ReadonlyArray<DesignDeleteTarget>) => {
    if (!activeProject || !activeThread || targets.length === 0) return;
    const result = await deleteDesignTargets({
      environmentId: activeProject.environmentId,
      input: {
        commandId: newCommandId(),
        cwd: activeProject.workspaceRoot,
        threadId: activeThread.id,
        targets,
      },
    });
    if (result._tag === "Failure") {
      // A failure here means the fan-out itself never ran, so nothing was
      // deleted and there is no entry to read: this is the one case that still
      // needs telling somewhere the user is already looking.
      if (!isAtomCommandInterrupted(result)) {
        reportDesignMutationFailure("Design delete failed", squashAtomCommandFailure(result));
      }
      return;
    }
    // The picker's rows came from a listing this has just invalidated.
    appAtomRegistry.refresh(
      getProjectEntriesQueryAtom(activeProject.environmentId, activeProject.workspaceRoot),
    );
    composerRef.current?.replaceDesignDeleteTargets([]);
  };

  const onRespondToApproval = useStableCallback(
    async (requestId: ApprovalRequestId, decision: ProviderApprovalDecision) => {
      if (!activeThreadId) return;
      const request = pendingApprovals.find((candidate) => candidate.requestId === requestId);
      if (!request || !activeThread) return;

      setRespondingRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      const result = await respondToThreadApproval({
        environmentId,
        input: {
          threadId: activeThreadId,
          requestId,
          decision,
          expectedRevision: activeThread.updatedAt,
          turnId: request.turnId,
        },
      });
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        setThreadFailure(activeThreadId, result, "Failed to submit approval decision.");
      }
      setRespondingRequestIds((existing) => existing.filter((id) => id !== requestId));
      return result;
    },
  );

  const onRespondToUserInput = useCallback(
    async (requestId: ApprovalRequestId, answers: Record<string, unknown>) => {
      if (!activeThreadId || activePendingUserInput?.requestId !== requestId) return;
      if (!activeThreadId) return;
      const request = pendingUserInputs.find((candidate) => candidate.requestId === requestId);
      if (!request || !activeThread) return;

      setRespondingUserInputRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      const result = await respondToThreadUserInput({
        environmentId,
        input: {
          threadId: activeThreadId,
          requestId,
          answers,
          expectedRevision: activeThread.updatedAt,
          turnId: request.turnId,
        },
      });
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        setThreadFailure(activeThreadId, result, "Failed to submit user input.");
      }
      setRespondingUserInputRequestIds((existing) => existing.filter((id) => id !== requestId));
      return result;
    },
    [
      activePendingUserInput,
      activeThread,
      activeThreadId,
      environmentId,
      pendingUserInputs,
      respondToThreadUserInput,
      setThreadFailure,
    ],
  );

  const setActivePendingUserInputQuestionIndex = useCallback(
    (nextQuestionIndex: number) => {
      if (!activePendingUserInput) {
        return;
      }
      setPendingUserInputQuestionIndexByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: nextQuestionIndex,
      }));
    },
    [activePendingUserInput],
  );

  const onSelectActivePendingUserInputOption = useCallback(
    (questionId: string, optionLabel: string) => {
      if (!activePendingUserInput) {
        return;
      }
      setPendingUserInputAnswersByRequestId((existing) => {
        const question =
          (activePendingProgress?.activeQuestion?.id === questionId
            ? activePendingProgress.activeQuestion
            : undefined) ??
          activePendingUserInput.questions.find((entry) => entry.id === questionId);
        if (!question) {
          return existing;
        }

        return {
          ...existing,
          [activePendingUserInput.requestId]: {
            ...existing[activePendingUserInput.requestId],
            [questionId]: togglePendingUserInputOptionSelection(
              question,
              existing[activePendingUserInput.requestId]?.[questionId],
              optionLabel,
            ),
          },
        };
      });
      promptRef.current = "";
      composerRef.current?.resetCursorState({ cursor: 0 });
    },
    [activePendingProgress?.activeQuestion, activePendingUserInput, composerRef],
  );

  const onChangeActivePendingUserInputCustomAnswer = useCallback(
    (
      questionId: string,
      value: string,
      nextCursor: number,
      expandedCursor: number,
      _cursorAdjacentToMention: boolean,
    ) => {
      if (!activePendingUserInput) {
        return;
      }
      promptRef.current = value;
      setPendingUserInputAnswersByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: {
          ...existing[activePendingUserInput.requestId],
          [questionId]: setPendingUserInputCustomAnswer(
            existing[activePendingUserInput.requestId]?.[questionId],
            value,
          ),
        },
      }));
      const snapshot = composerRef.current?.readSnapshot();
      if (
        snapshot?.value !== value ||
        snapshot.cursor !== nextCursor ||
        snapshot.expandedCursor !== expandedCursor
      ) {
        composerRef.current?.focusAt(nextCursor);
      }
    },
    [activePendingUserInput, composerRef],
  );

  const onAdvanceActivePendingUserInput = useStableCallback(() => {
    if (!activePendingUserInput || !activePendingProgress) {
      return;
    }
    if (activePendingProgress.isLastQuestion) {
      if (activePendingResolvedAnswers) {
        void onRespondToUserInput(activePendingUserInput.requestId, activePendingResolvedAnswers);
      }
      return;
    }
    setActivePendingUserInputQuestionIndex(activePendingProgress.questionIndex + 1);
  });

  const onPreviousActivePendingUserInputQuestion = useCallback(() => {
    if (!activePendingProgress) {
      return;
    }
    setActivePendingUserInputQuestionIndex(Math.max(activePendingProgress.questionIndex - 1, 0));
  }, [activePendingProgress, setActivePendingUserInputQuestionIndex]);

  const onSubmitPlanFollowUp = useCallback(
    async ({
      text,
      interactionMode: nextInteractionMode,
      sourceProposedPlan,
      designSystemMentions,
    }: {
      text: string;
      interactionMode: "default" | "plan";
      sourceProposedPlan?: SourceProposedPlanReference;
      designSystemMentions: ReadonlyArray<NonNullable<ChatMessage["designSystemMentions"]>[number]>;
    }) => {
      if (
        !activeThread ||
        !isServerThread ||
        isSendBusy ||
        isConnecting ||
        sendInFlightRef.current
      ) {
        return;
      }

      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }

      const sendCtx = composerRef.current?.getSendContext();
      if (!sendCtx) {
        return;
      }
      const { selectedModelSelection: ctxSelectedModelSelection } = sendCtx;

      const threadIdForSend = activeThread.id;
      const threadRefForSend = scopeThreadRef(activeThread.environmentId, threadIdForSend);
      const messageIdForSend = newMessageId();
      const messageCreatedAt = new Date().toISOString();
      const outgoingMessageText = trimmed.trim();

      sendLifecycle.begin({ preparingWorktree: false });
      setThreadError(threadIdForSend, null);

      keepPositionForOutgoingTurn();

      setOptimisticUserMessages((existing) => [
        ...existing,
        {
          id: messageIdForSend,
          role: "user",
          text: outgoingMessageText,
          turnId: null,
          createdAt: messageCreatedAt,
          updatedAt: messageCreatedAt,
          streaming: false,
          designSystemMentions: [...designSystemMentions],
        },
      ]);

      const recoverPlanModeAfterImplementationFailure = async () => {
        const recoveryMode = resolvePlanFollowUpFailureRecovery({
          interactionMode: nextInteractionMode,
          hasSourceProposedPlan: sourceProposedPlan !== undefined,
        });
        if (recoveryMode === null) {
          return;
        }
        setComposerDraftInteractionMode(threadRefForSend, recoveryMode);
        const recoveryResult = await persistThreadSettingsForNextTurn({
          threadId: threadIdForSend,
          expectedRevision: serverThread.updatedAt,
          createdAt: new Date().toISOString(),
          modelSelection: ctxSelectedModelSelection,
          runtimeMode,
          interactionMode: recoveryMode,
          forceInteractionMode: true,
        });
        if (recoveryResult._tag === "Failure" && !isAtomCommandInterrupted(recoveryResult)) {
          console.warn(
            "Failed to restore Plan Mode after implementation start failure.",
            squashAtomCommandFailure(recoveryResult),
          );
        }
      };

      const settingsResult = await persistThreadSettingsForNextTurn({
        threadId: threadIdForSend,
        expectedRevision: serverThread.updatedAt,
        createdAt: messageCreatedAt,
        modelSelection: ctxSelectedModelSelection,
        runtimeMode,
        interactionMode: nextInteractionMode,
      });
      let failure: AtomCommandResult<unknown, unknown> | null =
        settingsResult._tag === "Failure" ? settingsResult : null;

      if (failure === null) {
        // Keep the mode toggle and plan-follow-up banner in sync immediately
        // while the same-thread implementation turn is starting.
        setComposerDraftInteractionMode(
          scopeThreadRef(activeThread.environmentId, threadIdForSend),
          nextInteractionMode,
        );

        const startResult = await startThreadTurn({
          environmentId,
          input: {
            threadId: threadIdForSend,
            message: {
              messageId: messageIdForSend,
              role: "user",
              text: outgoingMessageText,
              attachments: [],
              designSystemMentions: [...designSystemMentions],
            },
            modelSelection: ctxSelectedModelSelection,
            titleSeed: activeThread.title,
            runtimeMode,
            interactionMode: nextInteractionMode,
            ...(sourceProposedPlan ? { sourceProposedPlan } : {}),
            createdAt: messageCreatedAt,
          },
        });
        failure = startResult._tag === "Failure" ? startResult : null;
      }

      if (failure === null) {
        if (sourceProposedPlan !== undefined) {
          void observeProposedPlanImplementation({
            turnStartCommandAccepted: true,
            initialOutcome: "pending",
            waitForOutcome: () =>
              waitForProposedPlanImplementationOutcome({
                sourceThreadRef: threadRefForSend,
                sourcePlanId: sourceProposedPlan.planId,
                targetThreadRef: threadRefForSend,
                targetFailureSessionUpdatedAtBaseline: activeThread.session?.updatedAt ?? null,
              }),
            onAccepted: () => undefined,
            onRejected: async () => {
              await recoverPlanModeAfterImplementationFailure();
              setThreadError(
                threadIdForSend,
                "The provider rejected the implementation turn before it started.",
              );
              resetLocalDispatch();
            },
          });
        }
        sendLifecycle.finish({
          turnStarted: true,
          dispatchedTo: threadIdForSend,
          activeThreadId: activeThread.id,
          phase,
        });
        return;
      }

      setOptimisticUserMessages((existing) =>
        existing.filter((message) => message.id !== messageIdForSend),
      );
      await recoverPlanModeAfterImplementationFailure();
      if (!isAtomCommandInterrupted(failure)) {
        setThreadFailure(threadIdForSend, failure, "Failed to send plan follow-up.");
      }
      sendLifecycle.finish({
        turnStarted: false,
        dispatchedTo: threadIdForSend,
        activeThreadId: activeThread.id,
        phase,
      });
    },
    [
      activeThread,
      isConnecting,
      isSendBusy,
      isServerThread,
      persistThreadSettingsForNextTurn,
      phase,
      resetLocalDispatch,
      runtimeMode,
      sendLifecycle,
      setComposerDraftInteractionMode,
      setThreadError,
      setThreadFailure,
      startThreadTurn,
      environmentId,
      composerRef,
    ],
  );

  const onImplementPlanInNewThread = useStableCallback(async () => {
    if (
      !activeThread ||
      !activeProject ||
      !activeProposedPlan ||
      !isServerThread ||
      isSendBusy ||
      isConnecting ||
      activeEnvironmentUnavailable ||
      sendInFlightRef.current
    ) {
      return;
    }

    const sendCtx = composerRef.current?.getSendContext();
    if (!sendCtx) {
      return;
    }
    const { selectedModelSelection: ctxSelectedModelSelection } = sendCtx;
    if (!checkProviderReadinessForPrompt(ctxSelectedModelSelection.instanceId, activeThread.id)) {
      return;
    }

    const createdAt = new Date().toISOString();
    const nextThreadId = newThreadId();
    const planMarkdown = activeProposedPlan.planMarkdown;
    const handoff = buildNewThreadPlanImplementationHandoff({
      planMarkdown,
      sourceThreadId: activeThread.id,
      sourcePlanId: activeProposedPlan.id,
    });
    const outgoingImplementationPrompt = handoff.prompt.trim();
    const nextThreadTitle = truncate(buildPlanImplementationThreadTitle(planMarkdown));
    const nextThreadModelSelection: ModelSelection = ctxSelectedModelSelection;
    const sourceThreadRef = scopeThreadRef(activeThread.environmentId, activeThread.id);
    const targetThreadRef = scopeThreadRef(activeThread.environmentId, nextThreadId);

    useProposedPlanHandoffStore.getState().begin({
      sourceThreadRef,
      sourcePlanId: activeProposedPlan.id,
    });
    sendLifecycle.begin({ preparingWorktree: false });
    // The turn starts on `nextThreadId`, so the viewed thread's state never
    // moves and its acknowledgement never arrives: every exit releases the
    // local stand-in, and the rule says so rather than each exit repeating it.
    const finish = (turnStarted: boolean) =>
      sendLifecycle.finish({
        turnStarted,
        dispatchedTo: nextThreadId,
        activeThreadId: activeThread.id,
        phase,
      });

    const createResult = await createThread({
      environmentId,
      input: {
        threadId: nextThreadId,
        projectId: activeProject.id,
        title: nextThreadTitle,
        modelSelection: nextThreadModelSelection,
        runtimeMode,
        interactionMode: handoff.interactionMode,
        branch: activeThreadBranch,
        worktreePath: activeThread.worktreePath,
        createdAt,
      },
    });
    let failure: AtomCommandResult<unknown, unknown> | null =
      createResult._tag === "Failure" ? createResult : null;

    let turnStartCommandAccepted = false;
    if (failure === null) {
      const startResult = await startThreadTurn({
        environmentId,
        input: {
          threadId: nextThreadId,
          message: {
            messageId: newMessageId(),
            role: "user",
            text: outgoingImplementationPrompt,
            attachments: [],
            designSystemMentions: [],
          },
          modelSelection: ctxSelectedModelSelection,
          titleSeed: nextThreadTitle,
          runtimeMode,
          interactionMode: handoff.interactionMode,
          sourceProposedPlan: handoff.sourceProposedPlan,
          createdAt,
        },
      });
      failure = startResult._tag === "Failure" ? startResult : null;
      turnStartCommandAccepted = failure === null;
    }

    let implementationOutcome: ProposedPlanImplementationOutcome = "pending";
    if (failure === null) {
      const outcomeResult = await settlePromise(() =>
        waitForProposedPlanImplementationOutcome({
          sourceThreadRef,
          sourcePlanId: activeProposedPlan.id,
          targetThreadRef,
          timeoutMs: 1_000,
        }),
      );
      failure = outcomeResult._tag === "Failure" ? outcomeResult : null;
      if (outcomeResult._tag === "Success") {
        implementationOutcome = outcomeResult.value;
      }
    }

    const cleanupUnstartedImplementationThread = async () => {
      useProposedPlanHandoffStore.getState().clear(sourceThreadRef);
      const cleanupResult = await deleteThread({
        environmentId,
        input: {
          threadId: nextThreadId,
          expectedRevision: createdAt,
        },
      });
      if (cleanupResult._tag === "Failure" && !isAtomCommandInterrupted(cleanupResult)) {
        console.warn(
          "Failed to clean up implementation thread after start failure.",
          squashAtomCommandFailure(cleanupResult),
        );
      }
    };

    if (failure === null && implementationOutcome === "rejected") {
      await cleanupUnstartedImplementationThread();
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Could not start implementation thread",
          description: "The provider rejected the turn before it started.",
        }),
      );
      finish(turnStartCommandAccepted);
      return;
    }

    if (implementationOutcome === "accepted") {
      useProposedPlanHandoffStore.getState().clear(sourceThreadRef);
    }

    if (failure === null) {
      const navigateResult = await settlePromise(() =>
        navigate({
          to: "/$environmentId/$threadId",
          params: {
            environmentId: activeThread.environmentId,
            threadId: nextThreadId,
          },
        }),
      );
      failure = navigateResult._tag === "Failure" ? navigateResult : null;
    }

    if (failure !== null) {
      if (shouldDeleteFailedImplementationThread({ turnStartAccepted: turnStartCommandAccepted })) {
        await cleanupUnstartedImplementationThread();
      }
      if (!isAtomCommandInterrupted(failure)) {
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: turnStartCommandAccepted
              ? "Implementation started, but could not open it"
              : "Could not start implementation thread",
            description: commandFailureMessage(
              squashAtomCommandFailure(failure),
              turnStartCommandAccepted
                ? "The implementation thread was preserved so its source-plan relationship stays valid."
                : "An error occurred while creating the new thread.",
            ),
          }),
        );
      }
    }
    void observeProposedPlanImplementation({
      turnStartCommandAccepted,
      initialOutcome: implementationOutcome,
      waitForOutcome: () =>
        waitForProposedPlanImplementationOutcome({
          sourceThreadRef,
          sourcePlanId: activeProposedPlan.id,
          targetThreadRef,
        }),
      onAccepted: () => {
        useProposedPlanHandoffStore.getState().clear(sourceThreadRef);
      },
      onRejected: async () => {
        await cleanupUnstartedImplementationThread();
        await navigate({
          to: "/$environmentId/$threadId",
          params: sourceThreadRef,
        });
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Implementation did not start",
            description: "The source plan is ready to try again.",
          }),
        );
      },
    });
    finish(turnStartCommandAccepted);
  });

  // The model picker reads this during its own render, so it must not be given
  // a frozen identity: it narrows onto the thread fields it reads instead, none
  // of which move with a provider delta.
  const activeThreadHasSession = activeThread?.session != null;
  const activeThreadSessionProviderInstanceId = activeThread?.session?.providerInstanceId ?? null;
  const activeThreadModelSelection = activeThread?.modelSelection ?? null;
  const getModelDisabledReason = useCallback(
    (instanceId: ProviderInstanceId, model: string): string | null => {
      if (activeThreadModelSelection === null) {
        return null;
      }
      const reason = getStartedThreadModelChangeBlockReason({
        providers: providerStatuses,
        hasStartedSession: activeThreadHasSession,
        currentModelSelection: activeThreadModelSelection,
        currentProviderInstanceId: activeThreadSessionProviderInstanceId,
        nextModelSelection: { instanceId, model },
      });
      return reason ? `${reason.description} Start a new thread to use this model.` : null;
    },
    [
      activeThreadHasSession,
      activeThreadModelSelection,
      activeThreadSessionProviderInstanceId,
      providerStatuses,
    ],
  );

  const onProviderModelSelect = useStableCallback(
    (instanceId: ProviderInstanceId, model: string) => {
      if (!activeThread) return;
      // Look up the configured instance so model normalization and custom
      // model lookup stay scoped to that exact instance. Unknown instance ids
      // are rejected by returning early; the server remains authoritative too.
      const entry = providerStatuses.find((snapshot) => snapshot.instanceId === instanceId);
      const resolvedDriverKind = entry?.driver ?? null;
      if (
        lockedProvider !== null &&
        resolvedDriverKind !== null &&
        resolvedDriverKind !== lockedProvider
      ) {
        scheduleComposerFocus();
        return;
      }
      if (lockedProvider !== null && activeThread.session?.providerInstanceId) {
        const currentEntry = providerStatuses.find(
          (snapshot) => snapshot.instanceId === activeThread.session?.providerInstanceId,
        );
        if (
          currentEntry?.continuation?.groupKey &&
          entry?.continuation?.groupKey &&
          currentEntry.continuation.groupKey !== entry.continuation.groupKey
        ) {
          scheduleComposerFocus();
          return;
        }
      }
      const resolvedModel = resolveAppModelSelectionForInstance(
        instanceId,
        settings,
        providerStatuses,
        model,
      );
      if (!resolvedModel) {
        scheduleComposerFocus();
        return;
      }
      const nextModelSelection: ModelSelection = {
        instanceId,
        model: resolvedModel,
      };
      const modelChangeBlockReason = getStartedThreadModelChangeBlockReason({
        providers: providerStatuses,
        hasStartedSession: activeThread.session !== null,
        currentModelSelection: activeThread.modelSelection,
        currentProviderInstanceId: activeThread.session?.providerInstanceId ?? null,
        nextModelSelection,
      });
      if (modelChangeBlockReason) {
        toastManager.add({
          type: "warning",
          title: modelChangeBlockReason.title,
          description: modelChangeBlockReason.description,
        });
        scheduleComposerFocus();
        return;
      }
      setComposerDraftModelSelection(
        scopeThreadRef(activeThread.environmentId, activeThread.id),
        nextModelSelection,
      );
      setStickyComposerModelSelection(nextModelSelection);
      scheduleComposerFocus();
    },
  );
  const onEnvModeChange = useCallback(
    (mode: DraftThreadEnvMode) => {
      if (canOverrideServerThreadEnvMode) {
        setPendingServerThreadEnvMode(mode);
        scheduleComposerFocus();
        return;
      }
      if (isLocalDraftThread) {
        setDraftThreadContext(
          composerDraftTarget,
          resolveDraftContextForEnvMode({
            mode,
            currentWorktreePath: draftThread?.worktreePath ?? null,
            newWorktreesStartFromOrigin: settings.newWorktreesStartFromOrigin,
          }),
        );
      }
      scheduleComposerFocus();
    },
    [
      canOverrideServerThreadEnvMode,
      composerDraftTarget,
      draftThread?.worktreePath,
      isLocalDraftThread,
      settings.newWorktreesStartFromOrigin,
      setPendingServerThreadEnvMode,
      scheduleComposerFocus,
      setDraftThreadContext,
    ],
  );

  const onStartFromOriginChange = (nextStartFromOrigin: boolean) => {
    if (canOverrideServerThreadEnvMode && activeThread) {
      setPendingServerThreadStartFromOriginByThreadId((current) =>
        current[activeThread.id] === nextStartFromOrigin
          ? current
          : { ...current, [activeThread.id]: nextStartFromOrigin },
      );
      return;
    }
    if (isLocalDraftThread) {
      setDraftThreadContext(composerDraftTarget, {
        startFromOrigin: nextStartFromOrigin,
      });
    }
  };

  const onExpandTimelineImage = useCallback((preview: ExpandedImagePreview) => {
    setExpandedImage(preview);
  }, []);
  const onOpenTurnDiff = useCallback(
    (turnId: TurnId, filePath?: string) => {
      if (!isServerThread || !activeThreadRef) return;
      useDiffPanelStore.getState().selectTurn(activeThreadRef, turnId, filePath);
      useRightPanelStore.getState().open(activeThreadRef, "diff");
      onDiffPanelOpen?.();
    },
    [activeThreadRef, isServerThread, onDiffPanelOpen],
  );
  const diffWorkspaceSurfaceContext = useMemo<DiffWorkspaceSurfaceContextValue | null>(
    () =>
      activeThreadRef
        ? {
            threadRef: activeThreadRef,
            composerDraftTarget,
          }
        : null,
    [activeThreadRef, composerDraftTarget],
  );
  const browserWorkspaceSurfaceContext = useMemo<BrowserWorkspaceSurfaceContextValue | null>(
    () =>
      activeThreadRef
        ? {
            render: (surface) => (
              <Suspense fallback={null}>
                <PreviewPanel
                  mode="embedded"
                  threadRef={activeThreadRef}
                  tabId={surface.resourceId}
                  configuredUrls={configuredPreviewUrls}
                  visible
                />
              </Suspense>
            ),
          }
        : null,
    [activeThreadRef, configuredPreviewUrls],
  );
  const filesWorkspaceSurfaceContext = useMemo<FilesWorkspaceSurfaceContextValue | null>(
    () =>
      activeProject && activeWorkspaceRoot && activeThreadRef
        ? {
            environmentId: activeProject.environmentId,
            cwd: activeWorkspaceRoot,
            projectName: activeProject.title,
            threadRef: activeThreadRef,
            composerDraftTarget,
            keybindings,
            availableEditors,
            onOpenFile: openFileSurface,
            onPendingChange: handleFilePendingChange,
          }
        : null,
    [
      activeProject,
      activeThreadRef,
      activeWorkspaceRoot,
      availableEditors,
      composerDraftTarget,
      handleFilePendingChange,
      keybindings,
      openFileSurface,
    ],
  );
  const terminalWorkspaceSurfaceContext = useMemo<TerminalWorkspaceSurfaceContextValue | null>(
    () =>
      activeThreadRef
        ? {
            render: (surface) => (
              <PersistentThreadTerminalPanel
                threadRef={activeThreadRef}
                surface={surface}
                launchContext={activeTerminalLaunchContext ?? null}
                focusRequestId={terminalFocusRequestId}
                keybindings={keybindings}
                onAddTerminalContext={addTerminalContextToDraft}
                onSplitTerminal={splitPanelTerminal}
                onSplitTerminalVertical={splitPanelTerminalVertical}
                onNewTerminal={addTerminalSurface}
                onActiveTerminalChange={activatePanelTerminal}
                onCloseTerminal={closePanelTerminal}
                splitShortcutLabel={splitTerminalShortcutLabel ?? undefined}
                splitVerticalShortcutLabel={splitTerminalVerticalShortcutLabel ?? undefined}
                newShortcutLabel={newTerminalShortcutLabel ?? undefined}
                closeShortcutLabel={closeTerminalShortcutLabel ?? undefined}
              />
            ),
          }
        : null,
    [
      activeTerminalLaunchContext,
      activeThreadRef,
      activatePanelTerminal,
      addTerminalContextToDraft,
      addTerminalSurface,
      closePanelTerminal,
      closeTerminalShortcutLabel,
      keybindings,
      newTerminalShortcutLabel,
      splitPanelTerminal,
      splitPanelTerminalVertical,
      splitTerminalShortcutLabel,
      splitTerminalVerticalShortcutLabel,
      terminalFocusRequestId,
    ],
  );
  if (activeThread === undefined) {
    // The route names a thread the client cannot read. That is never an empty selection:
    // an unreachable Environment explains itself, and a reachable one leaves the thread
    // itself to explain why it will not open and to offer the way back in.
    if (routeEnvironmentUnavailableState !== null) {
      return (
        <ThreadUnavailableState
          label={routeEnvironmentUnavailableState.label}
          connection={routeEnvironmentUnavailableState.connection}
          onRetry={() => {
            void handleReconnectActiveEnvironment(routeEnvironmentUnavailableState.environmentId);
          }}
          onOpenConnectionSettings={() => {
            void navigate({ to: "/settings/connections" });
          }}
        />
      );
    }
    if (routeKind === "server") {
      return (
        <ThreadUnreadableState
          presentation={presentUnreadableThread({
            threadTitle: serverThreadShell?.title ?? null,
            status: serverThreadReadState.status,
            error: Option.getOrNull(serverThreadReadState.error),
          })}
          onRetry={() => refreshEnvironmentThread(routeThreadRef)}
        />
      );
    }
    return <NoActiveThreadState />;
  }

  const panelToggleControls = (
    <PanelLayoutControls
      terminalAvailable={activeProject !== null}
      terminalOpen={terminalUiState.terminalOpen}
      terminalShortcutLabel={shortcutLabelForCommand(keybindings, "terminal.toggle")}
      rightPanelAvailable={activeProject !== null || hasAgentTaskList}
      rightPanelOpen={rightPanelOpen}
      rightPanelShortcutLabel={shortcutLabelForCommand(keybindings, "rightPanel.toggle")}
      onToggleTerminal={toggleTerminalVisibility}
      onToggleRightPanel={toggleRightPanel}
    />
  );
  const panelLayoutControls = (
    <div className="workspace-titlebar-controls z-50 gap-1 [-webkit-app-region:no-drag]">
      {rightPanelOpen && !shouldUseRightPanelSheet ? (
        <RightPanelMaximizeControl
          maximized={rightPanelMaximized}
          onToggle={toggleRightPanelMaximized}
        />
      ) : null}
      {panelToggleControls}
    </div>
  );
  // An empty thread follows the project creation pipeline: the ingest, then
  // the study question, then the verbs as starters. A starter only sets the chip.
  const emptyThreadIngestThreadId =
    emptyThreadState.type === "ingesting" ? emptyThreadState.ingestThreadId : null;
  const emptyThreadContent = (
    <EmptyThread
      state={emptyThreadState}
      verbs={verbActionEntries}
      onPickAction={(selection) => {
        setComposerDraftAction(composerDraftTarget, initialActionSelection(selection));
        scheduleComposerFocus();
      }}
      onOpenIngestThread={
        emptyThreadIngestThreadId === null || activeProject === null
          ? null
          : () =>
              void navigate({
                to: "/$environmentId/$threadId",
                params: buildThreadRouteParams(
                  scopeThreadRef(
                    activeProject.environmentId,
                    ThreadId.make(emptyThreadIngestThreadId),
                  ),
                ),
              })
      }
      onOpenStudy={() => {
        if (activeProject !== null) {
          openProjectStudy(scopeProjectRef(activeProject.environmentId, activeProject.id));
        }
      }}
    />
  );
  const rightPanelSurfaceContent = {
    screen: activeThreadRef ? <CloudScreenView threadRef={activeThreadRef} /> : null,
    tasks: selectedAgentTaskList ? <AgentTaskListPanel taskList={selectedAgentTaskList} /> : null,
    agent:
      activeSubagent && activeThreadRef ? (
        <Suspense fallback={null}>
          <AgentConversationPanel
            agent={activeSubagent}
            cwd={activeWorkspaceRoot}
            threadRef={activeThreadRef}
          />
        </Suspense>
      ) : null,
    "pull-requests": activeThreadRef ? (
      <ThreadPullRequestsPanel
        threadRef={activeThreadRef}
        onOpenPullRequest={openThreadPullRequest}
      />
    ) : null,
    contributions:
      isServerThread && activeThread ? (
        <ContributionsPanel
          projectId={activeThread.projectId}
          environmentId={activeThread.environmentId}
          cwd={gitCwd}
          threadCreatedAt={activeThread.createdAt}
        />
      ) : null,
  } satisfies RightPanelSurfaceContentMap;
  const renderRightPanelRegion = (
    presentation: WorkspaceSurfacePresentation,
    surfaceContent?: ReactNode,
  ) => {
    const rightPanelTabs = activeThreadRef ? (
      <RightPanelTabs
        mode={presentation}
        maxPanelWidth={workspacePanelLayout.maxPanelWidth}
        {...(presentation === "inline"
          ? { maximized: rightPanelMaximized }
          : { layoutControls: panelToggleControls })}
        surfaces={rightPanelState.surfaces}
        activeSurfaceId={activeRightPanelSurface?.id ?? null}
        pendingSurfaceIds={pendingFileSurfaceIds}
        previewSessions={activePreviewState.sessions}
        terminalLabelsById={activeTerminalLabelsById}
        {...(taskCounts ? { taskCounts } : {})}
        agentLabelsById={agentLabelsById}
        onActivate={activateRightPanelSurface}
        onCloseSurface={closeRightPanelSurface}
        onCloseOtherSurfaces={closeOtherRightPanelSurfaces}
        onCloseSurfacesToRight={closeRightPanelSurfacesToRight}
        onCloseAllSurfaces={closeAllRightPanelSurfaces}
        onCopyFilePath={copyRightPanelFilePath}
        onAddBrowser={createDefaultBrowserSurface}
        onAddBrowserInProfile={createBrowserSurface}
        browserProfiles={browserProfileOptions}
        onAddTerminal={addTerminalSurface}
        onAddDiff={addDiffSurface}
        onAddFiles={addFilesSurface}
        onAddScreen={
          hasCloudDisplay && activeThreadRef
            ? () => useRightPanelStore.getState().open(activeThreadRef, "screen")
            : undefined
        }
        onAddPullRequest={
          // The thread action opens this thread's linked collection beside the conversation.
          // The separate Pull Requests workspace remains reachable from the sidebar and the
          // command palette.
          activeThreadRef ? () => addPullRequestsSurface() : undefined
        }
        onAddContributions={
          isServerThread && isGitRepo && activeThreadRef ? addContributionsSurface : undefined
        }
        shortcutLabels={{
          Browser: shortcutLabelForCommand(keybindings, "preview.toggle") ?? undefined,
          Terminal: shortcutLabelForCommand(keybindings, "terminal.new") ?? undefined,
          Diff: shortcutLabelForCommand(keybindings, "diff.toggle") ?? undefined,
        }}
        browserAvailable={browserAvailable}
        diffAvailable={isServerThread && isGitRepo}
        filesAvailable={activeProject !== null}
        experimentSurfaceActions={experimentSurfaceActions}
      >
        {surfaceContent}
      </RightPanelTabs>
    ) : null;
    return presentation === "sheet" ? (
      <RightPanelSheet open onClose={closePreviewPanel}>
        {rightPanelTabs}
      </RightPanelSheet>
    ) : (
      rightPanelTabs
    );
  };

  return (
    <WorkspaceShellRegion
      ref={workspacePanelLayout.workspaceRef}
      name="conversation"
      className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden bg-background"
    >
      {rightPanelOpen && !shouldUseRightPanelSheet ? panelLayoutControls : null}
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-col overflow-x-hidden",
          rightPanelMaximized ? "w-0 flex-none" : "flex-1",
        )}
        data-chat-column-maximized-away={rightPanelMaximized ? "true" : "false"}
      >
        {/* Top bar */}
        <WorkspaceShellHeader
          data-chat-header
          className={cn(
            "border-b border-border transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none",
            isElectron
              ? cn(
                  "workspace-topbar drag-region relative px-3 sm:px-5",
                  reserveTitleBarControlInset &&
                    !inlineRightPanelOwnsTitleBar &&
                    "wco:pr-[var(--workspace-native-controls-inset)]",
                )
              : "workspace-topbar pl-[calc(env(safe-area-inset-left)+0.75rem)] pr-[calc(env(safe-area-inset-right)+0.75rem)] sm:pl-[calc(env(safe-area-inset-left)+1.25rem)] sm:pr-[calc(env(safe-area-inset-right)+1.25rem)]",
            COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
          )}
        >
          {!rightPanelOpen ? panelLayoutControls : null}
          <ChatHeader
            activeThreadEnvironmentId={activeThread.environmentId}
            activeThreadId={activeThread.id}
            {...(routeKind === "draft" && draftId ? { draftId } : {})}
            activeThreadTitle={activeThread.title}
            titleGenerationPending={titleGenerationPending}
            activeProjectName={activeProject?.title}
            openInCwd={gitCwd}
            activeProjectScripts={activeProject?.scripts}
            preferredScriptId={
              activeProject ? (lastInvokedScriptByProjectId[activeProject.id] ?? null) : null
            }
            keybindings={keybindings}
            availableEditors={availableEditors}
            rightPanelOpen={rightPanelOpen}
            gitCwd={gitCwd}
            onRunProjectScript={runProjectScript}
            onAddProjectScript={saveProjectScript}
            onUpdateProjectScript={updateProjectScript}
            onDeleteProjectScript={deleteProjectScript}
          />
        </WorkspaceShellHeader>
        {/* Error banner */}
        <ProviderStatusBanner status={activeProviderStatus} session={activeThread.session} />
        <ThreadErrorBanner
          error={threadError}
          onDismiss={() => setThreadError(activeThread.id, null)}
        />
        {/* Main content area with optional task-list sidebar */}
        <div className="flex min-h-0 min-w-0 flex-1">
          {/* Chat column */}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {/* Messages Wrapper */}
            <div
              className={cn(
                "relative flex min-h-0 flex-1 flex-col",
                isLocalDraftThread && !isWorking && "hidden",
              )}
            >
              {legacyNotice !== null ? (
                // A thread from a retired Feature Workspace says once where its turns run now.
                <p
                  className="mx-auto w-full max-w-3xl shrink-0 truncate px-5 pt-2 text-center text-muted-foreground text-xs"
                  data-legacy-working-directory-notice="true"
                  title={legacyNotice}
                >
                  {legacyNotice}
                </p>
              ) : null}
              {/* Messages — LegendList handles virtualization and scrolling internally */}
              <Suspense fallback={null}>
                <MessagesTimeline
                  key={activeThread.id}
                  queuedMessages={queuedMessages}
                  onSteerQueuedMessage={onSteerQueuedMessage}
                  onRemoveQueuedMessage={onRemoveQueuedMessage}
                  steerQueuedMessageShortcutLabel={shortcutLabelForCommand(
                    keybindings,
                    "thread.steerQueuedMessage",
                  )}
                  isWorking={isWorking}
                  activeTurnInProgress={isWorking || !latestTurnSettled}
                  activeTurnStartedAt={activeWorkStartedAt}
                  agentTaskLists={agentTaskLists}
                  listRef={legendListRef}
                  timelineEntries={timelineEntries}
                  latestTurn={activeLatestTurn}
                  emptyState={emptyThreadContent}
                  runningTurnId={
                    activeThread.session?.status === "running"
                      ? activeThread.session.activeTurnId
                      : null
                  }
                  turnDiffSummaryByAssistantMessageId={turnDiffSummaryByAssistantMessageId}
                  activeThreadEnvironmentId={activeThread.environmentId}
                  routeThreadKey={routeThreadKey}
                  onOpenTurnDiff={onOpenTurnDiff}
                  checkpointTurnCountByUserMessageId={checkpointTurnCountByUserMessageId}
                  canForkThread={canForkNativeHistory}
                  canRevertToCheckpoint={
                    canRevertConversation &&
                    activeThread.session?.status === "ready" &&
                    phase !== "running" &&
                    !isSendBusy &&
                    !isConnecting
                  }
                  onForkAssistantMessage={onForkAssistantMessage}
                  onRevertToTurnCount={onRevertTimelineTurn}
                  isForkingThread={isForkingThread}
                  isRevertingCheckpoint={isRevertingCheckpoint}
                  onImageExpand={onExpandTimelineImage}
                  onFileExpand={setExpandedFile}
                  markdownCwd={gitCwd ?? undefined}
                  resolvedTheme={resolvedTheme}
                  timestampFormat={timestampFormat}
                  workspaceRoot={activeWorkspaceRoot}
                  skills={timelineProviderSkills}
                  anchorMessageId={timelineAnchorMessageId}
                  onAnchorReady={onTimelineAnchorReady}
                  onAnchorSizeChanged={onTimelineAnchorSizeChanged}
                  contentInsetEndAdjustment={composerTimelineInset}
                  onContentOverflowChange={setTimelineOverflows}
                  onIsAtEndChange={onIsAtEndChange}
                  onManualNavigation={cancelTimelineLiveFollowForUserNavigation}
                  followLiveEdge={timelineFollowsLiveEdge}
                  hasOlderHistory={threadHistory.hasOlder}
                  historyStartsMidTurn={threadHistory.startsMidTurn}
                  historyPending={threadHistory.isPending}
                  historyError={threadHistory.error}
                  onLoadOlderHistory={threadHistory.loadOlder}
                />
              </Suspense>

              {/* scroll to end pill — shown when user has scrolled away from the live edge */}
              {showScrollToBottom && (
                <div
                  className="pointer-events-none absolute left-1/2 z-30 flex -translate-x-1/2 justify-center py-1.5"
                  style={{ bottom: composerOverlayHeight + 4 }}
                >
                  <button
                    type="button"
                    aria-label="Scroll to end"
                    title="Scroll to end"
                    onClick={() => scrollToEnd(true)}
                    className="pointer-events-auto flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1 text-muted-foreground text-xs shadow-sm transition-colors hover:border-border hover:text-foreground hover:cursor-pointer"
                  >
                    <ChevronDownIcon className="size-3.5" />
                    Scroll to end
                  </button>
                </div>
              )}
              {activeThreadRef && browserAvailable ? (
                <PreviewSessionSync threadRef={activeThreadRef} />
              ) : null}
              {activeThreadRef && browserAvailable && previewMiniPlayer ? (
                <ThreadPreviewMiniPlayer
                  threadRef={activeThreadRef}
                  miniPlayer={previewMiniPlayer}
                  composerOverlayElement={composerOverlayElement}
                />
              ) : null}
            </div>

            {/* Input bar */}
            <WorkspaceShellComposer
              ref={setComposerOverlayElement}
              data-chat-composer-overlay="true"
              className={cn(
                "pointer-events-none inset-x-0 z-20 pt-1.5 sm:pt-2",
                isLocalDraftThread && !isWorking
                  ? "relative my-auto max-h-full overflow-y-auto py-8"
                  : "absolute bottom-0",
              )}
            >
              {isLocalDraftThread && !isWorking ? (
                <div className="pointer-events-auto relative z-10 mx-auto mb-6 max-w-3xl">
                  {emptyThreadContent}
                </div>
              ) : null}
              <div
                aria-hidden="true"
                className="chat-composer-horizontal-inset pointer-events-none absolute inset-x-0 top-1.5 bottom-0 z-0 sm:top-2"
              >
                <div className="relative mx-auto h-full w-full max-w-3xl overflow-clip rounded-t-[20px]">
                  <div className="chat-composer-shared-blur absolute -inset-8" />
                </div>
              </div>
              {studyOffer !== null ? (
                <div className="chat-composer-horizontal-inset pointer-events-auto relative z-10 mb-2">
                  <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-xl border bg-card/85 py-1.5 ps-3 pe-1.5 text-sm shadow-xs backdrop-blur">
                    <ListChecksIcon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate text-muted-foreground">
                      {studyOffer.awaiting
                        ? "This project awaits its Project Study."
                        : "This project has no Project Study yet."}
                    </span>
                    <Button
                      className="ms-auto shrink-0"
                      onClick={() => openProjectStudy(studyOffer.project)}
                      size="xs"
                      variant="outline"
                    >
                      Start the study
                    </Button>
                  </div>
                </div>
              ) : null}
              <div className="chat-composer-horizontal-inset">
                <div
                  data-chat-composer-stack="true"
                  className="pointer-events-auto relative z-10 isolate"
                >
                  <ComposerSurface.Shell>
                    <ComposerSurface.Host>
                      <div className="relative z-10">
                        <ChatComposer
                          composerAction={composerAction}
                          onComposerActionChange={handleComposerActionChange}
                          actionContext={actionContext}
                          customActionSkills={composerCustomActionSkills}
                          alwaysIngestAttachments={alwaysIngestAttachments}
                          onEnableAlwaysIngestAttachments={enableAlwaysIngest}
                          nextActionId={nextActionId}
                          projectRoot={activeProject?.workspaceRoot ?? null}
                          composerRef={composerRef}
                          composerDraftTarget={composerDraftTarget}
                          environmentId={environmentId}
                          routeKind={routeKind}
                          routeThreadRef={routeThreadRef}
                          draftId={draftId}
                          activeThreadId={activeThreadId}
                          activeThreadEnvironmentId={activeThread?.environmentId}
                          activeThreadProviderInstanceId={
                            activeThread?.session?.providerInstanceId ?? null
                          }
                          activeThreadShell={serverThreadShell ?? null}
                          isServerThread={isServerThread}
                          isLocalDraftThread={isLocalDraftThread}
                          promptHistoryMessages={composerPromptHistoryMessages}
                          threadSyncPhase={null}
                          bannerItems={composerBannerItems}
                          attachmentUploadsCapabilityKnown={attachmentUploadsCapabilityKnown}
                          supportsAttachmentUploads={supportsAttachmentUploads}
                          supportsQuestionAttachments={supportsQuestionAttachments}
                          maxFileAttachmentBytes={maxFileAttachmentBytes}
                          forceExpandedOnMobile={false}
                          projectSelectionRequired={activeProject === null}
                          providerCatalogKnown={serverConfig !== null}
                          sendDisabledReason={null}
                          isRevertingCheckpoint={false}
                          activeTasksProgress={null}
                          activeTaskSteps={null}
                          compactThreadUnavailable={!compactionControl.enabled}
                          compactDisabled={!compactionControl.enabled}
                          compactDisabledReason={compactionControl.disabledReason ?? null}
                          onCompactContext={requestContextCompaction}
                          pullRequestProjectId={activeProject?.id ?? null}
                          pullRequestRepository={null}
                          restingControlsHost={restingControlsHost}
                          restingControlsHaveLeadingContext={restingControlsHaveLeadingContext}
                          onRestingControlsVisibilityChange={onRestingControlsVisibilityChange}
                          onRestingChange={onComposerRestingChange}
                          onComposerOverlayHeightChange={publishComposerOverlayHeight}
                          getTimelineScrollableNode={getTimelineScrollableNode}
                          isTimelineAtLogicalEnd={isTimelineAtLogicalEnd}
                          timelineOverflows={timelineOverflows}
                          onPageScrollKeyDown={onPageScrollKeyDown}
                          onPageScrollKeyUp={onPageScrollKeyUp}
                          onPageScrollRelease={onPageScrollRelease}
                          onOpenProviderSetup={openProviderSetup}
                          scheduleComposerFocus={scheduleComposerFocus}
                          phase={phase}
                          turnInProgress={turnInProgress}
                          isConnecting={isConnecting}
                          isSendBusy={isSendBusy}
                          isPreparingWorktree={isPreparingWorktree}
                          environmentUnavailable={activeEnvironmentUnavailableState}
                          activePendingApproval={activePendingApproval}
                          pendingApprovals={pendingApprovals}
                          pendingUserInputs={pendingUserInputs}
                          activePendingProgress={activePendingProgress}
                          activePendingResolvedAnswers={activePendingResolvedAnswers}
                          activePendingIsResponding={activePendingIsResponding}
                          activePendingDraftAnswers={activePendingDraftAnswers}
                          activePendingQuestionIndex={activePendingQuestionIndex}
                          respondingRequestIds={respondingRequestIds}
                          showPlanFollowUpPrompt={showPlanFollowUpPrompt}
                          activeProposedPlan={activeProposedPlan}
                          runtimeMode={runtimeMode}
                          interactionMode={interactionMode}
                          lockedProvider={lockedProvider}
                          providerStatuses={providerStatuses as ServerProvider[]}
                          activeProjectDefaultModelSelection={activeProject?.defaultModelSelection}
                          activeThreadModelSelection={activeThread?.modelSelection}
                          activeContextWindow={activeContextWindow}
                          resolvedTheme={resolvedTheme}
                          settings={settings}
                          keybindings={keybindings}
                          terminalOpen={Boolean(terminalUiState.terminalOpen)}
                          gitCwd={gitCwd}
                          promptRef={promptRef}
                          composerImagesRef={composerImagesRef}
                          composerFilesRef={composerFilesRef}
                          composerTerminalContextsRef={composerTerminalContextsRef}
                          onSend={handleComposerSend}
                          onComposerFocus={warmComposerThreadSession}
                          onInterrupt={onInterrupt}
                          onImplementPlanInNewThread={onImplementPlanInNewThread}
                          onRespondToApproval={onRespondToApproval}
                          onSelectActivePendingUserInputOption={
                            onSelectActivePendingUserInputOption
                          }
                          onAdvanceActivePendingUserInput={onAdvanceActivePendingUserInput}
                          onPreviousActivePendingUserInputQuestion={
                            onPreviousActivePendingUserInputQuestion
                          }
                          onChangeActivePendingUserInputCustomAnswer={
                            onChangeActivePendingUserInputCustomAnswer
                          }
                          onProviderModelSelect={onProviderModelSelect}
                          getModelDisabledReason={getModelDisabledReason}
                          toggleInteractionMode={toggleInteractionMode}
                          handleRuntimeModeChange={handleRuntimeModeChange}
                          handleInteractionModeChange={handleInteractionModeChange}
                          focusComposer={focusComposer}
                          setThreadError={setThreadError}
                          onExpandImage={onExpandTimelineImage}
                        />
                      </div>
                    </ComposerSurface.Host>
                  </ComposerSurface.Shell>
                </div>
              </div>
              <div className="chat-composer-horizontal-inset chat-composer-lower-chrome relative z-10 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                <div className="pointer-events-auto">
                  <BranchToolbar
                    environmentId={activeThread.environmentId}
                    threadId={activeThread.id}
                    {...(routeKind === "draft" && draftId ? { draftId } : {})}
                    onEnvModeChange={onEnvModeChange}
                    startFromOrigin={startFromOrigin}
                    onStartFromOriginChange={onStartFromOriginChange}
                    {...(canOverrideServerThreadEnvMode
                      ? { effectiveEnvModeOverride: envMode }
                      : {})}
                    {...(canOverrideServerThreadEnvMode
                      ? {
                          activeThreadBranchOverride: activeThreadBranch,
                          onActiveThreadBranchOverrideChange: setPendingServerThreadBranch,
                        }
                      : {})}
                    envLocked={envLocked || !isGitRepo}
                    {...(!isGitRepo
                      ? { worktreeUnavailableReason: "Worktrees require a Git repository." }
                      : {})}
                    onComposerFocusRequest={scheduleComposerFocus}
                    {...(canCheckoutPullRequestIntoThread
                      ? { onCheckoutPullRequestRequest: openPullRequestDialog }
                      : {})}
                    {...(hasMultipleEnvironments ? { onEnvironmentChange } : {})}
                    availableEnvironments={logicalProjectEnvironments}
                  />
                </div>
              </div>
            </WorkspaceShellComposer>

            {pullRequestDialogState ? (
              <PullRequestThreadDialog
                key={pullRequestDialogState.key}
                open
                environmentId={activeThread.environmentId}
                threadId={activeThread.id}
                cwd={activeProject?.workspaceRoot ?? null}
                initialReference={pullRequestDialogState.initialReference}
                onOpenChange={(open) => {
                  if (!open) {
                    closePullRequestDialog();
                  }
                }}
                onPrepared={handlePreparedPullRequestThread}
              />
            ) : null}
          </div>
          {/* end chat column */}
        </div>
        {/* end horizontal flex container */}

        {mountedTerminalThreadRefs.map(({ key: mountedThreadKey, threadRef: mountedThreadRef }) => (
          <PersistentThreadTerminalDrawer
            key={mountedThreadKey}
            threadRef={mountedThreadRef}
            threadId={mountedThreadRef.threadId}
            visible={mountedThreadKey === activeThreadKey && terminalUiState.terminalOpen}
            launchContext={
              mountedThreadKey === activeThreadKey ? (activeTerminalLaunchContext ?? null) : null
            }
            focusRequestId={mountedThreadKey === activeThreadKey ? terminalFocusRequestId : 0}
            splitShortcutLabel={splitTerminalShortcutLabel ?? undefined}
            splitVerticalShortcutLabel={splitTerminalVerticalShortcutLabel ?? undefined}
            newShortcutLabel={newTerminalShortcutLabel ?? undefined}
            closeShortcutLabel={closeTerminalShortcutLabel ?? undefined}
            keybindings={keybindings}
            onAddTerminalContext={addTerminalContextToDraft}
          />
        ))}
      </div>

      {activeWorkspaceSurfaceScope ? (
        <RightPanelWorkspaceSurfaceHost
          browserContext={browserWorkspaceSurfaceContext}
          diffContext={diffWorkspaceSurfaceContext}
          filesContext={filesWorkspaceSurfaceContext}
          surfaceContent={rightPanelSurfaceContent}
          terminalContext={terminalWorkspaceSurfaceContext}
          state={rightPanelState}
          scope={activeWorkspaceSurfaceScope}
          useSheet={shouldUseRightPanelSheet}
          renderRegion={renderRightPanelRegion}
          globalRetentionLimit={4}
          onFailure={reportWorkspaceSurfaceFailure}
          renderFailure={renderWorkspaceSurfaceFailure}
        />
      ) : null}

      <AlertDialog
        open={pendingRevert !== null && pendingRevert.routeThreadKey === routeThreadKey}
        onOpenChange={(open) => {
          if (!open) setPendingRevert(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit from here?</AlertDialogTitle>
            <AlertDialogDescription>
              This rewinds the thread to before this message and returns its prompt to the composer.
              Attachments are not carried back.
              {activeThread?.worktreePath === null
                ? " Files stay as they are because this thread shares the project checkout."
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            {/* Restoring files is only offered from an isolated Thread Worktree;
                in a shared Primary Checkout it would discard another thread's work. */}
            {activeThread?.worktreePath ? (
              <Button
                variant="destructive"
                onClick={() => {
                  if (!pendingRevert || pendingRevert.routeThreadKey !== routeThreadKey) return;
                  setPendingRevert(null);
                  void onRevertToTurnCount(pendingRevert.turnCount, pendingRevert.messageId, true);
                }}
              >
                Revert files too
              </Button>
            ) : null}
            <Button
              onClick={() => {
                if (!pendingRevert || pendingRevert.routeThreadKey !== routeThreadKey) return;
                setPendingRevert(null);
                void onRevertToTurnCount(pendingRevert.turnCount, pendingRevert.messageId, false);
              }}
            >
              Revert and keep changes
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>

      {/* Mounted once for the whole chat view: the thread's pull requests panel and the command
          palette both ask for it by thread, and the palette closes the moment its command runs. */}
      <LinkPullRequestDialogHost />

      {expandedImage && (
        <ExpandedImageDialog
          key={`${expandedImage.images[expandedImage.index]?.src ?? "image"}:${expandedImage.index}`}
          preview={expandedImage}
          onClose={closeExpandedImage}
        />
      )}
      {expandedFile && (
        <ExpandedFileDialog
          key={expandedFile.src}
          onClose={closeExpandedFile}
          preview={expandedFile}
        />
      )}
    </WorkspaceShellRegion>
  );
}

export default function ChatView(props: ChatViewProps) {
  return <ChatViewContent {...props} />;
}
